/**
 * Atlante section — nessun dato brand vive nel tema: tutto arriva a runtime
 * dal Gist pubblico indicato in data-gist-url e viene renderizzato qui.
 */
(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  // --- Copy bilingue --------------------------------------------------------
  //
  // Il negozio e' bilingue (italiano/inglese) ma questa section costruiva ogni
  // stringa in italiano: cambiando lingua, la scheda di un brand restava in
  // italiano dalla prima riga all'ultima. Il criterio e' lo stesso gia' usato
  // in sections/manifatture-products.liquid (localization.language.iso_code,
  // qui passato in data-lang) invece dei file locales/ del tema: sono poche
  // stringhe che vivono solo dentro questa feature, e tenerle qui accanto al
  // codice che le usa evita di spargerle in un file di traduzioni del tema.
  //
  // Le descrizioni dei brand NON stanno qui: arrivano dal Gist in due campi
  // paralleli, `narrative` (italiano) e `narrative_en` (inglese). Vedi
  // brandNarrative() piu' sotto.
  const STRINGS = {
    it: {
      // Il link in cima alla scheda per chi e' arrivato da un capo (la
      // targhetta della scheda prodotto): vedi readReturnUrl().
      backToProduct: 'Torna al capo',
      // Nelle schede degli ALTRI marchi il link porta comunque al capo di
      // partenza: il nome del suo marchio evita di pensare a un capo di
      // quello che si sta guardando.
      backToBrandProduct: (name) => `Torna al capo ${name}`,
      productsTitle: 'Prodotti',
      productsLoading: 'Caricamento prodotti…',
      productsError: 'Impossibile caricare i prodotti.',
      // Ultima rete, quando ne' il Gist ne' le due copie rispondono: la
      // pagina mostra comunque i marchi dell'archivio, che il tema ha gia'
      // reso lato server. Non e' un messaggio d'errore: e' quello che si
      // puo' ancora dire di vero al visitatore.
      offlineNote: 'La mappa non è disponibile in questo momento. Questi sono i marchi presenti in archivio.',
      since: (year) => `dal ${year}`,
      clusterCity: (city, count) => `${city}: ${count} marchi`,
      clusterArea: (count) => `${count} marchi in questa zona`,
      // Etichetta sulla mappa di un gruppo di citta' diverse (vedi
      // pointLabelText): corta di proposito, la forma lunga e' clusterArea.
      areaLabel: (count) => `Zona · ${count}`,
      regionMapLabel: (title) => `Mappa dei marchi, regione ${title}`,
      usaRegions: {
        northeast: 'Nord-est',
        midwest: 'Midwest',
        south: 'Sud',
        west: 'Ovest',
        other: 'Altre zone',
      },
      intlRegions: { europe: 'Europa', asia: 'Asia', other: 'Altri paesi' },
      filterGroups: { vendor: 'Marchio', city: 'Città', state: 'Stato', country: 'Nazione' },
      filterAllOf: {
        vendor: 'Tutti i marchi',
        city: 'Tutte le città',
        state: 'Tutti gli stati',
        country: 'Tutte le nazioni',
      },
      filterCount: (shown, total) =>
        shown === total ? `${total} marchi` : `${shown} di ${total} marchi`,
      filterResults: 'Risultati della ricerca',
      filterMoreResults: (count) => `e altre ${count}`,
    },
    en: {
      backToProduct: 'Back to the item',
      backToBrandProduct: (name) => `Back to the ${name} item`,
      productsTitle: 'Products',
      productsLoading: 'Loading products…',
      productsError: 'Unable to load the products.',
      offlineNote: 'The map is unavailable right now. These are the brands in the archive.',
      since: (year) => `since ${year}`,
      clusterCity: (city, count) => `${city}: ${count} brands`,
      clusterArea: (count) => `${count} brands in this area`,
      areaLabel: (count) => `Area · ${count}`,
      regionMapLabel: (title) => `Map of the brands, ${title} region`,
      usaRegions: {
        northeast: 'Northeast',
        midwest: 'Midwest',
        south: 'South',
        west: 'West',
        other: 'Other areas',
      },
      intlRegions: { europe: 'Europe', asia: 'Asia', other: 'Other countries' },
      filterGroups: { vendor: 'Brand', city: 'City', state: 'State', country: 'Country' },
      filterAllOf: {
        vendor: 'All brands',
        city: 'All cities',
        state: 'All states',
        country: 'All countries',
      },
      filterCount: (shown, total) =>
        shown === total ? `${total} brands` : `${shown} of ${total} brands`,
      filterResults: 'Search results',
      filterMoreResults: (count) => `and ${count} more`,
    },
  };

  // Lingua corrente della pagina. Resta 'it' finche' la section non e'
  // connessa (data-lang): il fallback e' l'italiano, la lingua in cui il
  // dataset e' scritto per intero, non una stringa vuota.
  let LANG = 'it';

  function t(key) {
    const table = STRINGS[LANG] || STRINGS.it;
    return table[key] !== undefined ? table[key] : STRINGS.it[key];
  }

  /**
   * Descrizione del brand nella lingua della pagina. In inglese si usa
   * `narrative_en` se c'e', altrimenti si ripiega sull'italiano: una scheda
   * senza descrizione sarebbe una perdita di contenuto peggiore di una
   * descrizione nella lingua sbagliata, e il campo inglese entra nel Gist un
   * brand alla volta.
   */
  function brandNarrative(brand) {
    if (LANG === 'en' && brand.narrative_en) return brand.narrative_en;
    return brand.narrative;
  }

  // Ampiezza della finestra entro cui tutte le visite chiedono lo stesso URL
  // del Gist (vedi load()): e' anche il ritardo massimo con cui una modifica
  // al Gist arriva a chi ha gia' visitato il sito da poco. Cinque minuti: il
  // Gist cambia al massimo una volta al giorno (la Routine notturna), quindi
  // e' un ritardo che non si nota, mentre la differenza sul traffico si
  // vede a ogni pagina aperta.
  const GIST_CACHE_WINDOW_MS = 5 * 60 * 1000;

  // La copia dell'ultimo Gist andato a buon fine, tenuta nel browser del
  // visitatore. Stessa forma della cache delle recensioni
  // (gg_reviews_cache_v1, vedi fixes/recensioni-resilienza nel repo
  // grid-garments-websitemods): una chiave sola, con dentro quando e' stata
  // scritta e il dato cosi' com'e' arrivato.
  //
  //   entro le 24h  -> si usa e NON si fa alcuna richiesta
  //   oltre le 24h  -> si riprova il Gist, e se non risponde si usa lo stesso
  //
  // Non ha scadenza dura: una copia vecchia e' sempre meglio di una pagina
  // che non sa dire niente, e si vede solo quando il Gist non risponde.
  const CACHE_KEY = 'gg_manifatture_cache_v1';
  const CACHE_FRESH_MS = 24 * 60 * 60 * 1000;

  // Chi viene servito dalla copia non chiede niente al Gist, e senza altro
  // resterebbe fermo a quella copia fino alle 24h. Per questo, DOPO aver
  // disegnato, la pagina va a controllare in sottofondo se il Gist e'
  // cambiato: se lo e', la copia viene sostituita subito e il dato nuovo e'
  // gia' li' alla pagina successiva. Il ritardo scende cosi' da "fino a un
  // giorno" a "la prossima pagina che apre".
  //
  // Il controllo e' limitato a UNO per finestra di cache, condiviso fra le
  // schede tramite localStorage: senza il limite, tornerebbe una richiesta
  // per ogni pagina aperta, cioe' esattamente il traffico verso GitHub che
  // la copia serve a togliere (ed e' un rate limit ad aver spento le
  // recensioni il 2026-08-17).
  const REVALIDATE_KEY = 'gg_manifatture_revalidate_v1';

  // Impronta del testo del Gist, per capire in sottofondo se e' cambiato
  // senza confrontare due file interi carattere per carattere. FNV-1a a 32
  // bit: poche righe, nessuna dipendenza, e le collisioni qui non fanno
  // danno — al massimo si rimanda l'aggiornamento di una copia al prossimo
  // controllo.
  // Un dato "buono" e' un oggetto con almeno una voce. Vale per il Gist e per
  // le copie: un oggetto vuoto non e' un insieme di marchi, e' l'assenza di
  // marchi, e va trattato come tale.
  function haContenuto(dati) {
    return !!dati && typeof dati === 'object' && Object.keys(dati).length > 0;
  }

  function impronta(testo) {
    let h = 0x811c9dc5;
    for (let i = 0; i < testo.length; i += 1) {
      h ^= testo.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }

  // Bounding box lat/lng usata sia per il path della sagoma USA nel liquid
  // sia per proiettare i dot, cosi' restano allineati tra loro.
  const PROJECTION = {
    lngMin: -125,
    lngMax: -66.9,
    latMin: 24.4,
    latMax: 49.4,
    width: 960,
    height: 600,
    padding: 20,
  };

  function project(lat, lng) {
    const usableWidth = PROJECTION.width - PROJECTION.padding * 2;
    const usableHeight = PROJECTION.height - PROJECTION.padding * 2;
    const x =
      PROJECTION.padding +
      ((lng - PROJECTION.lngMin) / (PROJECTION.lngMax - PROJECTION.lngMin)) * usableWidth;
    const y =
      PROJECTION.padding +
      ((PROJECTION.latMax - lat) / (PROJECTION.latMax - PROJECTION.latMin)) * usableHeight;
    return { x, y };
  }

  // Rettangolo di riferimento (in unita' di viewBox) usato di default da tutte
  // le funzioni di posizionamento sotto (placeLabels, explodePositions, ecc.):
  // l'intero viewBox della mappa nazionale, esattamente il comportamento di
  // prima che esistessero le mappe regionali mobile (vedi USA_REGIONS piu'
  // sotto). Passare un `bounds` diverso a quelle funzioni le fa lavorare sul
  // ritaglio di una singola regione invece che sull'intera mappa, senza
  // duplicarne la logica.
  const DEFAULT_BOUNDS = { x1: 0, y1: 0, x2: PROJECTION.width, y2: PROJECTION.height };

  // I dot condividono un unico marker solo se si riferiscono davvero allo
  // stesso posto — stessa citta' (nome + stato), non semplice vicinanza sulla
  // mappa. Si raggruppa per nome citta' invece che per coordinate esatte
  // perche' due brand della stessa citta' possono avere lat/lng leggermente
  // diverse (geocodificate su indirizzi diversi all'interno della citta'), e
  // un confronto sulle sole coordinate li terrebbe separati anche se sono
  // davvero nello stesso posto. Citta' diverse restano sempre dot distinti,
  // anche quando la loro proiezione cade a pochi pixel di distanza: in quel
  // caso e' compito dell'algoritmo di posizionamento etichette (sotto)
  // tenerle leggibili, non del clustering nascondere la distinzione tra due
  // posti diversi.
  function clusterUsaBrands(brands) {
    const groups = new Map();
    brands.forEach((brand) => {
      const city = (brand.city || '').trim().toLowerCase();
      const state = (brand.state || '').trim().toLowerCase();
      // Se manca il nome citta', raggruppare per nome perderebbe di senso e
      // rischierebbe di unire per errore brand di posti diversi che
      // condividono solo un campo vuoto: in quel caso si torna alla
      // coordinata come chiave, cosi' al peggio restano dot distinti invece
      // di fondersi in uno sbagliato.
      const key = city ? `${city}|${state}` : `coord|${brand.lat.toFixed(2)},${brand.lng.toFixed(2)}`;
      const { x, y } = project(brand.lat, brand.lng);
      if (!groups.has(key)) {
        groups.set(key, { x, y, city: brand.city || brand.label, brands: [] });
      } else {
        // Media incrementale delle coordinate: il dot rappresenta la citta'
        // nel suo complesso, non l'indirizzo esatto di un singolo brand.
        const group = groups.get(key);
        const n = group.brands.length + 1;
        group.x += (x - group.x) / n;
        group.y += (y - group.y) / n;
      }
      groups.get(key).brands.push(brand);
    });
    return Array.from(groups.values());
  }

  // Solo per mobile (vedi isMobileViewport/renderMobileRegions): sulle card
  // regionali, larghe quanto lo schermo, due dot possono restare cosi'
  // vicini in pixel reali da risultare illeggibili e impossibili da toccare
  // separatamente. La soglia arriva sempre dal chiamante gia' riscalata sul
  // viewBox della card (mobileClusterDistance/MOBILE_CLUSTER_BASE_DISTANCE),
  // cosi' il criterio e' la distanza FISICA a schermo, uguale in ogni card,
  // e non un numero di unita' di viewBox che vale distanze diverse a seconda
  // di quanta area geografica copre la card.
  // Fonde in un unico dot (con badge numerico, popover al tap identico a
  // quello dei cluster "stessa citta'") i gruppi gia' passati da
  // clusterUsaBrands che restano comunque troppo vicini tra loro — a
  // differenza di clusterUsaBrands, qui la vicinanza sulla mappa e' l'unico
  // criterio: non deve trattarsi della stessa citta'. Fusione "greedy": a
  // ogni passo unisce la prima coppia trovata entro soglia (usando il
  // centroide pesato sul numero di brand), finche' nessuna coppia resta
  // entro soglia. Non e' garantito il raggruppamento globalmente ottimale,
  // ma per il numero di punti in gioco (poche decine) e' sufficiente,
  // deterministico sull'ordine di input, e verificato empiricamente sul
  // dataset reale (vedi la calibrazione in MOBILE_CLUSTER_BASE_DISTANCE).
  function clusterByProximity(groups, thresholdSq) {
    // sourceGroups conserva i gruppi originali (pre-fusione) che compongono
    // ogni punto: serve solo su mobile per "esplodere" un cluster al tap
    // (vedi toggleRegionCluster in ManifattureSection), tornando alle
    // posizioni vere di ciascun brand invece del centroide fuso.
    const merged = groups.map((g) => ({ x: g.x, y: g.y, city: g.city, brands: g.brands.slice(), multiCity: false, sourceGroups: [g] }));

    let didMerge = true;
    while (didMerge) {
      didMerge = false;
      outer: for (let i = 0; i < merged.length; i++) {
        for (let j = i + 1; j < merged.length; j++) {
          const dx = merged[i].x - merged[j].x;
          const dy = merged[i].y - merged[j].y;
          if (dx * dx + dy * dy > thresholdSq) continue;

          const a = merged[i];
          const b = merged[j];
          const totalBrands = a.brands.length + b.brands.length;
          const sameCity = !a.multiCity && !b.multiCity && a.city === b.city;
          merged[i] = {
            x: (a.x * a.brands.length + b.x * b.brands.length) / totalBrands,
            y: (a.y * a.brands.length + b.y * b.brands.length) / totalBrands,
            city: sameCity ? a.city : null,
            brands: a.brands.concat(b.brands),
            multiCity: !sameCity,
            sourceGroups: a.sourceGroups.concat(b.sourceGroups),
          };
          merged.splice(j, 1);
          didMerge = true;
          break outer;
        }
      }
    }

    return merged;
  }

  // Il raggio dei dot scala con fontScale per restare della stessa
  // dimensione fisica reale in ogni card regionale (vedi paintPointsInto/
  // BASE_LABEL_FONT_SIZE): un dot cluster piu' grande (nelle regioni con
  // viewBox piu' ampio, es. "Sud") puo' arrivare a toccare visivamente un
  // dot vicino che clusterByProximity non ha fuso (perche' resta fuori
  // soglia) — verificato sul dataset reale, es. il cluster della zona
  // tessile NC che tocca il dot "Soffe" li' accanto. Non si fondono (non e'
  // compito di questa funzione decidere cosa raggruppare, solo evitare che
  // due cerchi disegnati si sovrappongano): si scostano leggermente lungo la
  // retta che ne congiunge i centri, il minimo indispensabile per non
  // toccarsi piu'. Piu' passate (il numero di dot per regione e' sempre una
  // decina scarsa, quindi economico) perche' risolvere una coppia puo' farne
  // toccare un'altra in precedenza libera.
  function separateOverlappingDots(groups, fontScale, bounds) {
    const scale = fontScale || 1;
    const b = bounds || DEFAULT_BOUNDS;
    const radiusOf = (group) => (group.brands.length > 1 ? 8 : 6) * scale;
    // +2 unita' (di base, scalate come il raggio) di margine oltre alla
    // somma dei raggi: evita che due dot restino esattamente tangenti
    // (visivamente ancora indistinguibili a schermo piccolo). Scalato come
    // il raggio perche' altrimenti, nelle regioni con viewBox piu' ampio
    // (fontScale piu' alto, es. "Sud"), un margine fisso in unita' di
    // viewBox varrebbe proporzionalmente meno pixel reali — verificato che
    // senza questo scaling restava una minima sovrapposizione residua
    // (dovuta anche al contorno dei dot, stroke-width: 1.5 in
    // component-manifatture.css, anch'esso in unita' di viewBox).
    const MARGIN = 2 * scale;

    for (let pass = 0; pass < 8; pass++) {
      let resolvedAny = false;
      for (let i = 0; i < groups.length; i++) {
        for (let j = i + 1; j < groups.length; j++) {
          const a = groups[i];
          const g = groups[j];
          const minDist = radiusOf(a) + radiusOf(g) + MARGIN;
          const dx = g.x - a.x;
          const dy = g.y - a.y;
          const dist = Math.hypot(dx, dy);
          if (dist >= minDist) continue;

          // Direzione arbitraria (verticale) se i due centri coincidono
          // esattamente (dist 0): evita una divisione per zero senza un
          // caso speciale nel resto della logica.
          const ux = dist > 0.001 ? dx / dist : 0;
          const uy = dist > 0.001 ? dy / dist : 1;
          const push = (minDist - dist) / 2;
          a.x = Math.min(Math.max(a.x - ux * push, b.x1 + radiusOf(a)), b.x2 - radiusOf(a));
          a.y = Math.min(Math.max(a.y - uy * push, b.y1 + radiusOf(a)), b.y2 - radiusOf(a));
          g.x = Math.min(Math.max(g.x + ux * push, b.x1 + radiusOf(g)), b.x2 - radiusOf(g));
          g.y = Math.min(Math.max(g.y + uy * push, b.y1 + radiusOf(g)), b.y2 - radiusOf(g));
          resolvedAny = true;
        }
      }
      if (!resolvedAny) break;
    }

    return groups;
  }

  // Rimuove eventuali parti tra parentesi dal nome (es. "Huntington
  // (Clothiers)" -> "Huntington"): sulla mappa, dove lo spazio e' scarso e
  // spesso condiviso con altri dot vicini, il nome completo del brand non
  // serve a identificare il punto — la parte descrittiva resta comunque
  // visibile aprendo il pannello di dettaglio.
  function stripParenthetical(text) {
    return text.replace(/\s*\([^)]*\)/g, '').trim();
  }

  // Alcuni nomi restano troppo lunghi per la mappa anche da soli, senza
  // essere in un cluster: un'abbreviazione mirata SOLO per l'etichetta
  // visibile sulla mappa lascia piu' margine di manovra all'algoritmo di
  // posizionamento. Il nome completo resta ovunque altrove (aria-label,
  // popover cluster, pannello di dettaglio).
  const MAP_LABEL_ABBREVIATIONS = {
    'Sports Image Incorporated': 'Sports Image',
  };

  // Abbreviazioni valide SOLO dentro le card regionali mobile, mai sulla
  // mappa nazionale desktop — stesso criterio con cui esiste un
  // MOBILE_LABEL_POSITION_OVERRIDES separato da quello condiviso: sul desktop
  // questi nomi trovano gia' posto per intero, ed e' il ritaglio regionale,
  // molto piu' piccolo del viewBox nazionale, a renderli ingombranti. Un
  // nome abbreviato anche sul desktop sarebbe una perdita di informazione
  // senza alcuna necessita'.
  const MOBILE_MAP_LABEL_ABBREVIATIONS = {
    // Il nome piu' lungo dell'intera mappa (31 caratteri). Nella card "Sud",
    // dove il font e' ingrandito per compensare un viewBox largo 560, il
    // testo intero misura ~400 unita': piu' di due terzi della card. Anche
    // spezzato su piu' righe (vedi labelLineVariants) restava un blocco cosi'
    // ingombrante da non trovare posto accanto al proprio dot — finiva a 47
    // unita' da esso, e per giunta occupava proprio lo spazio a sinistra di
    // "Nutmeg Mills", spingendo via anche quella (35 unita' dal proprio dot).
    // Abbreviato, ciascuna delle due sta accanto al dot a cui appartiene
    // ("Riverside" a destra del proprio, "Nutmeg Mills" a sinistra del
    // proprio) e anche "True Fan" poco sotto ne guadagna. La parte
    // societaria del nome non serve a identificare il punto sulla mappa, ed
    // e' esattamente il caso per cui queste tabelle esistono: il nome
    // completo resta ovunque altrove (aria-label, elenco del cluster,
    // pannello di dettaglio, ricerca e tendine).
    //
    // Le misure qui sopra vengono da un banco di prova con un dataset
    // RICOSTRUITO (il Gist non e' raggiungibile dagli ambienti di Claude
    // Code sul web, vedi README), ma il risultato e' stato poi confermato a
    // schermo sul sito in produzione, sui dati veri.
    'Riverside Manufacturing Company': 'Riverside',
  };

  // isMobileRegion: si sta scrivendo l'etichetta dentro una card regionale
  // mobile (vedi layoutPoints/USA_REGIONS). Solo li' valgono anche le
  // abbreviazioni della tabella mobile.
  function mapLabelText(text, isMobileRegion) {
    if (isMobileRegion && MOBILE_MAP_LABEL_ABBREVIATIONS[text]) {
      return MOBILE_MAP_LABEL_ABBREVIATIONS[text];
    }
    return MAP_LABEL_ABBREVIATIONS[text] || text;
  }

  // Posizionamento etichette anti-sovrapposizione: per ogni punto si genera un
  // ventaglio di posizioni candidate a raggio crescente (ricerca "a spirale"
  // attorno al dot, piu' anelli e piu' angoli per anello) e si sceglie la
  // prima libera da sovrapposizioni e interamente dentro il viewBox. Il
  // raggio massimo resta volutamente contenuto: un'etichetta "tecnicamente"
  // non ambigua ma spostata lontano dal proprio dot risulta comunque poco
  // leggibile. Nelle zone davvero dense, se nessuna posizione entro questo
  // raggio e' del tutto libera, si preferisce restare vicini accettando una
  // minima sovrapposizione (vedi fallback in placeLabels) piuttosto che
  // allontanarsi in cerca di uno spazio "pulito".
  const LABEL_RINGS = [9, 12, 15, 18, 22, 27, 32];
  const LABEL_ANGLE_STEP = 8;

  // scale opzionale (default 1, vedi BASE_LABEL_FONT_SIZE): i raggi di
  // LABEL_RINGS delimitano quanto un'etichetta puo' allontanarsi dal proprio
  // dot per trovare spazio libero (da altre etichette, dot, O DAL BORDO DELLA
  // MAPPA). Un'etichetta ingrandita per una card regionale mobile occupa piu'
  // spazio: se i raggi restassero quelli tarati sul font di base, vicino al
  // bordo della mappa nessun raggio disponibile basterebbe piu' a contenere
  // il testo piu' largo, e placeLabels finirebbe per scegliere comunque
  // l'unica opzione — un'etichetta che esce dal viewBox (verificato con
  // region-labels-in-bounds.js: senza questo scaling, 3 etichette su 4
  // regioni sconfinavano di pochi pixel). Scalare il raggio di ricerca
  // proporzionalmente com'era gia' fatto per LABEL_POSITION_OVERRIDES.
  // point.preferAngle opzionale (in radianti): direzione verso cui e'
  // PREFERIBILE scrivere l'etichetta di questo punto. La usano i membri di
  // un cluster appena esploso (vedi explodePositions/toggleRegionCluster):
  // i dot si aprono a raggiera attorno al centroide, e se ogni etichetta
  // esce nella stessa direzione radiale del proprio dot, le etichette si
  // aprono a ventaglio insieme ai dot invece di accavallarsi verso il
  // centro del gruppo. Non e' un vincolo ma solo un ordine di preferenza:
  // i candidati restano tutti disponibili e valgono gli stessi identici
  // controlli (bordi, sovrapposizioni, ambiguita'), quindi se la direzione
  // radiale e' occupata la ricerca ripiega esattamente come sempre.
  // Il ventaglio di candidati dipende solo da tre cose: la scala, il lato
  // preferito (destra/sinistra rispetto al ritaglio) e l'eventuale direzione
  // radiale di un punto appena esploso. Per i punti SENZA direzione radiale
  // — cioe' tutti quelli di una mappa a riposo — due punti della stessa
  // mappa sullo stesso lato ricevono percio' esattamente la stessa lista,
  // nello stesso ordine: costruirla e ordinarla (315 candidati) una volta
  // per punto era lavoro rifatto identico decine di volte per ogni
  // ridisegno. Si tiene quindi in cache per chiave scala+lato.
  //
  // Gli oggetti candidato della lista in cache sono CONDIVISI tra i punti
  // (uno di essi finisce in point.labelOffset): vanno trattati come
  // immutabili, come gia' avviene: nessun chiamante li modifica, li legge
  // soltanto (paintPointsInto, labelRect, layoutConflictArea).
  const LABEL_CANDIDATE_CACHE = new Map();

  function labelCandidatesFor(point, bounds, scale) {
    const b = bounds || DEFAULT_BOUNDS;
    const s = scale || 1;
    const preferEnd = point.x > b.x1 + (b.x2 - b.x1) * 0.6;
    if (point.preferAngle === undefined) {
      const key = `${s}|${preferEnd ? 1 : 0}`;
      const cached = LABEL_CANDIDATE_CACHE.get(key);
      if (cached) return cached;
      const built = buildLabelCandidates(point, preferEnd, s);
      LABEL_CANDIDATE_CACHE.set(key, built);
      return built;
    }
    return buildLabelCandidates(point, preferEnd, s);
  }

  function buildLabelCandidates(point, preferEnd, s) {
    const candidates = [];

    LABEL_RINGS.forEach((baseRadius) => {
      const radius = baseRadius * s;
      for (let angle = 0; angle < 360; angle += LABEL_ANGLE_STEP) {
        const rad = (angle * Math.PI) / 180;
        const dx = Math.cos(rad) * radius;
        const dy = Math.sin(rad) * radius;
        let anchor = 'middle';
        if (dx > radius * 0.2) anchor = 'start';
        else if (dx < -radius * 0.2) anchor = 'end';
        candidates.push({ dx, dy, anchor, radius });
      }
    });

    // Il raggio (vicinanza al dot) viene prima di tutto: un candidato piu'
    // vicino sul lato "sbagliato" e' sempre preferibile a uno piu' lontano
    // sul lato "giusto". La preferenza di lato (tenere l'etichetta lontana
    // dal bordo mappa piu' vicino) e' solo lo spareggio tra candidati allo
    // STESSO raggio.
    // Bug corretto: prima il raggio non contava nell'ordinamento primario,
    // quindi a parita' di "lato preferito" si tentavano TUTTI i raggi (fino
    // al massimo) prima di provare il lato opposto al raggio piu' piccolo —
    // risultato: etichette spinte lontano dal dot quando sarebbe bastato
    // metterle sul lato opposto, alla stessa distanza minima.
    // Scostamento angolare dalla direzione preferita (0 = esattamente in
    // quella direzione, PI = opposta). Senza preferAngle vale 0 per tutti e
    // l'ordinamento resta identico a prima.
    // Lo scostamento si calcola UNA volta per candidato invece che a ogni
    // confronto dell'ordinamento (dove un atan2 per candidato diventava un
    // atan2 per ogni coppia confrontata).
    if (point.preferAngle !== undefined) {
      candidates.forEach((candidate) => {
        const angle = Math.atan2(candidate.dy, candidate.dx);
        candidate.deviation = Math.abs(
          ((angle - point.preferAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI
        );
      });
    }

    candidates.sort((a, b) => {
      if (a.radius !== b.radius) return a.radius - b.radius;
      // A parita' di raggio, prima la direzione radiale del ventaglio (se
      // richiesta), poi la vecchia preferenza di lato come spareggio.
      const devA = a.deviation === undefined ? 0 : a.deviation;
      const devB = b.deviation === undefined ? 0 : b.deviation;
      if (Math.abs(devA - devB) > 1e-6) return devA - devB;
      const aBad = preferEnd ? a.anchor === 'start' : a.anchor === 'end';
      const bBad = preferEnd ? b.anchor === 'start' : b.anchor === 'end';
      if (aBad !== bBad) return aBad ? 1 : -1;
      return 0;
    });

    return candidates;
  }

  // fontScale opzionale (vedi BASE_LABEL_FONT_SIZE/buildRegionCanvas): la
  // stima e' tarata sul font-size di base (11px); una card regionale mobile
  // che usa un font-size piu' grande per compensare un viewBox piu' ampio
  // (vedi sotto) deve stimare una larghezza proporzionalmente piu' grande,
  // altrimenti l'algoritmo di anti-sovrapposizione sottostimerebbe lo spazio
  // davvero occupato dal testo ingrandito.
  function estimateLabelWidth(text, fontScale) {
    // Nota: getComputedTextLength() nel browser mostra che questa stima e'
    // piu' larga del vero rendering (margine di sicurezza volontario). Un
    // test A/B su scenari reali densi (southeast textile belt) ha confermato
    // che stringere la stima peggiora gli overlap nel caso peggiore (3 invece
    // di 2) senza migliorare gli altri scenari: il margine estra va tenuto.
    return (text.length * 6.4 + 12) * (fontScale || 1);
  }

  // --- Etichette spezzate su piu' righe (solo card regionali mobile) --------
  //
  // Dentro una card regionale il font e' ingrandito per compensare un viewBox
  // piu' ampio (vedi MOBILE_LABEL_FONT_REFERENCE_WIDTH): un nome lungo occupa
  // cosi' una frazione enorme della larghezza del ritaglio — "Riverside
  // Manufacturing Company" misura ~400 unita' su un viewBox largo 560 nella
  // card "Sud". Un'etichetta del genere non entra in NESSUNA delle posizioni
  // che la ricerca sa generare: a destra del proprio dot esce dal bordo, a
  // sinistra pure, e la ricerca finisce per forza nel ripiego, che sceglie il
  // meno peggio tra candidati tutti sbagliati (verificato sul rendering
  // reale: il testo usciva dal ritaglio e veniva tagliato dal bordo della
  // card). Lo stesso meccanismo, in forma piu' lieve, spinge due nomi vicini
  // (Cincinnati e Columbus nella card "Midwest") a sovrapporsi l'uno
  // all'altro e a finire piu' vicini al dot del vicino che al proprio.
  //
  // Il rimedio e' quello che si era gia' dovuto applicare a mano, caso per
  // caso, dentro LABEL_POSITION_OVERRIDES ("Sports Image", "Delta Pro
  // Weight"): spezzare il nome su piu' righe, che lo rende molto piu'
  // stretto. Qui e' generalizzato e AUTOMATICO, cosi' non serve piu' che
  // qualcuno si accorga a mano di ogni nuovo nome lungo che entra nel Gist.
  //
  // Nota sulla priorita': la variante su una riga sola resta sempre la prima
  // provata, e viene scartata solo se non esiste NESSUNA posizione pulita per
  // lei (vedi placeLabels). Un'etichetta che oggi si piazza bene resta quindi
  // identica: si spezza solo cio' che oggi e' gia' difettoso.
  const LABEL_WRAP_MAX_LINES = 3;

  // Quanto costa, nel ripiego di placeLabels, ogni riga oltre la prima. E'
  // volutamente piccolo rispetto alle aree di sovrapposizione in gioco
  // (centinaia di unita'): non deve decidere nulla da solo, serve solo a far
  // vincere il testo intero su una riga sola quando due ripieghi sarebbero
  // altrimenti equivalenti.
  const LABEL_WRAP_FALLBACK_PENALTY = 25;

  // Spezza un testo in esattamente `count` righe, ai confini tra parole,
  // scegliendo i tagli che rendono la riga PIU' LARGA la piu' stretta
  // possibile: righe bilanciate ("Riverside" / "Manufacturing Company")
  // invece di una lunghissima e una corta. I nomi della mappa hanno poche
  // parole, quindi si prova esaustivamente ogni combinazione di tagli invece
  // di ricorrere a un'euristica greedy che qui non risparmierebbe nulla.
  // Restituisce null quando le parole non bastano per `count` righe (un nome
  // di una parola sola non e' spezzabile: "Huntington" resta com'e').
  function splitLabelIntoLines(text, count) {
    const words = String(text).split(/\s+/).filter(Boolean);
    if (words.length < count) return null;
    const cuts = [];
    let best = null;
    let bestWidth = Infinity;
    const walk = (start, remaining) => {
      if (remaining === 1) {
        const lines = [];
        let prev = 0;
        cuts.forEach((cut) => {
          lines.push(words.slice(prev, cut).join(' '));
          prev = cut;
        });
        lines.push(words.slice(prev).join(' '));
        // Un'etichetta di cluster ha la forma "Citta' · N" (vedi
        // layoutPoints): il separatore non puo' restare appeso a un capo di
        // riga, ne' aprendo la successiva ("Chicago" / "· 2") ne' chiudendo
        // la precedente ("Chicago ·" / "2") — in nessuno dei due casi si
        // legge piu' come un conteggio. "Alexander" / "City · 2" invece si',
        // e resta permessa. Se nessun taglio sopravvive, l'etichetta non e'
        // spezzabile e resta su una riga sola.
        if (lines.some((line) => line.startsWith('·') || line.endsWith('·'))) return;
        const width = Math.max(...lines.map((line) => estimateLabelWidth(line, 1)));
        if (width < bestWidth) {
          bestWidth = width;
          best = lines;
        }
        return;
      }
      for (let cut = start + 1; cut <= words.length - remaining + 1; cut++) {
        cuts.push(cut);
        walk(cut, remaining - 1);
        cuts.pop();
      }
    };
    walk(0, count);
    return best;
  }

  // Le forme alternative di una stessa etichetta, dalla piu' desiderabile
  // (una riga sola) alla piu' compatta: e' l'elenco che placeLabels prova in
  // ordine.
  //
  // Dipendono SOLO dal testo, e lo stesso testo torna a ogni ridisegno della
  // stessa card — e un tap su un cluster ne fa parecchi di fila, perche'
  // explosionIsReadable prova piu' ventagli alternativi prima di sceglierne
  // uno. Si tengono quindi da parte, come gia' si fa per le larghezze
  // misurate (LABEL_WIDTH_CACHE) e per i candidati di posizione
  // (LABEL_CANDIDATE_CACHE). Gli array restituiti sono CONDIVISI tra i
  // chiamanti: vanno trattati come immutabili, come gia' avviene (placeLabels
  // li legge soltanto, e quando ne adotta uno lo copia dentro
  // point.labelOffset).
  const LABEL_VARIANTS_CACHE = new Map();

  // Sotto quale frazione della larghezza del ritaglio un'etichetta non vale
  // la pena di spezzarla. Spezzare serve a chi e' largo rispetto alla card:
  // per un nome corto la larghezza non e' quasi mai il motivo per cui la
  // ricerca non trova posto (sono i vicini a occuparlo), e le forme spezzate
  // costerebbero solo lavoro in piu' — misure di testo nel DOM e giri di
  // ripiego — proprio nel momento piu' delicato, il tap che esplode un
  // cluster denso. Un quarto della card e' ampiamente sotto a ogni caso
  // reale in cui spezzare e' servito davvero (il piu' stretto e' "Gear for
  // Sports", al 40%; "Riverside Manufacturing Company" sta al 71%).
  const LABEL_WRAP_MIN_WIDTH_RATIO = 0.25;

  function labelIsWideForBounds(text, bounds, labelFontSize) {
    const b = bounds || DEFAULT_BOUNDS;
    const scale = labelFontSize ? labelFontSize / BASE_LABEL_FONT_SIZE : 1;
    return estimateLabelWidth(text, scale) > (b.x2 - b.x1) * LABEL_WRAP_MIN_WIDTH_RATIO;
  }

  function labelLineVariants(text) {
    const cached = LABEL_VARIANTS_CACHE.get(text);
    if (cached) return cached;
    const variants = [[text]];
    for (let count = 2; count <= LABEL_WRAP_MAX_LINES; count++) {
      const lines = splitLabelIntoLines(text, count);
      if (!lines) break;
      variants.push(lines);
    }
    LABEL_VARIANTS_CACHE.set(text, variants);
    return variants;
  }

  // Discendente (lo spazio SOTTO l'ancora) del rettangolo di un'etichetta di
  // `lineCount` righe: il solito discendente del testo piu' il passo
  // verticale reale tra le righe (1.15em, lo stesso scritto nei tspan da
  // paintPointsInto) per ogni riga oltre la prima. Le righe in piu' scendono
  // sotto l'ancora, quindi allungano il discendente e non l'altezza sopra
  // l'ancora (vedi labelRect). E' la stessa formula gia' usata a mano in
  // layoutConflictArea, qui in un posto solo perche' la usano anche
  // placeLabels e il disegno.
  function labelDescenderFor(lineCount, scale) {
    return (4 + (lineCount - 1) * 1.15 * BASE_LABEL_FONT_SIZE) * scale;
  }

  function rectsOverlap(a, b) {
    return !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2);
  }

  // Area di sovrapposizione tra due rettangoli (0 se non si toccano). Usata
  // solo nel fallback per distinguere una sovrapposizione appena accennata
  // (un angolo che si sfiora) da una quasi totale (due etichette impilate
  // l'una sull'altra, illeggibili): contare semplicemente "quante" cose si
  // toccano tratterebbe i due casi come equivalenti.
  function rectOverlapArea(a, b) {
    const xOverlap = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
    const yOverlap = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
    return xOverlap * yOverlap;
  }

  // descender opzionale (default 4, lo spazio sotto la baseline del testo
  // gia' usato prima dell'introduzione di fontScale): come height, va
  // scalato dal chiamante quando il font-size non e' quello di base.
  function labelRect(point, offset, width, height, descender) {
    const d = descender === undefined ? 4 : descender;
    let x1 = point.x + offset.dx;
    if (offset.anchor === 'end') x1 -= width;
    else if (offset.anchor === 'middle') x1 -= width / 2;
    return { x1, y1: point.y + offset.dy - height, x2: x1 + width, y2: point.y + offset.dy + d };
  }

  function isWithinBounds(rect, bounds) {
    const b = bounds || DEFAULT_BOUNDS;
    return rect.x1 >= b.x1 && rect.x2 <= b.x2 && rect.y1 >= b.y1 && rect.y2 <= b.y2;
  }

  // Il raggio corrisponde alla dimensione VISIVA effettiva del dot a riposo
  // (6 singolo, 8 cluster — stessi valori di dotRadius in renderMap), non a
  // quella ingrandita dell'hover con margine extra: quel margine (prima 8+2 e
  // 10+2) creava una zona vietata piu' grande del dot vero, bloccando
  // posizioni realmente libere attorno a dot vicini — es. un'etichetta che
  // avrebbe potuto stare sotto al proprio dot restava scartata perche' la
  // zona "vietata" di un dot distinto poco piu' in la' iniziava prima del
  // dovuto. scale opzionale (default 1, vedi BASE_LABEL_FONT_SIZE): stesso
  // fattore usato per ingrandire il font nelle card regionali mobile (vedi
  // paintPointsInto) — il dot reso a schermo cresce della stessa proporzione
  // (vedi paintPointsInto), quindi anche il suo ingombro come ostacolo per
  // le altre etichette deve crescere di conseguenza.
  function dotObstacleRect(point, scale) {
    const radius = (point.group.brands.length > 1 ? 8 : 6) * (scale || 1);
    return { x1: point.x - radius, y1: point.y - radius, x2: point.x + radius, y2: point.y + radius };
  }

  function countNearbyPoints(point, allPoints, thresholdSq) {
    let count = 0;
    allPoints.forEach((other) => {
      if (other === point) return;
      const dx = other.x - point.x;
      const dy = other.y - point.y;
      if (dx * dx + dy * dy <= thresholdSq) count++;
    });
    return count;
  }

  // Come countNearbyPoints, ma contando su un altro insieme di punti
  // (crowdingFrom di placeLabels): il punto stesso non vi compare come
  // oggetto, quindi si salta per gruppo, e per una zona si saltano i suoi
  // membri.
  function countNearbyPlaces(point, places, thresholdSq) {
    const own = point.group.sourceGroups && point.group.sourceGroups.length > 1 ? point.group.sourceGroups : null;
    let count = 0;
    places.forEach((other) => {
      if (other.group === point.group || (own && own.indexOf(other.group) !== -1)) return;
      const dx = other.x - point.x;
      const dy = other.y - point.y;
      if (dx * dx + dy * dy <= thresholdSq) count++;
    });
    return count;
  }

  // Distanza minima da un punto al perimetro di un rettangolo (0 se il punto
  // e' dentro). Usata per capire quanto un'etichetta e' "vicina" a un dot:
  // guardare solo il centro dell'etichetta penalizzerebbe ingiustamente i
  // nomi lunghi (il centro di un'etichetta larga e' sempre distante dal
  // proprio dot anche quando l'etichetta gli e' letteralmente appoggiata),
  // mentre guardare solo l'ancora ignorerebbe che l'estremo opposto di
  // un'etichetta lunga puo' finire vicinissimo a un dot diverso.
  function pointToRectDistance(px, py, rect) {
    const dx = Math.max(rect.x1 - px, 0, px - rect.x2);
    const dy = Math.max(rect.y1 - py, 0, py - rect.y2);
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Per un dato rettangolo etichetta, distanza dal dot proprietario e dal dot
  // (tra tutti gli altri) piu' vicino ad ANY parte del rettangolo — serve a
  // garantire che nessun punto dell'etichetta finisca mai piu' vicino a un
  // dot diverso dal proprio: altrimenti l'etichetta sembrerebbe riferirsi a
  // quello sbagliato, anche se tecnicamente libera da sovrapposizioni.
  //
  // `others` e' l'elenco degli ALTRI punti (mai il proprio). Prima si
  // passava l'elenco completo piu' l'indice del punto da saltare: dovendo
  // essere l'indice esatto dentro QUEL preciso array, l'elenco non poteva
  // essere ridotto ai soli vicini che possono davvero risultare piu' vicini
  // del proprio dot (vedi nearbyPoints in placeLabels), e ogni candidato
  // pagava una distanza per OGNI dot della mappa. Saltare il proprio punto
  // per identita' invece che per posizione toglie quel vincolo senza
  // cambiare in nulla il risultato.
  function ambiguityInfo(point, rect, others) {
    const ownDist = pointToRectDistance(point.x, point.y, rect);
    let otherDist = Infinity;
    for (let i = 0; i < others.length; i++) {
      const other = others[i];
      if (other === point) continue;
      const dist = pointToRectDistance(other.x, other.y, rect);
      if (dist < otherDist) otherDist = dist;
    }
    return { ownDist, otherDist, ambiguous: otherDist <= ownDist };
  }

  // Le etichette devono evitare sia le altre etichette gia' piazzate sia i dot
  // dei punti vicini (non il proprio, che e' l'ancora intenzionale dell'offset),
  // non devono mai uscire dal viewBox della mappa, e — vincolo piu' importante
  // di tutti in zone dense — non devono mai finire piu' vicine a un dot
  // diverso dal proprio. I punti piu' affollati (con piu' vicini nel raggio
  // NEARBY_THRESHOLD) vengono piazzati per primi: hanno meno alternative
  // libere, quindi conviene dargli la scelta migliore finche' lo spazio
  // intorno e' ancora tutto disponibile.
  const NEARBY_THRESHOLD = 100;

  // Eccezione manuale per il punto piu' denso dell'intera mappa (il pugno di
  // dot quasi coincidenti Hanes/Wrangler/Martinsville/Stedman/Soffe/Sports
  // Image Incorporated nella zona tessile NC): l'euristica generale, qui,
  // non ha UNA soluzione che non sposti semplicemente il difetto su un'altra
  // etichetta vicina (verificato con piu' tentativi: usare la larghezza
  // reale invece di quella stimata nel fallback risolve una posizione ma ne
  // rompe un'altra nello stesso cluster). Per questi punti, verificati
  // visivamente col rendering reale, si fissano le posizioni invece di
  // continuare a rincorrere l'euristica: Wrangler e Stedman a destra del
  // proprio dot, ciascuno il piu' possibile centrato sul proprio dot (dy
  // vicino a 0, non impilati stretti come in un primo tentativo) — c'e'
  // abbastanza spazio verticale tra i due dot (~8 unita') e verso il resto
  // della mappa per distanziarli, restando comunque leggibili. Soffe a
  // destra del proprio dot. Sports Image su due righe ("Sports" / "Image",
  // via tspan) a sinistra del proprio dot: da sola la scritta su una riga
  // era troppo larga per stare a sinistra senza toccare l'etichetta di
  // Healthknit; spezzata su due righe piu' strette (~32 unita' ciascuna
  // invece di ~65), sfiora solo leggermente l'etichetta di Hanes sopra di se'
  // (~1% dell'area, verificato con overlapArea() e a schermo — non un difetto
  // reale) restando comunque piu' vicina al proprio dot che a qualsiasi
  // altro. Hanes abbassato e riavvicinato al proprio dot (era 15.6 unita'
  // sopra, ora 6.5) fino a restare appena sopra l'etichetta di Sports Image:
  // verificato con overlapArea() che resta completamente pulito (nessuna
  // sovrapposizione, nessuna ambiguita' — piu' vicino al proprio dot che a
  // qualsiasi altro) e piu' vicino visivamente al dot a cui appartiene.
  const LABEL_POSITION_OVERRIDES = {
    Hanes: { dx: -9, dy: -6, anchor: 'end', radius: 11 },
    Wrangler: { dx: 9, dy: -2, anchor: 'start', radius: 9 },
    Stedman: { dx: 9, dy: 2, anchor: 'start', radius: 9 },
    Soffe: { dx: 9, dy: 0, anchor: 'start', radius: 9 },
    'Sports Image': { dx: -6, dy: -5, anchor: 'end', radius: 14, lines: ['Sports', 'Image'] },
  };

  // Eccezione manuale come LABEL_POSITION_OVERRIDES sopra, ma applicata SOLO
  // dentro le card regionali mobile (mai sulla mappa nazionale desktop): sul
  // desktop questi stessi punti trovano gia' una posizione pulita e vicina
  // al proprio dot (verificato con dump-all-labels.js), perche' il ritaglio
  // regionale mobile — molto piu' piccolo del viewBox nazionale — e' cio' che
  // rende densa la zona attorno a loro, non un problema del punto in se'. Un
  // override in LABEL_POSITION_OVERRIDES (condiviso con desktop) sposterebbe
  // quindi un'etichetta desktop gia' corretta senza necessita'.
  const MOBILE_LABEL_POSITION_OVERRIDES = {
    // NOTA: qui c'era un override per "Collegiate Pacific" (card "Midwest"),
    // aggiunto perche' la ricerca automatica non trovava una posizione entro
    // il raggio utile e ripiegava su un candidato lontanissimo dal dot. La
    // causa vera era la larghezza del testo: essendo misurata a mappa ancora
    // nascosta, tornava 0 e tutte le etichette mobile ripiegavano sulla stima
    // prudente, fino al ~39% piu' larga del testo reale (vedi renderData, dove
    // ora la mappa viene resa visibile PRIMA di disegnarla). Con la larghezza
    // vera la ricerca automatica trova da sola una posizione a 10,5 unita' dal
    // proprio dot, mentre l'override — tarato su quelle misure sbagliate —
    // scriveva ormai l'etichetta sopra il dot di Carhartt (117px² misurati sul
    // rendering reale, cioe' sopra un dot ALTRUI: il difetto peggiore
    // possibile). E' stato quindi rimosso: nessun override e' meglio di un
    // override tarato su una misura sbagliata.
    // Nella card "Sud", Healthknit e Delta Pro Weight sono strette tra i
    // cluster "3"/"8 marchi in questa zona", Soffe e Tee Jays — la
    // stessa zona affollata gia' nota dal cluster NC (vedi
    // LABEL_POSITION_OVERRIDES sopra), ma con questi due punti che restano
    // dot singoli (non un cluster da esplodere) anche su mobile: la ricerca
    // automatica ripiegava su posizioni lontane e ambigue (verificato sul
    // rendering reale, es. "Delta Pro Weight" finiva sopra il dot di Tee
    // Jays). Sopra il proprio dot e' l'unica direzione libera per
    // Healthknit. Per Delta Pro Weight nessuna singola riga larga trova
    // spazio in nessuna direzione: spezzata su due righe ("Delta Pro" /
    // "Weight", come "Sports Image" sopra) il blocco piu' stretto entra
    // sotto al proprio dot senza toccare ne' Alexander City (il cluster
    // "2 marchi" appena sotto) ne' gli altri vicini. dy alzato da 18 a
    // 14 (verificato sul rendering reale): a 18 restava tecnicamente senza
    // sovrapposizioni di bounding box, ma il margine verso l'etichetta
    // "Alexander City · 2" sotto era troppo stretto per leggersi come due
    // etichette distinte. Oltre 14 (provato 12) l'etichetta inizia invece a
    // toccare il dot di Soffe sopra.
    Healthknit: { dx: 0, dy: -20, anchor: 'middle', radius: 20 },
    'Delta Pro Weight': { dx: 10, dy: 14, anchor: 'middle', radius: 18, lines: ['Delta Pro', 'Weight'] },
    // dy alzato da ~26.6 (quanto sceglieva gia' la ricerca automatica) a 28:
    // il margine misurato in questo harness di test (~11px a schermo) era
    // gia' tecnicamente pulito, ma l'utente ha verificato su un dispositivo
    // reale che il margine vero (font del tema effettivo, con metriche
    // diverse da quelle di questo harness) risultava troppo stretto per
    // leggere le due etichette come distinte. dy:33 (provato prima) allargava
    // troppo il margine, spostando l'etichetta visibilmente lontana dal
    // proprio dot: 28 e' il compromesso verificato (~14px di margine reale,
    // circa il 25% in piu' del margine originale insufficiente, ma senza
    // staccare l'etichetta dal dot). Verificato che dy:28 non tocca ne' Tee
    // Jays ne' 3D Emblem ne' Nutmeg Mills piu' in basso.
    //
    // A differenza degli altri override di questa mappa (validi sempre,
    // anche durante un'esplosione altrove nella card), questo era stato
    // inizialmente applicato SOLO alla card collassata (un tag temporaneo,
    // MOBILE_LABEL_POSITION_OVERRIDES_COLLAPSED_ONLY + un flag
    // anyExplosionActive dedicato), perche' un'esplosione del cluster da 8
    // nella regione "Sud" porta un suo membro (Stedman) a meno di 1 unita'
    // dal punto in cui questo dx/dy fisso scriverebbe il testo — e lo stesso
    // problema segnalato qui (etichetta troppo vicina a "Delta Pro Weight")
    // si ripresentava, con lo stesso identico rimedio, quando a esplodere
    // era invece il cluster da 3 dello stesso NC (la ricerca automatica
    // usata li' al posto dell'override sceglieva un margine reale di soli
    // ~4px, sotto alla soglia gia' rivelatasi insufficiente). Ora che
    // overrideAnchorIsSafe (vedi sopra placeLabels) scarta genericamente
    // QUALSIASI override quando il suo punto di ancoraggio e' diventato
    // troppo vicino a un dot altrui — esattamente il caso di Stedman durante
    // l'esplosione del cluster da 8 — l'override puo' restare nella mappa
    // SEMPRE VALIDA come gli altri qui sopra: resta attivo (e risolve anche
    // il caso "3") in ogni stato tranne quello specifico in cui servirebbe
    // davvero disattivarlo, senza bisogno di un meccanismo dedicato.
    'Alexander City · 2': { dx: 5, dy: 28, anchor: 'middle', radius: 28 },
  };

  // fontScale opzionale (default 1, vedi BASE_LABEL_FONT_SIZE): quando una
  // card regionale mobile usa un font-size piu' grande del solito (per
  // compensare un viewBox piu' ampio — vedi buildRegionCanvas), ogni misura
  // in unita' di viewBox derivata dalla dimensione del testo (larghezza
  // stimata, altezza, discendente, e gli offset di LABEL_POSITION_OVERRIDES)
  // va scalata di conseguenza: altrimenti l'anti-sovrapposizione userebbe
  // stime tarate sul font piu' piccolo, sottostimando lo spazio davvero
  // occupato dal testo ingrandito.
  //
  // disableOverrideIndices opzionale (default nessuno, un Set di
  // point.index): LABEL_POSITION_OVERRIDES/MOBILE_LABEL_POSITION_OVERRIDES
  // sono tarati su un layout GEOGRAFICO fisso (le posizioni vere dei punti
  // sulla mappa nazionale/nella card regionale collassata) — dx/dy fissi
  // hanno senso solo se il dot resta dov'era quando l'override e' stato
  // calibrato. Quando un cluster per vicinanza viene ESPLOSO
  // (toggleRegionCluster), SOLO i suoi membri vengono ricollocati a ventaglio
  // da explodePositions: conservano la direzione vera rispetto al centroide,
  // ma la loro distanza da esso cambia — quindi non sono piu' nel punto per
  // cui un dx/dy fisso era stato calibrato. Applicare comunque lo stesso
  // dx/dy fisso (verificato sul rendering reale: succede, "Wrangler"
  // finiva sopra "Sports Image" con ~474px² di sovrapposizione) sposta
  // l'etichetta in una direzione che non ha piu' alcun senso rispetto alla
  // nuova posizione del dot. Per questi soli indici si disabilita quindi
  // l'override e si lascia che la ricerca automatica (comunque valida per
  // qualsiasi geometria, ricalcolata sulla posizione vera di ciascun dot)
  // piazzi l'etichetta. Gli ALTRI punti della stessa card regionale (dot
  // NON coinvolti in quella esplosione, es. "Collegiate Pacific" in
  // "Midwest" o "Healthknit"/"Delta Pro Weight" in "Sud") restano nella
  // loro posizione geografica vera per tutta la durata dell'esplosione e
  // devono quindi CONTINUARE a usare il proprio override fisso — passare
  // qui un singolo booleano valido per l'intera card (come in una prima
  // versione di questa funzione) li faceva invece rimbalzare su una
  // posizione automatica ogni volta che un QUALSIASI cluster della stessa
  // card veniva esploso, anche quando non c'entravano nulla con
  // quell'esplosione (verificato sul rendering reale).
  //
  // disableOverrideIndices sopra e' una correzione SCOPERTA a mano dopo aver
  // visto sul rendering reale un dx/dy fisso finire sopra un dot diverso dal
  // proprio (i membri esplosi rispetto a se stessi). E' corretta e resta
  // cosi', ma da sola non copre un caso diverso: un override che NON
  // appartiene al cluster esploso puo' comunque ritrovarsi troppo vicino a
  // un membro ALTRUI appena atterrato li' dall'esplosione (es. "Alexander
  // City", il cui dx/dy fisso non ha nulla a che fare col cluster NC che le
  // esplode accanto, eppure un suo membro puo' atterrarci sopra). Scoprire
  // e classificare ogni caso simile a mano (un flag dedicato per QUESTO
  // override, come si e' fatto la prima volta) richiederebbe che qualcuno
  // se ne accorga di nuovo a ogni nuovo caso — overrideAnchorIsSafe sotto e'
  // una rete di sicurezza GENERALE, valida per QUALSIASI override (presente
  // o futuro, in una qualsiasi delle due mappe sopra): prima di fidarsi di
  // un dx/dy fisso, controlla se il punto preciso in cui verrebbe scritta
  // ciascuna riga del testo e' rimasto abbastanza lontano da OGNI altro dot
  // attualmente sulla mappa (inclusi quelli appena spostati da
  // un'esplosione altrove, di QUALSIASI cluster) — se un dot vi si e'
  // avvicinato troppo, l'override viene scartato per QUESTO SOLO rendering
  // e si lascia scegliere alla ricerca automatica, senza dover prima
  // scoprire e classificare a mano il caso specifico.
  function overrideAnchorIsSafe(point, scaledOverride, override, points, scale, bounds, isMobileRegion) {
    const numLines = override.lines ? override.lines.length : 1;
    // Passo verticale reale tra una riga e la successiva (1.15em, lo stesso
    // usato dai tspan in renderMap): le righe oltre la prima di un'etichetta
    // multi-riga scendono SEMPRE verso il basso rispetto all'ancora, a
    // prescindere dal segno di dy — vanno controllate anche loro, non solo
    // il punto di ancoraggio della prima riga, altrimenti un dot che si
    // avvicina troppo alla SECONDA riga (es. "Weight" sotto "Delta Pro")
    // passerebbe inosservato.
    const lineStep = 1.15 * BASE_LABEL_FONT_SIZE * scale;
    for (let line = 0; line < numLines; line++) {
      const anchorX = point.x + scaledOverride.dx;
      const anchorY = point.y + scaledOverride.dy + line * lineStep;
      for (let k = 0; k < points.length; k++) {
        const other = points[k];
        if (other.index === point.index) continue;
        const otherRadius = (other.group.brands.length > 1 ? 8 : 6) * scale;
        const clearance = Math.hypot(other.x - anchorX, other.y - anchorY) - otherRadius;
        if (clearance < MIN_OVERRIDE_ANCHOR_CLEARANCE * scale) return false;
      }
    }

    // Il controllo sul RETTANGOLO qui sotto vale solo per le card regionali
    // mobile, che sono i casi per cui e' nato: font ingrandito che fa
    // sporgere il testo dalla card, e "Alexander City · 2" scritta sopra il
    // dot di un vicino. Sulla mappa nazionale desktop i dot non si muovono
    // (i cluster si aprono in un elenco, non esplodono) e gli override di
    // LABEL_POSITION_OVERRIDES sono stati tarati e verificati a schermo
    // proprio li'. Applicato anche li', con le sue stime prudenti, scartava
    // override buoni: "Sports Image" su due righe a sinistra del dot sfiorava
    // di un'unita' il quadrato attorno al dot di Delta Pro Weight, veniva
    // scartato, e la ricerca automatica la rimetteva su una riga a destra,
    // addosso a Wrangler e Stedman (dal 2026-09-16 al fix). E il commit che
    // aveva introdotto il controllo dichiarava la mappa desktop invariata.
    if (!isMobileRegion) return true;

    // Il punto di ancoraggio non e' l'etichetta: con ancora 'middle' il testo
    // si estende per meta' larghezza da entrambi i lati, quindi un dot puo'
    // restare lontanissimo dall'ancora e finire lo stesso sotto al testo. Il
    // controllo sopra, che guarda solo l'ancora, non lo vede (verificato sul
    // rendering reale: "Alexander City · 2" scritta per intero sopra il dot
    // di un vicino, con l'ancora a decine di unita' di distanza). Lo stesso
    // vale per il bordo del ritaglio: un dx/dy fisso non sa nulla di quanto
    // sia largo il testo che ci finisce, e con il font ingrandito di una card
    // regionale il testo puo' sporgere fuori dalla card.
    //
    // Si controlla quindi anche il RETTANGOLO vero dell'etichetta. Vale la
    // stessa logica dell'ancora: non e' una correzione dell'override, e'
    // solo il criterio per cui, in questo preciso rendering, non ci si fida
    // di un dx/dy fisso e si lascia scegliere alla ricerca automatica —
    // che, a differenza dell'override, ricalcola tutto sulla geometria
    // attuale.
    // Stessa distinzione di placeLabels tra le due larghezze: la stima
    // prudente per il confronto con i dot (esagerare li' e' innocuo) e la
    // misura REALE per il bordo del ritaglio (esagerare li' scarterebbe un
    // override che a schermo entra benissimo). Per un override gia' spezzato
    // su piu' righe la misura reale del testo intero non serve a niente:
    // resta la stima, riga per riga.
    const height = 14 * scale;
    const descender = labelDescenderFor(numLines, scale);
    const overlapWidth = override.lines
      ? Math.max(...override.lines.map((line) => estimateLabelWidth(line, scale)))
      : estimateLabelWidth(point.labelText, scale);
    const boundsWidth =
      !override.lines && point.width !== undefined && point.width > 3 ? point.width : overlapWidth;
    if (!isWithinBounds(labelRect(point, scaledOverride, boundsWidth, height, descender), bounds)) {
      return false;
    }
    const rect = labelRect(point, scaledOverride, overlapWidth, height, descender);
    for (let k = 0; k < points.length; k++) {
      const other = points[k];
      if (other.index === point.index) continue;
      if (rectsOverlap(dotObstacleRect(other, scale), rect)) return false;
    }
    return true;
  }

  // Margine minimo (in unita' di viewBox al font-size di base) tra il punto
  // di ancoraggio di un override e il dot ALTRUI piu' vicino, sotto il
  // quale l'override viene considerato inaffidabile per questo rendering
  // (vedi overrideAnchorIsSafe sopra). Calibrato misurando, con lo stesso
  // controllo, TUTTI gli stati reali del dataset attuale (ogni card
  // collassata e ogni singola esplosione, desktop e ogni regione mobile):
  // il margine piu' stretto tra un override oggi valido e il dot piu'
  // vicino non scende mai sotto le ~7 unita' (il caso piu' stretto e' nel
  // pugno di dot NC su desktop, tra "Stedman"/"Wrangler" e "Martinsville").
  // 2 unita' lascia quindi ampio margine per non disturbare nessun caso
  // attuale (verificato con dump-all-labels.js e con un controllo di
  // sovrapposizioni su tutte le card/esplosioni mobile: nessuna differenza
  // rispetto a prima), pur restando ben al di sotto del margine con cui e'
  // stata scoperta la collisione reale che ha motivato questo controllo
  // ("Alexander City" a meno di 1 unita' dal dot di "Stedman" appena
  // fuoriuscito dall'esplosione del cluster da 8 in "Sud").
  const MIN_OVERRIDE_ANCHOR_CLEARANCE = 2;

  // crowdingFrom opzionale: i punti su cui contare i vicini che decidono
  // l'ordine di piazzamento (vedi sotto, crowding). Serve solo alla mappa
  // desktop con una zona (desktopZoneGroups): l'ordine va contato sui punti
  // di prima, tutti separati, altrimenti togliere i membri della zona dal
  // conteggio cambia l'ordine di meta' mappa — NEARBY_THRESHOLD e' di 100
  // unita' — e sposta etichette lontanissime che non c'entrano (verificato:
  // Excel Sportswear finiva a 38 unita' dal proprio dot, piu' vicina a un
  // altro).
  function placeLabels(points, bounds, fontScale, disableOverrideIndices, isMobileRegion, crowdingFrom) {
    const scale = fontScale || 1;
    const dotObstacles = points.map((point) => dotObstacleRect(point, scale));
    const placedLabelRects = [];

    const processingOrder = points
      .map((point) => {
        const override =
          disableOverrideIndices && disableOverrideIndices.has(point.index)
            ? undefined
            : LABEL_POSITION_OVERRIDES[point.labelText] ||
              (isMobileRegion ? MOBILE_LABEL_POSITION_OVERRIDES[point.labelText] : undefined);
        // dx/dy/radius sono tarati per il font-size di base (11px): scalati
        // qui cosi' restano proporzionalmente corretti anche quando questo
        // override si applica dentro una card regionale con font ingrandito
        // (es. il cluster NC che esplode nella regione "Sud"). Calcolato
        // gia' qui (non nel forEach sotto) perche' overrideAnchorIsSafe deve
        // girare PRIMA dell'ordinamento, non solo prima dell'uso: un punto
        // il cui override verra' scartato da overrideAnchorIsSafe (vedi
        // sotto) deve comportarsi in tutto e per tutto come un punto SENZA
        // override, compreso nell'ordine di elaborazione — verificato sul
        // rendering reale che non farlo (valutare la sicurezza solo al
        // momento dell'uso, con l'ordinamento gia' deciso sulla sola
        // presenza dell'override nella mappa) sposta un punto cosi' PRIMA
        // di altri automatici con cui condivide il turno, cambiando quali
        // rettangoli sono gia' in placedLabelRects quando la sua ricerca
        // automatica di ripiego gira: "Alexander City · 2" finiva per
        // scegliere una posizione diversa (e stavolta sovrapposta a "Delta
        // Pro Weight", ~680px²) proprio perche' l'esplosione del cluster da
        // 8 le dava un override sulla carta valido (scartato solo dopo,
        // durante l'esplosione, per la vicinanza a Stedman) che la faceva
        // slittare in cima all'ordine invece che restare mescolata per
        // crowding con gli altri punti automatici come nello stato
        // collassato, dove infatti l'override resta valido e il problema
        // non si presenta.
        const scaledOverride =
          override && (scale === 1 ? override : { ...override, dx: override.dx * scale, dy: override.dy * scale, radius: override.radius * scale });
        const overrideIsSafe = !!override && overrideAnchorIsSafe(point, scaledOverride, override, points, scale, bounds, isMobileRegion);
        return {
          point,
          override: overrideIsSafe ? override : undefined,
          scaledOverride: overrideIsSafe ? scaledOverride : undefined,
          crowding: crowdingFrom
            ? countNearbyPlaces(point, crowdingFrom, NEARBY_THRESHOLD * NEARBY_THRESHOLD)
            : countNearbyPoints(point, points, NEARBY_THRESHOLD * NEARBY_THRESHOLD),
        };
      })
      .sort((a, b) => {
        // I punti con una posizione FISSA (override) non hanno bisogno di
        // conoscere gli ostacoli per scegliere la propria posizione — SOLO
        // dentro le card regionali mobile (isMobileRegion) vengono quindi
        // piazzati per primi, PRIMA di qualsiasi punto con ricerca
        // automatica, cosi' il loro rettangolo entra tra gli ostacoli fin
        // dall'inizio. Bug corretto: prima l'ordine dipendeva solo dalla
        // crowding (numero di vicini nel raggio NEARBY_THRESHOLD), quindi
        // in certe configurazioni un punto automatico con crowding piu'
        // alto veniva elaborato PRIMA di un punto con override vicino,
        // senza sapere che quella posizione fissa sarebbe stata occupata
        // li' — risultato: sovrapposizione reale (verificato sul rendering
        // reale, es. "Alexander City · 2" che finiva sopra "Delta Pro
        // Weight" quando il cluster da 8 esplodeva nella regione "Sud",
        // perche' i nuovi dot esplosi vicini cambiavano la crowding di
        // entrambi abbastanza da invertirne l'ordine di elaborazione).
        // Ristretto al mobile: sul desktop cambiare quest'ordine sposta
        // leggermente "Martinsville · 4" (nessun difetto reale, gia'
        // pulito prima) — verificato byte-per-byte con dump-all-labels.js
        // che senza questa restrizione il desktop non resta piu' identico.
        if (isMobileRegion && !!a.override !== !!b.override) return a.override ? -1 : 1;
        return b.crowding - a.crowding;
      });

    processingOrder.forEach(({ point, override, scaledOverride }) => {
      if (override) {
        point.labelOffset = scaledOverride;
        const width = override.lines
          ? Math.max(...override.lines.map((line) => estimateLabelWidth(line, scale)))
          : estimateLabelWidth(point.labelText, scale);
        // NOTA: per le etichette multi-riga questa "height" (14 per riga,
        // impilata sopra offset.dy) e' un'approssimazione grossolana, non il
        // vero ingombro (che andrebbe dalla cima della prima riga al fondo
        // dell'ultima, usando il reale passo verticale tra le righe). E'
        // COSCIENTEMENTE lasciata cosi': un rettangolo piu' fedele qui e'
        // stato provato e testato (vedi test A/B con dump completo di tutte
        // le etichette prima/dopo), e cambia la posizione scelta per "Hanes"
        // di circa 7 unita' — nessun'altra etichetta sul resto della mappa ne
        // risente. Poiche' oggi non causa alcun difetto visibile, si
        // preferisce NON toccarla piuttosto che spostare un'etichetta gia'
        // approvata; se in futuro la zona NC cambia (nuovo brand, nuovo
        // override) e questa approssimazione diventa un problema reale,
        // sostituire con un rettangolo che copra dalla cima della prima riga
        // al fondo dell'ultima (passo verticale reale: 1.15em a font-size
        // 11px, vedi tspan in renderMap).
        const height = (override.lines ? 14 * override.lines.length : 14) * scale;
        placedLabelRects.push(labelRect(point, scaledOverride, width, height, 4 * scale));
        return;
      }

      // Larghezza "sicura" (con margine) per il controllo di sovrapposizione
      // con altri dot/etichette: una stima larga qui e' innocua, evita solo
      // qualche candidato in piu'. Larghezza REALE (misurata nel browser via
      // measureLabelWidths, vedi ManifattureSection) SOLO per il controllo
      // dei bordi mappa: usare la stessa stima larga anche li' scartava
      // candidati vicino al bordo che in realta' entravano perfettamente
      // (spazio bianco, nessun rischio) — es. un'etichetta lunga vicino alla
      // costa che avrebbe potuto stare a destra del proprio dot, dove non
      // c'era nient'altro, veniva scartata solo perche' la stima esagerava
      // la larghezza del testo. estimateLabelWidth resta il fallback per i
      // test Node (senza DOM/SVG per misurare per davvero) in entrambi i casi.
      //
      // Bug preesistente scoperto testando lo scaling: measureLabelWidths
      // puo' restituire una larghezza pari al solo margine (3, cioe'
      // getComputedTextLength() = 0) se l'<svg> non ha ancora una larghezza
      // risolta nel momento in cui viene misurato — verificato che succede
      // ANCHE sulla mappa desktop (fontScale assente), quindi non e' un
      // effetto collaterale di questo cambiamento. La' non e' mai stato un
      // problema visibile: un boundsWidth vicino a zero fa sempre passare il
      // controllo bordi, il che e' innocuo quando il margine reale di
      // sicurezza (dot lontani dal bordo del viewBox nazionale) e' ampio per
      // puro caso. Con un font ingrandito per compensare un viewBox
      // regionale piu' ampio (scale !== 1, vedi buildRegionCanvas), lo stesso
      // margine "fortunato" non basta piu' e qualche etichetta finisce per
      // uscire dal ritaglio della card (verificato con
      // region-labels-in-bounds.js). Ricadere sulla stima sicura SOLO
      // quando scale !== 1 corregge questo caso senza toccare in alcun modo
      // il calcolo del desktop (scale === 1 sempre, comportamento
      // rigorosamente invariato, verificato byte-per-byte con
      // dump-all-labels.js).
      const overlapWidth = estimateLabelWidth(point.labelText, scale);
      // Una larghezza pari al solo margine (3) vuol dire che
      // getComputedTextLength() ha restituito 0, cioe' che il testo non era
      // misurabile: e' una NON misura, e va ignorata anche sulla mappa
      // nazionale. Prima l'ignoravamo solo con un font ingrandito
      // (scale !== 1) — sul desktop la si prendeva per buona, e un
      // boundsWidth di 3 fa passare qualunque controllo di bordo: le
      // etichette potevano uscire dal viewBox senza che nulla le fermasse.
      // Non succede nel caso normale (la mappa viene resa visibile prima di
      // essere disegnata, vedi renderBrands), quindi il comportamento
      // ordinario non cambia: cambia solo cosa succede quando la misura
      // fallisce davvero.
      const widthLooksMeasured = point.width !== undefined && point.width > 3;
      const boundsWidth = widthLooksMeasured ? point.width : overlapWidth;
      const height = 14 * scale;
      const descender = 4 * scale;
      // Le forme alternative della stessa etichetta (vedi labelLineVariants):
      // la prima e' SEMPRE il testo su una riga sola, con esattamente le
      // stesse misure di prima — sulla mappa nazionale desktop (dove
      // point.labelVariants non viene mai preparato, vedi layoutPoints)
      // l'elenco si ferma li' e tutto il resto di questa funzione lavora
      // esattamente come prima.
      const variants = (point.labelVariants || [[point.labelText]]).map((lines, variantIndex) => {
        if (variantIndex === 0) {
          return { lines, overlapWidth, boundsWidth, height, descender };
        }
        const variantOverlapWidth = Math.max(...lines.map((line) => estimateLabelWidth(line, scale)));
        // Stesso criterio del testo su una riga sola: la larghezza REALE
        // (misurata nel DOM, qui la piu' larga delle righe) solo per il
        // controllo dei bordi, e solo se e' una misura vera.
        const measured = point.variantWidths ? point.variantWidths[variantIndex] : undefined;
        return {
          lines,
          overlapWidth: variantOverlapWidth,
          boundsWidth: measured !== undefined && measured > 3 ? measured : variantOverlapWidth,
          height,
          descender: labelDescenderFor(lines.length, scale),
        };
      });
      const candidates = labelCandidatesFor(point, bounds, scale);
      // Nessun candidato puo' spingere l'etichetta oltre l'ultimo anello
      // della ricerca: tutti i rettangoli generati qui sotto stanno dentro
      // questo riquadro. Un ostacolo che non lo tocca nemmeno non potra'
      // mai sovrapporsi ad alcun candidato, e un dot piu' lontano del
      // doppio della diagonale utile non potra' mai risultare piu' vicino
      // all'etichetta del dot proprio (quindi non puo' renderla ambigua):
      // entrambi si possono escludere una volta per punto invece di
      // ricontrollarli per ognuno dei 315 candidati. E' una potatura
      // puramente geometrica, il risultato resta identico.
      const maxRadius = LABEL_RINGS[LABEL_RINGS.length - 1] * scale;
      // Il riquadro deve contenere i candidati di OGNI variante: la piu'
      // larga e' sempre quella su una riga sola (spezzare puo' solo
      // restringere), la piu' alta l'ultima (piu' righe = discendente piu'
      // lungo). Senza varianti i due estremi coincidono con l'unica forma
      // possibile e il riquadro resta identico a prima.
      const deepestDescender = variants[variants.length - 1].descender;
      const reach = {
        x1: point.x - maxRadius - overlapWidth,
        y1: point.y - maxRadius - height,
        x2: point.x + maxRadius + overlapWidth,
        y2: point.y + maxRadius + deepestDescender,
      };
      const farthestReach = Math.hypot(maxRadius + overlapWidth, maxRadius + height);
      const ambiguityRangeSq = (2 * farthestReach) ** 2;
      const nearbyPoints = points.filter((other) => {
        if (other === point) return false;
        const dx = other.x - point.x;
        const dy = other.y - point.y;
        return dx * dx + dy * dy <= ambiguityRangeSq;
      });
      // Include anche il proprio dot tra gli ostacoli: ai raggi piu' piccoli un
      // offset "in verticale" (ancora middle, sopra o sotto il dot) puo' far
      // ricadere il rettangolo dell'etichetta dentro il cerchio del dot stesso,
      // perche' l'altezza del testo supera l'offset. Senza questo controllo la
      // ricerca sceglieva quella posizione (nessun altro vincolo la escludeva),
      // risultando in etichette letteralmente sovrapposte al proprio dot.
      const nearDots = dotObstacles.filter((o) => rectsOverlap(o, reach));
      const nearLabels = placedLabelRects.filter((o) => rectsOverlap(o, reach));
      const obstacles = nearDots.concat(nearLabels);
      let chosen = null;

      // Si prova prima l'etichetta intera su una riga sola: solo se per lei
      // non esiste NESSUNA posizione pulita si passa alle forme spezzate
      // (vedi labelLineVariants). Un'etichetta che oggi trova posto resta
      // quindi esattamente dov'e'.
      for (const variant of variants) {
        for (const offset of candidates) {
          const rect = labelRect(point, offset, variant.overlapWidth, variant.height, variant.descender);
          const boundsRect = labelRect(point, offset, variant.boundsWidth, variant.height, variant.descender);
          if (!isWithinBounds(boundsRect, bounds) || obstacles.some((o) => rectsOverlap(o, rect))) continue;
          if (ambiguityInfo(point, rect, nearbyPoints).ambiguous) continue;
          chosen = { offset, rect, variant };
          break;
        }
        if (chosen) break;
      }

      if (!chosen) {
        // Ultima spiaggia: nessun candidato soddisfa tutti i vincoli. Si
        // sceglie il candidato che minimizza l'AREA di sovrapposizione, non
        // solo quante cose tocca — altrimenti "sfiorare appena un angolo" e
        // "impilarsi del tutto su un'altra etichetta, illeggibile" contano
        // come lo stesso identico punteggio, e la ricerca puo' finire per
        // scegliere la seconda. La sovrapposizione con un DOT pesa piu' di
        // quella con un'altra etichetta: un cerchio pieno sopra parte del
        // testo (le etichette sono disegnate sopra i dot nell'SVG) e' un
        // difetto visivo peggiore di due etichette che si sfiorano, quindi a
        // parita' di area va preferita la seconda. L'ambiguita' resta un
        // fattore secondario (mai preferita a un'area di sovrapposizione
        // molto piu' piccola), e il raggio un'ultima discriminante per
        // restare il piu' vicino possibile al proprio dot.
        //
        // Il ripiego vale per TUTTE le forme dell'etichetta (una riga sola e
        // le eventuali forme spezzate, vedi labelLineVariants), non solo per
        // la prima: se nessuna trova una posizione pulita, e' qui che si
        // decide quale delle due pessime situazioni e' la meno peggio, e una
        // forma piu' stretta puo' ridurre di molto l'area di sovrapposizione.
        // Ogni riga in piu' costa una piccola penalita' fissa
        // (LABEL_WRAP_FALLBACK_PENALTY): serve solo a far vincere la forma su
        // una riga sola quando i due ripieghi sono sostanzialmente pari,
        // senza mai pesare quanto un difetto reale.
        let bestOffset = candidates[0];
        let bestVariant = variants[0];
        let bestScore = Infinity;
        variants.forEach((variant) => {
          const wrapPenalty = (variant.lines.length - 1) * LABEL_WRAP_FALLBACK_PENALTY;
          candidates.forEach((offset) => {
            const rect = labelRect(point, offset, variant.overlapWidth, variant.height, variant.descender);
            const boundsRect = labelRect(point, offset, variant.boundsWidth, variant.height, variant.descender);
            let dotOverlapArea = 0;
            for (let k = 0; k < nearDots.length; k++) dotOverlapArea += rectOverlapArea(nearDots[k], rect);
            let labelOverlapArea = 0;
            for (let k = 0; k < nearLabels.length; k++) labelOverlapArea += rectOverlapArea(nearLabels[k], rect);
            // offset.deviation e' lo stesso scostamento gia' calcolato una
            // volta per candidato in buildLabelCandidates.
            const directionPenalty = offset.deviation === undefined ? 0 : offset.deviation * 20;
            // Tutti i termini del punteggio tranne l'ambiguita' sono gia' noti
            // qui, e l'ambiguita' puo' solo AGGIUNGERE penalita': se questo
            // parziale non e' gia' migliore del migliore trovato finora, il
            // candidato e' perso comunque e non vale la pena cercargli il dot
            // estraneo piu' vicino (il controllo piu' costoso della funzione,
            // una distanza per ogni dot vicino). La scelta finale resta
            // identica: si salta solo lavoro che non poteva cambiarla.
            const partialScore =
              dotOverlapArea * 4 +
              labelOverlapArea +
              offset.radius * 0.1 +
              directionPenalty +
              wrapPenalty +
              (isWithinBounds(boundsRect, bounds) ? 0 : 50000);
            if (partialScore >= bestScore) return;
            const amb = ambiguityInfo(point, rect, nearbyPoints);
            // Tra due candidati entrambi ambigui, non sono equivalenti: quanto piu'
            // il dot proprio e' distante rispetto al dot estraneo piu' vicino
            // (ownDist - otherDist), tanto piu' l'etichetta sembra appartenere
            // all'altro dot. Una penalita' fissa non lo distingue e, a parita' di
            // area di sovrapposizione, la ricerca sceglie il raggio piu' piccolo
            // (discriminante finale sotto) anche quando e' il piu' ambiguo dei due.
            const ambiguityPenalty = amb.ambiguous ? 500 + Math.max(0, amb.ownDist - amb.otherDist) * 5 : 0;
            // (lo scostamento dalla direzione radiale preferita — solo per i
            // membri di un cluster esploso, vedi labelCandidatesFor — pesa
            // poco: serve a rompere la parita' tra due ripieghi altrimenti
            // equivalenti a favore di quello che tiene il ventaglio ordinato.)
            //
            // I termini si risommano qui nell'ordine originale invece di
            // aggiungere l'ambiguita' al parziale: la somma in virgola mobile
            // non e' associativa, quindi due ordini diversi possono dare
            // risultati che differiscono nell'ultima cifra, e fra due
            // candidati quasi pari basta quello per farne vincere un altro.
            // Il parziale resta solo il filtro che evita il calcolo
            // dell'ambiguita' sui candidati gia' perdenti.
            const score =
              dotOverlapArea * 4 +
              labelOverlapArea +
              ambiguityPenalty +
              offset.radius * 0.1 +
              directionPenalty +
              wrapPenalty +
              (isWithinBounds(boundsRect, bounds) ? 0 : 50000);
            if (score < bestScore) {
              bestScore = score;
              bestOffset = offset;
              bestVariant = variant;
            }
          });
        });
        chosen = {
          offset: bestOffset,
          variant: bestVariant,
          rect: labelRect(point, bestOffset, bestVariant.overlapWidth, bestVariant.height, bestVariant.descender),
        };
      }

      placedLabelRects.push(chosen.rect);
      // Gli oggetti candidato sono CONDIVISI tra i punti (vedi
      // LABEL_CANDIDATE_CACHE): una forma spezzata ne produce quindi una
      // copia con le proprie righe invece di scriverci dentro. Su una riga
      // sola si continua a riusare l'oggetto in cache, esattamente come
      // prima.
      if (chosen.variant.lines.length > 1) {
        point.labelOffset = { ...chosen.offset, lines: chosen.variant.lines };
        // La larghezza dell'etichetta non e' piu' quella del testo intero su
        // una riga: layoutConflictArea (che misura a posteriori quanto e'
        // "sporco" un layout) legge point.width, e con la larghezza della
        // riga singola giudicherebbe sovrapposta un'etichetta che a schermo
        // non lo e'.
        point.width = chosen.variant.boundsWidth;
      } else {
        point.labelOffset = chosen.offset;
      }
    });
    return points;
  }

  // Stesso breakpoint "mobile" usato in tutto il tema Dawn per la nav e il
  // resto del layout — non ne esiste uno gia' definito in questa repo (che
  // contiene solo la feature Atlante, vedi README), quindi si ripropone
  // il valore standard invece di inventarne uno nuovo solo per la mappa.
  const MOBILE_BREAKPOINT_QUERY = '(max-width: 749px)';
  // Sotto questa soglia il pannello e' un bottom sheet (con maniglia e due
  // misure); sopra e' il drawer laterale a tutta altezza. E' la stessa
  // soglia della media query in component-manifatture.css, e NON coincide
  // con MOBILE_BREAKPOINT_QUERY (che riguarda solo il raggruppamento dei
  // dot): tra 750 e 989px la mappa e' gia' "desktop" ma la scheda e'
  // ancora un foglio dal basso.
  const SHEET_LAYOUT_QUERY = '(max-width: 989px)';
  // Le due misure di CSS (--manifatture-sheet-peek/full), in frazione
  // dell'altezza della viewport: servono qui per decidere su quale delle
  // due far scattare il foglio a fine trascinamento. Vanno tenute
  // allineate ai valori del CSS.
  const SHEET_PEEK_RATIO = 0.62;
  const SHEET_FULL_RATIO = 0.92;
  // Sotto questa frazione della misura "peek" il trascinamento verso il
  // basso non e' piu' un "rimpicciolisci" ma un "via dallo schermo": e' lo
  // stesso gesto con cui si chiude un foglio in qualsiasi app.
  const SHEET_DISMISS_RATIO = 0.55;
  // Quanto contenuto deve scorrere prima che il foglio si alzi da solo:
  // pochi pixel, quel tanto che basta a distinguere uno scroll vero da un
  // tocco che scivola. Alzarlo al primo pixel faceva "saltare" il foglio
  // anche solo appoggiando il dito.
  const SHEET_AUTO_EXPAND_SCROLL = 16;

  function isMobileViewport() {
    return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  }

  // Il pannello e' un foglio dal basso (e non il drawer laterale)?
  function isSheetLayout() {
    return typeof window.matchMedia === 'function' && window.matchMedia(SHEET_LAYOUT_QUERY).matches;
  }

  // L'altezza di riferimento del foglio: quella della viewport VISIBILE,
  // la stessa che il CSS intende con dvh. window.innerHeight su mobile
  // include l'area sotto le barre del browser, quindi le misure calcolate
  // qui risulterebbero piu' grandi di quelle rese davvero a schermo e lo
  // scatto di fine trascinamento cadrebbe sulla misura sbagliata.
  function viewportHeight() {
    return (window.visualViewport && window.visualViewport.height) || window.innerHeight;
  }

  // Soglia di fusione per clusterByProximity, usata SOLO su mobile ed
  // espressa nelle unita' di viewBox della regione di RIFERIMENTO
  // ("Nord-est", vedi MOBILE_LABEL_FONT_REFERENCE_WIDTH): il valore reale
  // usato da ogni card e' questo moltiplicato per il proprio fontScale
  // (mobileClusterDistance()), esattamente come gia' accade per font-size,
  // raggio dei dot, raggio di ricerca delle etichette e ventaglio di
  // esplosione. Motivo: due dot vanno fusi quando sono troppo vicini SULLO
  // SCHERMO, non quando lo sono in unita' di viewBox — e ogni card rende un
  // viewBox di larghezza diversa alla stessa larghezza reale, quindi una
  // soglia fissa in unita' di viewBox vale una distanza fisica diversa in
  // ogni card. Con la vecchia soglia fissa (25 unita', ereditata da quando
  // il mobile mostrava un'unica mappa nazionale da 960 unita' di larghezza)
  // le card piu' strette fondevano dot che a schermo restano perfettamente
  // distinti e toccabili separatamente: nel "Nord-est" (266 unita' di
  // larghezza) 25 unita' sono ~3,6 volte la distanza fisica che le stesse 25
  // unita' valevano sulla mappa nazionale — bastavano due dot a 35px reali
  // l'uno dall'altro per sparire dentro un badge numerico muto (la card
  // mostrava 4 dot, di cui 2 badge, per 8 citta' diverse).
  //
  // Il valore base e' calibrato misurando sul dataset reale, in PIXEL a
  // schermo (390px di larghezza, il caso stretto tipico), tutte le coppie di
  // dot di ogni card, e verificando il rendering vero card per card
  // (Playwright: etichette fuori dal bordo, etichette sovrapposte tra loro o
  // sopra un dot, distanza minima tra i centri dei dot). 13, 14 e 15 danno
  // tutti lo stesso identico risultato — un plateau, quindi 14 e' scelto al
  // centro, con margine da entrambi i lati:
  //  - sotto (12) la card "Nord-est" separa anche New York e Bay Shore, che
  //    a schermo distano 16,9px con dot da 17px di diametro: si toccano, e
  //    l'etichetta "The Game Headwear" finisce 5px fuori dal bordo della
  //    card e sopra un dot;
  //  - sopra (16) la soglia inizia a fondere una coppia in piu' nella card
  //    "Sud", cioe' cambia proprio il layout gia' verificato dove il
  //    clustering serve davvero.
  // A 14 nessuna card ha etichette fuori bordo o sovrapposte, e nel
  // "Nord-est" i dot rimasti distano almeno 27,2px l'uno dall'altro: non
  // hanno nemmeno bisogno di essere scostati da separateOverlappingDots.
  //
  // Le soglie effettive che ne risultano (unita' di viewBox della card):
  // Nord-est 14,0 (19,7px) · Ovest 21,8 · Midwest 22,7 · Sud 29,5 · Altre
  // zone 50,5. Restano fusi solo i dot che a schermo si toccherebbero
  // davvero: Salem-Dover (14,9px), New York-Bay Shore (16,9px),
  // Minneapolis-Owatonna (17,3px), Anaheim-Carlsbad (15,9px) e il pugno
  // tessile NC nella card "Sud" (tutte le coppie sotto i 10px). Si separano
  // invece L.L. Bean (26,9px dal cluster Salem/Dover) e Starter/New Haven
  // (19,7px da Bay Shore), che prima sparivano dentro un badge "5" muto.
  const MOBILE_CLUSTER_BASE_DISTANCE = 14;

  // Soglia effettiva, in unita' di viewBox della card, per il fontScale dato
  // (vedi buildRegionCanvas): stessa distanza fisica a schermo in ogni card.
  function mobileClusterDistance(fontScale) {
    return MOBILE_CLUSTER_BASE_DISTANCE * (fontScale || 1);
  }

  // ---------------------------------------------------------------------
  // Esplosione di un cluster nelle card regionali mobile
  // ---------------------------------------------------------------------
  // Distanza minima (in unita' di viewBox, da scalare per il fontScale della
  // card) tra due dot esplosi, e tra un dot esploso e un dot NON coinvolto
  // nell'esplosione: somma dei raggi dei due dot piu' grandi possibili
  // (cluster 8 + cluster 8) piu' un margine, cosi' due dot non si
  // sovrappongono mai visivamente ne' diventano un unico bersaglio di tap.
  const EXPLODE_MIN_SEPARATION = 20;

  // Quanto puo' essere piu' stretto, rispetto a una distribuzione uniforme,
  // il divario angolare tra due membri esplosi adiacenti (vedi
  // explodePositions). 1 significherebbe "sempre distribuzione uniforme",
  // cioe' buttare via le direzioni geografiche vere; piu' e' basso, piu' le
  // direzioni vere sopravvivono ma piu' cresce il raggio del ventaglio.
  const EXPLODE_MIN_GAP_RATIO = 0.7;

  // Peso (in unita' di viewBox al quadrato, scalate col fontScale della card)
  // di un'etichetta che finisce piu' vicina a un dot altrui che al proprio, e
  // distanza oltre la quale un'etichetta comincia a essere considerata
  // "staccata" dal proprio dot. Servono a layoutConflictArea per giudicare un
  // ventaglio anche quando le sue etichette non si sovrappongono a nulla ma
  // non si capisce piu' a quale punto appartengano.
  const AMBIGUOUS_LABEL_PENALTY = 400;
  const LABEL_COMFORT_DISTANCE = 26;

  // Quanta sovrapposizione (area in unita' di viewBox al quadrato, scalata
  // col fontScale della card) possono causare le etichette rivelate da
  // un'esplosione prima che l'esplosione venga considerata controproducente
  // e sostituita dal popover con l'elenco (vedi toggleRegionCluster). Non
  // zero: un'esplosione utile puo' comunque far sfiorare due etichette di
  // pochissimo, e rinunciarci per questo sarebbe peggio del difetto.
  // Calibrato sul dataset reale — vedi i valori misurati in README.
  const EXPLODE_CONFLICT_TOLERANCE = 30;

  // Peso di un dot che, aprendo il ventaglio, finirebbe fuori dalla sagoma
  // degli stati disegnati nella card (in mare, o in uno stato che appartiene
  // a un'altra regione e quindi qui non e' disegnato). E' il difetto piu'
  // grave possibile per questa mappa — un dot in mezzo al nulla non dice
  // niente e sembra un errore — quindi vale piu' della tolleranza sulle
  // etichette: una variante che sposta un dot fuori dalla mappa perde SEMPRE
  // contro una che lo tiene dentro, e se nessuna variante ci riesce il tap
  // apre il popover invece del ventaglio.
  const EXPLODE_OFF_LAND_PENALTY = 60;

  // Colloca i membri di un cluster esploso attorno al proprio centroide.
  //
  // Versione precedente (ereditata dalla mappa nazionale): un cerchio
  // "spiderfy" a raggio fisso e grande (32 * fontScale * 1.6, cioe' ~51
  // unita' nel "Nord-est"), con i membri distribuiti a intervalli angolari
  // uguali a partire da una rotazione scelta solo per evitare gli altri dot.
  // L'angolo di ciascun membro non aveva quindi alcun rapporto con dove quel
  // brand si trova davvero, e il raggio era molto piu' grande del necessario:
  // sul dataset reale i membri finivano sistematicamente lontanissimi dal
  // proprio posto e spesso fuori dalla sagoma degli stati — "The Game
  // Headwear" (Bay Shore, Long Island) atterrava in mezzo alla Pennsylvania,
  // "Starter" (New Haven) nell'Atlantico, "Coed Sportswear" e "L.L. Bean"
  // in mare al largo del Maine, e lo stesso accadeva a Hanes/Wrangler/Sports
  // Image nella card "Sud" e a No Fear nella card "Ovest" (9 dot fuori dalla
  // terraferma in totale, misurati con isPointInFill sui path degli stati).
  //
  // Questa versione parte invece dalla GEOGRAFIA VERA e la deforma il meno
  // possibile:
  //  1. ogni membro conserva la propria direzione reale rispetto al
  //     centroide (il brand a nord del gruppo resta a nord);
  //  2. le direzioni troppo vicine tra loro vengono allargate fino a un
  //     divario angolare minimo, mantenendo pero' l'ordine circolare vero
  //     (spreadAngles, un rilassamento che spinge via solo le coppie in
  //     conflitto invece di ridistribuire tutto a intervalli uguali);
  //  3. il raggio non e' un valore fisso ma il minimo che garantisce la
  //     separazione richiesta a quel divario angolare — sep / (2 sin(g/2)),
  //     la formula della corda — e ogni membro puo' restare piu' lontano se
  //     la sua distanza vera dal centroide e' gia' maggiore (cosi' un membro
  //     gia' distante non viene "risucchiato" verso il centro);
  //  4. i membri che cadrebbero fuori dal ritaglio o fuori dalla sagoma
  //     degli stati vengono ricollocati cercando, lungo la propria
  //     direzione, la posizione valida piu' vicina possibile.
  //
  // `opts.isOnLand(x, y)` e' il test di "terraferma" (isPointInFill sui path
  // degli stati clonati nella card, vedi buildRegionCanvas): e' opzionale —
  // la card "Altre zone" non ha sagome proprie, quindi li' il vincolo
  // semplicemente non si applica.
  function spreadAngles(entries, minGap) {
    const n = entries.length;
    const TAU = Math.PI * 2;
    if (n < 2) return entries;
    const sorted = entries.slice().sort((a, b) => a.angle - b.angle);
    // Se il divario minimo richiesto non ci sta nel giro completo, l'unica
    // soluzione e' la distribuzione a intervalli uguali: si tiene comunque
    // l'ordine circolare vero e la direzione media del gruppo, cosi' anche
    // nel caso peggiore la disposizione resta coerente con la geografia.
    if (minGap * n >= TAU) {
      const mx = sorted.reduce((sum, e) => sum + Math.cos(e.angle), 0) / n;
      const my = sorted.reduce((sum, e) => sum + Math.sin(e.angle), 0) / n;
      const mean = Math.atan2(my, mx);
      sorted.forEach((entry, k) => {
        entry.angle = mean + (k - (n - 1) / 2) * (TAU / n);
      });
      return sorted;
    }
    for (let pass = 0; pass < 60; pass++) {
      let moved = false;
      for (let k = 0; k < n; k++) {
        const a = sorted[k];
        const b = sorted[(k + 1) % n];
        let gap = b.angle - a.angle;
        if (k === n - 1) gap += TAU;
        if (gap < minGap - 1e-6) {
          const push = (minGap - gap) / 2;
          a.angle -= push;
          b.angle += push;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return sorted;
  }

  function explodePositions(members, centerX, centerY, opts) {
    const n = members.length;
    // Traslazione del centro del ventaglio: normalmente zero (il ventaglio si
    // apre attorno al centroide vero del cluster). Serve per i gruppi
    // addossati a un confine della card, dove la direzione vera di un membro
    // punta fuori dalla mappa disegnata e nessuna rotazione basta: spostare
    // TUTTO il ventaglio di poco mantiene le posizioni relative — e quindi la
    // geografia interna del gruppo — mentre lo riporta dentro. Il chiamante
    // la prova solo se serve davvero (vedi toggleRegionCluster).
    const cx = centerX + (opts.centerShift ? opts.centerShift.dx : 0);
    const cy = centerY + (opts.centerShift ? opts.centerShift.dy : 0);
    const sep = opts.separation;
    const clearance = opts.clearance !== undefined ? opts.clearance : sep;
    const b = opts.bounds || DEFAULT_BOUNDS;
    const inset = opts.inset || 0;
    const obstacles = opts.obstacles || [];
    // Rettangoli delle etichette gia' presenti nella card (dei punti NON
    // coinvolti nell'esplosione): un dot esploso che ci finisce sopra copre
    // il testo di qualcun altro — le etichette sono disegnate sopra i dot,
    // ma un cerchio pieno a meta' di una parola resta un difetto. Evitarli
    // qui, quando si sceglie DOVE aprire il ventaglio, costa molto meno che
    // rincorrere il problema dopo, spostando le etichette.
    const labelObstacles = opts.labelObstacles || [];
    const dotRadius = opts.dotRadius || 0;
    // Peso dell'evitare le etichette altrui: 1 = le evita, 0 = le ignora.
    // Non e' una manopola da tarare a mano — il chiamante genera entrambe le
    // varianti e sceglie quella che, MISURATA sul layout finale, produce meno
    // sovrapposizioni (vedi toggleRegionCluster): su una tasca densa evitare
    // le etichette aiuta, su un gruppo isolato al contrario allontana i dot
    // da dove le loro stesse etichette avrebbero spazio.
    const labelWeight = opts.labelWeight === undefined ? 1 : opts.labelWeight;
    const radiusScales = opts.radiusScales || [1, 1.2, 1.45];
    const isOnLand = opts.isOnLand;
    const TAU = Math.PI * 2;

    const inCrop = (x, y) => x >= b.x1 + inset && x <= b.x2 - inset && y >= b.y1 + inset && y <= b.y2 - inset;
    const onLand = (x, y) => (isOnLand ? isOnLand(x, y) : true);

    // Direzione e distanza vere di ogni membro rispetto al centroide. Un
    // membro esattamente sul centroide (puo' succedere con due sole citta'
    // quasi coincidenti) non ha una direzione propria: gli si assegna la sua
    // fetta della distribuzione uniforme, cosi' non resta sotto al gruppo e
    // non finisce comunque addosso a un altro membro.
    const entries = members.map((m, i) => {
      const dx = m.x - cx;
      const dy = m.y - cy;
      const dist = Math.hypot(dx, dy);
      return { i, angle: dist > 0.01 ? Math.atan2(dy, dx) : (TAU * i) / n, trueDist: dist, trueX: m.x, trueY: m.y };
    });

    // Divario angolare minimo tra due membri adiacenti. Deve essere PIU'
    // STRETTO di quello di una distribuzione uniforme (TAU / n): con il
    // divario uniforme, infatti, l'unica soluzione possibile e' proprio la
    // distribuzione uniforme — le direzioni vere verrebbero scartate in
    // blocco e resterebbe solo il loro ordine circolare. Lasciandone una
    // frazione (EXPLODE_MIN_GAP_RATIO) le direzioni vere sopravvivono dove
    // non sono in conflitto, e vengono allargate solo dove lo sono: si paga
    // con un raggio un po' piu' grande, perche' due membri piu' vicini in
    // angolo hanno bisogno di piu' distanza dal centro per restare separati
    // (baseRadius e' sempre il minimo che garantisce `sep` a quel divario:
    // sep / (2 sin(gap/2)), la formula della corda).
    const minGap = (TAU / n) * EXPLODE_MIN_GAP_RATIO;
    const spread = spreadAngles(entries, minGap);
    const baseRadius = sep / (2 * Math.sin(Math.min(minGap / 2, Math.PI / 2)));

    const place = (rotation, radiusScale) => {
      const placed = [];
      spread.forEach((entry) => {
        const angle = entry.angle + rotation;
        // Raggio proprio del membro: mai meno di quello che garantisce la
        // separazione, ma se il brand era gia' piu' lontano dal centroide
        // resta alla sua distanza vera invece di essere avvicinato.
        const wanted = Math.max(baseRadius * radiusScale, Math.min(entry.trueDist, baseRadius * 2));
        let best = null;
        // Ricerca a raggi/angoli crescenti attorno alla posizione ideale:
        // stessa strategia "prova piu' candidati, scegli il migliore" gia'
        // usata da placeLabels. Il primo candidato pienamente valido vince,
        // altrimenti si tiene quello col punteggio piu' basso.
        for (let rStep = 0; rStep <= 10; rStep++) {
          const r = wanted * (1 + rStep * 0.12);
          for (const deltaDeg of [0, -7, 7, -14, 14, -21, 21, -28, 28]) {
            const a = angle + (deltaDeg * Math.PI) / 180;
            const x = cx + Math.cos(a) * r;
            const y = cy + Math.sin(a) * r;
            let penalty = 0;
            if (!inCrop(x, y)) penalty += 400;
            placed.forEach((p) => {
              const d = Math.hypot(p.x - x, p.y - y);
              if (d < sep) penalty += (sep - d) ** 2;
            });
            obstacles.forEach((o) => {
              const d = Math.hypot(o.x - x, o.y - y);
              if (d < clearance) penalty += (clearance - d) ** 2;
            });
            if (labelWeight) labelObstacles.forEach((rect) => {
              const overlap = rectOverlapArea(rect, {
                x1: x - dotRadius,
                y1: y - dotRadius,
                x2: x + dotRadius,
                y2: y + dotRadius,
              });
              if (overlap > 0) penalty += overlap * labelWeight;
            });
            // Il test di terraferma e' l'unico controllo costoso (attraversa
            // la geometria dei path degli stati): si fa per ultimo e solo se
            // il candidato e' ancora in gioco, cioe' se i controlli economici
            // non lo hanno gia' portato sopra al migliore trovato finora.
            // Puo' solo peggiorare il punteggio, quindi saltarlo su un
            // candidato gia' perdente non cambia mai la scelta — e fa
            // scendere il tap sul cluster piu' denso da ~230 ms a poche
            // decine.
            if (best && penalty >= best.score) continue;
            if (!onLand(x, y)) penalty += 200;

            // A parita' di tutto il resto vince la posizione piu' vicina a
            // dove il brand si trova davvero.
            const drift = Math.hypot(x - entry.trueX, y - entry.trueY);
            const score = penalty + drift * 0.05;
            if (!best || score < best.score) best = { x, y, score, penalty };
            // I candidati sono generati dal raggio piu' piccolo al piu'
            // grande e, a parita' di raggio, dallo scostamento angolare
            // minore al maggiore: il primo candidato senza alcuna penalita'
            // e' quindi gia' il piu' vicino alla posizione ideale, non serve
            // esaminare i restanti (e' cio' che tiene il tap sotto i ~30 ms
            // anche provando piu' ventagli alternativi).
            if (penalty === 0) break;
          }
          if (best && best.penalty === 0) break;
        }
        placed.push({ i: entry.i, x: best.x, y: best.y, score: best.score });
      });
      return placed;
    };

    // Una rotazione globale puo' salvare piu' membri in una volta sola (es.
    // un gruppo costiero, dove meta' delle direzioni punta verso il mare):
    // si provano poche rotazioni piccole, per non tradire troppo le
    // direzioni vere, e vince quella col punteggio totale piu' basso.
    let best = null;
    for (const rotDeg of [0, -10, 10, -20, 20, -30, 30]) {
      for (const radiusScale of radiusScales) {
        const candidate = place((rotDeg * Math.PI) / 180, radiusScale);
        const total = candidate.reduce((sum, p) => sum + p.score, 0);
        if (!best || total < best.total) best = { total, candidate };
        if (best.total === 0) break;
      }
      if (best.total === 0) break;
    }

    const out = new Array(n);
    best.candidate.forEach((p) => {
      out[p.i] = { x: p.x, y: p.y };
    });
    return out;
  }

  // Divisione in zone geografiche della mappa USA, SOLO per mobile (vedi
  // ManifattureSection.renderMap): invece di comprimere tutti i 46 brand in
  // un'unica mappa stretta, ogni zona diventa una propria card con titolo e
  // una mini-mappa ritagliata sulla sua sola area — molti meno dot nello
  // stesso spazio orizzontale, quindi testo e bersagli di tap naturalmente
  // piu' grandi senza dover deformare nulla (vedi la mappa "stirata"
  // verticalmente, tentata e poi scartata in una versione precedente).
  // Suddivisione in 4 regioni Census Bureau (Nord-est/Midwest/Sud/Ovest),
  // scelta perche' standard e riconoscibile invece di inventarne una nuova.
  // `bounds` e' il rettangolo di ritaglio (in unita' di viewBox, stesso
  // sistema di coordinate della mappa nazionale: nessuna riproiezione,
  // solo una "finestra" diversa sullo stesso canvas) calcolato UNA VOLTA
  // offline dalla vera bounding box di ogni stato della regione (dai path
  // gia' presenti in sections/manifatture.liquid), con un margine di 30
  // unita' per lato (poi troncato ai bordi 0/960/0/600 del canvas
  // nazionale, se il margine ce li farebbe superare) — non con getBBox() a
  // runtime sui path del DOM, perche' quei path vivono dentro
  // .manifatture-map__wrapper che su mobile viene nascosto (hidden, quindi
  // display:none): un elemento non renderizzato non ha una bounding box
  // misurabile via getBBox(). Il margine e' piu' ampio di quello usato per
  // la mappa nazionale (PROJECTION.padding, 20 unita') perche' un dot vicino
  // al bordo del ritaglio regionale ha molto meno spazio per la propria
  // etichetta che sulla mappa intera — verificato empiricamente: con 20
  // unita' l'etichetta di "Huntington" (Midwest) usciva leggermente dal
  // ritaglio quando l'unica posizione libera da sovrapposizioni era verso
  // il bordo piu' vicino.
  const USA_REGIONS = [
    {
      key: 'northeast',
      title: 'Nord-est',
      bounds: { x1: 694, y1: 33, x2: 960, y2: 283 },
      states: [
        'Connecticut', 'Maine', 'Massachusetts', 'New Hampshire', 'New Jersey',
        'New York', 'Pennsylvania', 'Rhode Island', 'Vermont',
      ],
    },
    {
      key: 'midwest',
      // Finestra spostata di 18 unita' verso destra rispetto al margine
      // simmetrico di 30 (sarebbe 322..754): stessa LARGHEZZA, quindi stesso
      // fontScale e stessa dimensione della mappa, ma il margine passa da 30
      // a 12 unita' a sinistra — dove nessuna etichetta lo usava — e da 30 a
      // 48 a destra, dove invece serviva. I due dot piu' a est della card
      // (Carhartt a Dearborn e Harley-Davidson a Milwaukee) hanno nomi
      // lunghi: con il margine simmetrico l'unico posto dove il testo
      // entrava era sopra al dot, lontano (Carhartt 14,8 unita' sopra,
      // Harley-Davidson 46,6). Con la finestra spostata entrambe stanno
      // accanto al proprio dot, a destra (14,7 e 11,6).
      //
      // Poi il bordo destro e' tornato stretto, e stavolta spostare la
      // finestra non bastava: a sinistra non c'e' piu' margine da cedere (12
      // unita', e sotto ci sono le sagome di Kansas e Dakota). I due brand
      // dell'Ohio — Cincinnati e Columbus, a 31 unita' l'uno dall'altro — si
      // contendono lo stesso angolo sud-est della card, e Columbus e' il dot
      // piu' a est di tutti: con il bordo a 772 gli restavano 87 unita' a
      // destra per un'etichetta che ne occupa 100, quindi l'unica direzione
      // libera era proprio quella vietata, e l'etichetta finiva sotto al dot,
      // addosso a "Velva Sheen" e piu' vicina al dot di quella che al
      // proprio (segnalato a schermo: non si capiva quale nome appartenesse a
      // quale punto). Il bordo e' quindi ESTESO a destra, il che allarga il
      // viewBox e quindi anche il font (fontScale = larghezza / 266): il
      // testo cresce con la finestra, ma il dot no, quindi il margine utile
      // cresce comunque. Serve
      //   (432 + D) * (9/266 + 100/432) <= 87 + D  ->  D >= ~37
      // (9 = il raggio del primo anello di ricerca, 100 = la larghezza
      // misurata di "Huntington", 432 = la larghezza precedente). D = 40, con
      // un po' di margine: verificato che Huntington e Velva Sheen stanno ora
      // ciascuna accanto al proprio dot, senza sovrapposizioni e senza
      // ambiguita'. La verifica e' stata fatta prima su un banco di prova con
      // un dataset RICOSTRUITO (il Gist non e' raggiungibile dagli ambienti
      // di Claude Code sul web, vedi README) e poi confermata a schermo sul
      // sito in produzione, sui dati veri. Il costo e' una striscia vuota in
      // piu' a destra dell'Ohio e una mappa ~8% piu' piccola nella card: il
      // testo resta della stessa dimensione fisica, perche' e' esattamente
      // cio' che fontScale compensa.
      title: 'Midwest',
      bounds: { x1: 340, y1: 0, x2: 812, y2: 350 },
      states: [
        'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Michigan', 'Minnesota',
        'Missouri', 'Nebraska', 'North Dakota', 'Ohio', 'South Dakota', 'Wisconsin',
      ],
    },
    {
      key: 'south',
      title: 'Sud',
      bounds: { x1: 281, y1: 186, x2: 841, y2: 594 },
      states: [
        'Alabama', 'Arkansas', 'Delaware', 'Florida', 'Georgia', 'Kentucky',
        'Louisiana', 'Maryland', 'Mississippi', 'North Carolina', 'Oklahoma',
        'South Carolina', 'Tennessee', 'Texas', 'Virginia', 'West Virginia',
      ],
    },
    {
      key: 'west',
      title: 'Ovest',
      bounds: { x1: 0, y1: 0, x2: 414, y2: 455 },
      states: [
        'Arizona', 'California', 'Colorado', 'Idaho', 'Montana', 'Nevada',
        'New Mexico', 'Oregon', 'Utah', 'Washington', 'Wyoming',
      ],
    },
    // Paracadute per uno stato non riconducibile a nessuna delle 4 regioni
    // sopra (es. sigla mancante/errata nel gist): mostra comunque il brand,
    // sull'intero viewBox nazionale (DEFAULT_BOUNDS) invece di un ritaglio
    // regionale — non ha sagome di stato proprie (states: []) perche' non
    // rappresenta un'area geografica specifica, ma non nasconde mai un
    // brand solo perche' i suoi dati sono incompleti.
    {
      key: 'other',
      title: 'Altre zone',
      bounds: DEFAULT_BOUNDS,
      states: [],
    },
  ];

  // Font-size di base (in unita' di viewBox), gli stessi valori impostati
  // in component-manifatture.css per .manifatture-map__label/.manifatture-
  // map__dot-badge: servono come riferimento per calcolare quanto ingrandire
  // il font di ogni card regionale (vedi MOBILE_LABEL_FONT_REFERENCE_WIDTH).
  const BASE_LABEL_FONT_SIZE = 11;
  const BASE_BADGE_FONT_SIZE = 9;

  // Ogni card regionale mobile rende il proprio <svg viewBox> alla STESSA
  // larghezza reale (il contenitore e' sempre largo quanto lo schermo), ma
  // le regioni hanno viewBox di larghezza diversa (la regione "Sud" copre
  // molta piu' area geografica del "Nord-est") — quindi la stessa unita' di
  // viewBox occupa MENO pixel reali in una regione piu' larga, e un
  // font-size fisso (11px, quello della mappa nazionale) risultava
  // visibilmente piu' piccolo in "Sud" che in "Nord-est": le etichette non
  // erano leggibili in modo uniforme tra una card e l'altra. La regione
  // "Nord-est" (la piu' stretta) resta il riferimento a scala 1 — la sua
  // resa a schermo era gia' quella piu' leggibile — e serve a calcolare
  // per ogni card un font-size proporzionalmente piu' grande (vedi
  // buildRegionCanvas) che compensi la larghezza viewBox maggiore,
  // rendendo tutte le etichette della stessa dimensione fisica reale.
  const MOBILE_LABEL_FONT_REFERENCE_WIDTH = (() => {
    const reference = USA_REGIONS.find((region) => region.key === 'northeast');
    return reference.bounds.x2 - reference.bounds.x1;
  })();

  // Sigla USPS dello stato (brand.state nel gist) -> chiave regione: serve a
  // smistare ogni gruppo gia' fuso da clusterUsaBrands (stesso stato per
  // tutti i brand di un gruppo, dato che si fonde solo per citta') nella sua
  // card regionale.
  const STATE_ABBR_TO_REGION = {
    CT: 'northeast', ME: 'northeast', MA: 'northeast', NH: 'northeast', NJ: 'northeast',
    NY: 'northeast', PA: 'northeast', RI: 'northeast', VT: 'northeast',
    IL: 'midwest', IN: 'midwest', IA: 'midwest', KS: 'midwest', MI: 'midwest',
    MN: 'midwest', MO: 'midwest', NE: 'midwest', ND: 'midwest', OH: 'midwest',
    SD: 'midwest', WI: 'midwest',
    AL: 'south', AR: 'south', DE: 'south', DC: 'south', FL: 'south', GA: 'south', KY: 'south',
    LA: 'south', MD: 'south', MS: 'south', NC: 'south', OK: 'south', SC: 'south',
    TN: 'south', TX: 'south', VA: 'south', WV: 'south',
    AK: 'west', AZ: 'west', CA: 'west', CO: 'west', HI: 'west', ID: 'west', MT: 'west', NV: 'west',
    NM: 'west', OR: 'west', UT: 'west', WA: 'west', WY: 'west',
  };

  // Smista i gruppi gia' fusi per citta' (clusterUsaBrands) nelle rispettive
  // regioni, restituendo solo le regioni che hanno almeno un gruppo (una
  // regione senza brand non deve mostrare una card vuota), nell'ordine
  // fisso di USA_REGIONS. Un gruppo il cui stato non e' riconosciuto finisce
  // nel paracadute "other" invece di sparire dalla vista mobile.
  function partitionGroupsByRegion(groups) {
    const byKey = new Map();
    groups.forEach((group) => {
      const stateAbbr = ((group.brands[0] && group.brands[0].state) || '').trim().toUpperCase();
      const regionKey = STATE_ABBR_TO_REGION[stateAbbr] || 'other';
      if (!byKey.has(regionKey)) byKey.set(regionKey, []);
      byKey.get(regionKey).push(group);
    });
    return USA_REGIONS.filter((region) => byKey.has(region.key)).map((region) => ({
      ...region,
      groups: byKey.get(region.key),
    }));
  }

  const REGION_ORDER = ['europe', 'asia', 'other'];

  // Il campo `country` del Gist e' testo libero (vedi README, "Campi di ogni
  // vendor nel Gist"): nessuno impedisce che due brand della stessa nazione
  // arrivino scritti in due modi diversi. E' successo davvero — Umbro con
  // "Inghilterra" e Barbour con "Regno Unito" — e sono finiti in due blocchi
  // separati dell'ordinamento per nazione, come se fossero due paesi.
  //
  // Il caso piu' insidioso non e' la traduzione, ma le nazioni costitutive del
  // Regno Unito: Inghilterra, Scozia, Galles e Irlanda del Nord sono nomi
  // legittimi di un luogo, ma NON sono lo stato, e per un brand britannico
  // capita di scrivere l'una o l'altro senza pensarci. Qui vengono ricondotti
  // tutti a "Regno Unito".
  //
  // Le chiavi sono in minuscolo perche' il confronto avviene su
  // `toLowerCase()`: cosi' "REGNO UNITO" e "regno unito" cadono nella stessa
  // voce senza doverle elencare due volte. Un nome che non e' in tabella viene
  // lasciato passare com'e' (solo ripulito dagli spazi): una nazione nuova
  // davvero — il primo brand portoghese — deve poter comparire senza che
  // qualcuno debba prima aggiornare questo file. La tabella serve a fondere le
  // varianti note, non a fare da lista chiusa dei paesi ammessi.
  //
  // Anche il tipo Map non e' un dettaglio: su un oggetto letterale la chiave
  // "constructor" (o "toString") risponderebbe con un membro ereditato da
  // Object.prototype invece che con undefined.
  //
  // Se qui si aggiunge una variante, va aggiunta anche in
  // supabase/functions/vendor-promote/index.ts (COUNTRY_ALIASES): quella la
  // normalizza in scrittura, questa la assorbe in lettura per i dati gia' nel
  // Gist.
  const COUNTRY_ALIASES = new Map([
    ['inghilterra', 'Regno Unito'],
    ['england', 'Regno Unito'],
    ['scozia', 'Regno Unito'],
    ['scotland', 'Regno Unito'],
    ['galles', 'Regno Unito'],
    ['wales', 'Regno Unito'],
    ['irlanda del nord', 'Regno Unito'],
    ['northern ireland', 'Regno Unito'],
    ['gran bretagna', 'Regno Unito'],
    ['great britain', 'Regno Unito'],
    ['united kingdom', 'Regno Unito'],
    ['uk', 'Regno Unito'],
    ['u.k.', 'Regno Unito'],
    ['japan', 'Giappone'],
    ['france', 'Francia'],
    ['germany', 'Germania'],
    ['italy', 'Italia'],
    ['spain', 'Spagna'],
    ['portugal', 'Portogallo'],
    ['ireland', 'Irlanda'],
    ['netherlands', 'Paesi Bassi'],
    ['holland', 'Paesi Bassi'],
    ['olanda', 'Paesi Bassi'],
    ['belgium', 'Belgio'],
    ['switzerland', 'Svizzera'],
    ['sweden', 'Svezia'],
    ['norway', 'Norvegia'],
    ['denmark', 'Danimarca'],
    ['finland', 'Finlandia'],
    ['austria', 'Austria'],
    ['poland', 'Polonia'],
    ['czech republic', 'Repubblica Ceca'],
    ['czechia', 'Repubblica Ceca'],
    ['south korea', 'Corea del Sud'],
    ['korea', 'Corea del Sud'],
    ['china', 'Cina'],
    ['taiwan', 'Taiwan'],
    ['canada', 'Canada'],
    ['australia', 'Australia'],
    ['new zealand', 'Nuova Zelanda'],
  ]);

  // Nome inglese delle nazioni canoniche, per la versione inglese del sito:
  // il Gist scrive `country` in italiano (e' la lingua in cui e' curato), ma
  // una card che dice "Giappone" in mezzo a una scheda in inglese e' lo stesso
  // mezzo passo indietro delle descrizioni non tradotte. Le chiavi sono le
  // forme canoniche prodotte da COUNTRY_ALIASES, non le varianti: la
  // normalizzazione avviene prima. Come li', un nome che non e' in tabella
  // passa invariato invece di sparire, cosi' il primo brand di una nazione
  // nuova compare comunque.
  const COUNTRY_EN = new Map([
    ['Regno Unito', 'United Kingdom'],
    ['Giappone', 'Japan'],
    ['Francia', 'France'],
    ['Germania', 'Germany'],
    ['Italia', 'Italy'],
    ['Spagna', 'Spain'],
    ['Portogallo', 'Portugal'],
    ['Irlanda', 'Ireland'],
    ['Paesi Bassi', 'Netherlands'],
    ['Belgio', 'Belgium'],
    ['Svizzera', 'Switzerland'],
    ['Svezia', 'Sweden'],
    ['Norvegia', 'Norway'],
    ['Danimarca', 'Denmark'],
    ['Finlandia', 'Finland'],
    ['Polonia', 'Poland'],
    ['Repubblica Ceca', 'Czech Republic'],
    ['Corea del Sud', 'South Korea'],
    ['Cina', 'China'],
    ['Nuova Zelanda', 'New Zealand'],
    ['Brasile', 'Brazil'],
    ['Stati Uniti', 'United States'],
  ]);

  // Nome della nazione da mostrare e su cui ordinare. Vale per ogni superficie
  // che legge brand.country (card internazionali e scheda di dettaglio): se
  // l'ordinamento fondesse le varianti ma la card continuasse a stampare
  // quella scritta nel Gist, due card vicine dello stesso paese mostrerebbero
  // due nomi diversi, e l'incoerenza sarebbe solo spostata, non risolta.
  function countryName(value) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed) return '';
    const canonical = COUNTRY_ALIASES.get(trimmed.toLowerCase()) || trimmed;
    if (LANG === 'en') return COUNTRY_EN.get(canonical) || canonical;
    return canonical;
  }

  // --- Notifica del carrello sopra il pannello ---------------------------
  //
  // Il carrello di questo negozio e' una notifica (settings_data.json ->
  // cart_type), e la notifica vive DENTRO la sezione header, che e'
  // position: sticky con un proprio z-index. Quella sezione e' quindi un
  // contesto di impilamento, e nessun z-index applicato alla notifica puo'
  // farla uscire: per quanto alto, resta ordinato dentro il contesto del
  // padre, che sta sotto al pannello (z-index 100). E' il motivo per cui
  // alzare <cart-notification> non e' servito a niente — si aggiungeva al
  // carrello e non compariva nulla, e il click successivo la congedava
  // (cart-notification.js -> handleBodyClick chiude a ogni click fuori)
  // senza che fosse mai stata vista.
  //
  // L'unico punto in cui si puo' intervenire e' l'ANTENATO che crea il
  // contesto. Si risalgono quindi i genitori della notifica alzando quelli
  // che la ingabbiano, e si rimette tutto com'era alla chiusura. Si
  // risale invece di puntare un selettore preciso perche' la struttura
  // dell'header e' del tema, non nostra: se cambia, questo continua a
  // funzionare senza inseguirla.
  const ELEVATED_Z_ATTR = 'data-manifatture-elevated-z';
  const ELEVATED_POS_ATTR = 'data-manifatture-elevated-position';

  // --- Quanto in alto deve stare il pannello ------------------------------
  //
  // Sopra i widget flottanti di terze parti: badge recensioni, promo, chat.
  // Sono iniettati a livello di body con z-index alti e arbitrari, e un
  // numero fisso scelto oggi sarebbe una rincorsa persa in partenza — basta
  // che domani un'app ne aggiunga uno piu' alto e il pannello ci finisce
  // sotto di nuovo. Si misura invece cosa c'e' davvero in pagina, a ogni
  // apertura.
  //
  // Si guardano i figli del body e i loro figli diretti: i widget stanno
  // li', e scandire l'intero documento costerebbe getComputedStyle su
  // migliaia di nodi a ogni apertura per ritrovare le stesse cose.
  const PANEL_Z_BASE = 100; // il valore del CSS: non si scende mai sotto
  const PANEL_Z_MARGIN = 10;
  // Limite noto: un widget inchiodato a questa quota o oltre pareggerebbe
  // con il pannello, e a parita' di z-index vince chi viene dopo nel DOM —
  // cioe' il widget, iniettato in fondo al body. Non e' un caso reale (i
  // widget delle app stanno tipicamente fra 9.999 e 999.999), ma se un
  // giorno succedesse il sintomo sarebbe di nuovo il badge sopra la scheda,
  // e la causa e' scritta qui invece che da scoprire.
  const PANEL_Z_MAX = 2147483000;

  function highestFloatingZIndex(exclude) {
    let max = 0;

    // Si guarda TUTTO il documento, non i primi due livelli sotto body.
    // Un widget di terze parti annida spesso il proprio z-index in
    // profondita' (contenitore dell'app, wrapper, iframe), e fermarsi in
    // superficie significa non vederlo affatto: il pannello si alzava di
    // poco e restava sotto.
    //
    // Il costo e' un getComputedStyle per nodo, misurato: ~1,7ms su 3.000
    // nodi, ~5ms su 9.000, ~12ms su 18.000 (su CPU da desktop; su un
    // telefono di fascia media va moltiplicato per qualche volta). Una
    // pagina di questo negozio sta nella fascia bassa, e la scansione
    // avviene all'apertura di una scheda — mentre il pannello sta gia'
    // entrando in animazione — non durante il caricamento della pagina.
    document.querySelectorAll('body *').forEach((el) => {
      if (exclude && (el === exclude || exclude.contains(el))) return;
      // Gli elementi che abbiamo alzato NOI per la notifica del carrello
      // non contano: misurarli farebbe salire il pannello a ogni apertura,
      // che a sua volta farebbe salire la notifica, all'infinito.
      if (el.hasAttribute(ELEVATED_Z_ATTR)) return;

      const style = window.getComputedStyle(el);
      if (style.position === 'static') return;
      const z = parseInt(style.zIndex, 10);
      if (!Number.isNaN(z) && z > max) max = z;
    });

    return max;
  }

  function trapsStacking(el) {
    const style = window.getComputedStyle(el);
    if (style.position === 'sticky' || style.position === 'fixed') return true;
    if (style.position !== 'static' && style.zIndex !== 'auto') return true;
    if (style.transform !== 'none' || style.filter !== 'none') return true;
    if (style.isolation === 'isolate') return true;
    const opacity = parseFloat(style.opacity);
    return !isNaN(opacity) && opacity < 1;
  }

  function elevateAncestors(el, zIndex) {
    let node = el && el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      if (trapsStacking(node) && !node.hasAttribute(ELEVATED_Z_ATTR)) {
        node.setAttribute(ELEVATED_Z_ATTR, node.style.zIndex);
        // Un contesto creato da transform/filter puo' essere su un elemento
        // static, dove z-index non ha effetto: serve renderlo posizionato.
        if (window.getComputedStyle(node).position === 'static') {
          node.setAttribute(ELEVATED_POS_ATTR, node.style.position);
          node.style.position = 'relative';
        }
        node.style.zIndex = String(zIndex);
      }
      node = node.parentElement;
    }
  }

  function restoreElevatedAncestors() {
    document.querySelectorAll(`[${ELEVATED_Z_ATTR}]`).forEach((node) => {
      node.style.zIndex = node.getAttribute(ELEVATED_Z_ATTR);
      node.removeAttribute(ELEVATED_Z_ATTR);
      if (node.hasAttribute(ELEVATED_POS_ATTR)) {
        node.style.position = node.getAttribute(ELEVATED_POS_ATTR);
        node.removeAttribute(ELEVATED_POS_ATTR);
      }
    });
  }

  // --- Prodotti in arrivo: data e via al flusso "avvisami" ----------------
  //
  // Sulla card di un prodotto non ancora rilasciato il tema mostra un
  // pulsante DISABILITATO "In arrivo" (card-product.liquid): sulla pagina
  // collezione ha senso, perche' il prodotto e' li' a un tap e la pagina
  // prodotto porta data di rilascio e form "avvisami". Nel pannello della
  // mappa quel pulsante spento comunica invece un vicolo cieco, e il
  // flusso di iscrizione — che esiste, e' progettato e funziona — resta
  // irraggiungibile.
  //
  // Qui il pulsante diventa un LINK alla pagina prodotto, con sopra la data
  // di rilascio. Il form non viene duplicato nel pannello di proposito: e'
  // un form customer a invio nativo, e hCaptcha si aggancia ai form
  // presenti al caricamento della pagina — uno iniettato via AJAX rischia
  // di far fallire l'iscrizione in modo invisibile, che e' esattamente il
  // bug gia' capitato in produzione (vedi il commento in testa a
  // drop-gate.js).
  //
  // Il link resta DENTRO [data-drop-gate-placeholder], cosi' e' sempre
  // drop-gate.js a decidere se mostrarlo: appena il drop e' rilasciato (o
  // sbloccato dal cookie di accesso anticipato) sparisce e compare il vero
  // pulsante d'acquisto, senza che questo codice ne sappia nulla.
  //
  // Nessuna ancora #notify-form nel link: resetScrollForOwnHash in
  // drop-gate.js riporta deliberatamente a inizio pagina quando l'hash
  // punta dentro il blocco "in arrivo" (a quel punto ancora nascosto),
  // quindi l'ancora verrebbe annullata.
  function enhanceComingSoonCards(root) {
    const grid = root.querySelector('.manifatture-products-grid');
    if (!grid) return;

    const notifyLabel = grid.dataset.notifyLabel;
    const availableFrom = grid.dataset.availableFrom;
    if (!notifyLabel) return;

    root.querySelectorAll('[data-manifatture-product]').forEach((item) => {
      const dropLabel = item.dataset.dropLabel;
      if (!dropLabel) return;

      const placeholder = item.querySelector('[data-drop-gate-placeholder]');
      const titleLink = item.querySelector('.card__heading a[href]');
      const button = placeholder && placeholder.querySelector('button');
      if (!placeholder || !titleLink || !button) return;

      const link = document.createElement('a');
      // Stesse classi del pulsante che sostituisce: l'aspetto e' quello del
      // tema, non una copia da tenere allineata a mano.
      link.className = button.className;
      link.href = titleLink.getAttribute('href');
      link.textContent = notifyLabel;
      button.replaceWith(link);

      const date = document.createElement('p');
      date.className = 'manifatture-products-grid__drop-date';
      date.textContent = availableFrom ? `${availableFrom} ${dropLabel}` : dropLabel;
      placeholder.insertBefore(date, placeholder.firstChild);
    });
  }

  // --- Filtri della griglia prodotti ------------------------------------
  //
  // Le opzioni le rende il Liquid (vede tutti i prodotti del vendor); qui
  // si applica la selezione e si calcolano i conteggi. I conteggi non sono
  // scritti nel markup di proposito: quello di un'opzione dipende da cosa
  // e' selezionato negli ALTRI gruppi, quindi un numero fisso mentirebbe
  // appena si tocca il secondo filtro. Vengono ricalcolati a ogni
  // cambiamento, e un'opzione che non porterebbe a nulla viene disabilitata
  // invece di lasciarti premere a vuoto.
  //
  // Tutto avviene sulle card gia' in pagina: nessuna nuova richiesta, quindi
  // il filtro e' istantaneo.
  function initProductFilters(root) {
    const bar = root.querySelector('[data-manifatture-filters]');
    const items = Array.from(root.querySelectorAll('[data-manifatture-product]'));
    if (!bar || !items.length) return;

    const emptyEl = root.querySelector('[data-manifatture-filters-empty]');
    const groups = Array.from(bar.querySelectorAll('[data-filter-group]'));

    const selection = () => {
      const state = {};
      groups.forEach((group) => {
        const active = group.querySelector('[aria-pressed="true"]');
        state[group.dataset.filterGroup] = active ? active.dataset.filterValue : '';
      });
      return state;
    };

    const matches = (item, state) =>
      Object.keys(state).every((name) => !state[name] || item.dataset[name] === state[name]);

    const apply = () => {
      const state = selection();
      let visible = 0;

      items.forEach((item) => {
        const show = matches(item, state);
        item.hidden = !show;
        if (show) visible += 1;
      });

      if (emptyEl) emptyEl.hidden = visible > 0;

      groups.forEach((group) => {
        const name = group.dataset.filterGroup;
        group.querySelectorAll('[data-filter-value]').forEach((chip) => {
          const candidate = Object.assign({}, state, { [name]: chip.dataset.filterValue });
          const count = items.filter((item) => matches(item, candidate)).length;

          const countEl = chip.querySelector('[data-filter-count]');
          if (countEl) countEl.textContent = count;
          // L'opzione attiva non si disabilita mai, altrimenti si resterebbe
          // bloccati su una selezione da cui non si puo' piu' uscire.
          chip.disabled = count === 0 && chip.getAttribute('aria-pressed') !== 'true';
        });
      });
    };

    bar.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-filter-value]');
      if (!chip || chip.disabled) return;
      const group = chip.closest('[data-filter-group]');
      if (!group) return;

      group.querySelectorAll('[data-filter-value]').forEach((other) => {
        other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
      });
      apply();
    });

    apply();
  }

  // --- Ricerca e filtri della section ---------------------------------------
  //
  // Una ricerca testuale e quattro tendine (marchio, citta', stato, nazione).
  // Tendine e non chip come nella barra dei prodotti (initProductFilters qui
  // sopra): li' le opzioni sono quattro o cinque, qui sono quasi una per
  // brand — in chip diventavano file lunghe che occupavano piu' spazio della
  // mappa che filtrano. Una tendina resta alta una riga qualunque sia il
  // numero di opzioni, e su mobile apre il selettore nativo del sistema.
  //
  // Cosa succede quando si filtra:
  //
  // - LA MAPPA NON VIENE RIDISEGNATA. I dot esclusi si opacizzano (classe
  //   is-dimmed), non spariscono: la mappa resta la stessa, i dot restano
  //   dove sono e le etichette non saltano da una posizione all'altra a ogni
  //   battuta — un ricalcolo di placeLabels a ogni tasto le avrebbe fatte
  //   ballare, oltre a costare. Un dot opacizzato resta cliccabile: e'
  //   visibile, e una cosa visibile che ignora i click e' peggio di una
  //   scheda aperta fuori dal filtro.
  // - Le card internazionali invece si NASCONDONO. Non sono una mappa: sono
  //   un elenco, dove la posizione di una card non porta informazione, e un
  //   elenco si filtra accorciandolo. Una regione che resta senza card
  //   sparisce con loro, e cosi' l'intera sezione se non ne resta nessuna.

  // Confronto tollerante come quello dei vendor attivi (readActiveVendors):
  // senza accenti, senza maiuscole, senza spazi ai bordi. Serve sia alla
  // ricerca testuale ("citta" trova "Città") sia alle chiavi delle chip, cosi'
  // due entry del Gist che scrivono la stessa citta' in modo diverso non
  // diventano due opzioni distinte.
  function normalizeText(value) {
    return String(value === null || value === undefined ? '' : value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  // I gruppi di chip, nell'ordine in cui compaiono nella barra. `raw` estrae
  // dal brand il valore grezzo del gruppo, `label` (opzionale) lo traduce in
  // testo mostrabile — serve solo alla nazione, che nel Gist e' un codice o
  // un nome in italiano e va letta con countryName come nel resto della
  // section.
  const SECTION_FILTER_GROUPS = [
    { name: 'vendor', raw: (brand) => brand.shopify_vendor || brand.label },
    { name: 'city', raw: (brand) => brand.city },
    { name: 'state', raw: (brand) => brand.state, label: (raw) => stateName(raw) || raw },
    { name: 'country', raw: (brand) => brand.country, label: (raw) => countryName(raw) || raw },
  ];

  // Nome esteso di uno stato USA a partire dalla sigla USPS, che e' come lo
  // stato sta nel Gist (brand.state). Serve in due punti: l'etichetta della
  // chip ("Ohio" si riconosce, "OH" molto meno) e il testo su cui lavora la
  // ricerca — chi cerca scrive "Carolina del Nord" o "North Carolina", non
  // "NC". Le voci sono una stringa sola dove il nome coincide nelle due
  // lingue (la gran parte) e una coppia { it, en } dove non coincide.
  const US_STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington D.C.',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: { it: 'Nuovo Messico', en: 'New Mexico' },
    NY: 'New York', NC: { it: 'Carolina del Nord', en: 'North Carolina' },
    ND: { it: 'Dakota del Nord', en: 'North Dakota' }, OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
    SC: { it: 'Carolina del Sud', en: 'South Carolina' },
    SD: { it: 'Dakota del Sud', en: 'South Dakota' }, TN: 'Tennessee', TX: 'Texas',
    UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: { it: 'Virginia Occidentale', en: 'West Virginia' }, WI: 'Wisconsin', WY: 'Wyoming',
  };

  // Tutte le forme sotto cui uno stato puo' essere cercato: la sigla e il
  // nome esteso in ENTRAMBE le lingue. Le due lingue insieme, e non solo
  // quella della pagina, perche' il nome inglese di uno stato e' comunque la
  // forma piu' diffusa e sarebbe strano che a sito italiano "North Carolina"
  // non trovasse nulla.
  // Indice inverso nome esteso -> sigla, in entrambe le lingue. Serve perche'
  // il campo `state` del Gist e' quasi sempre la sigla USPS ma non e'
  // garantito che lo sia: un'entry compilata a mano puo' portare "Ohio" per
  // esteso, e senza questo indice quello stesso stato diventerebbe una
  // seconda opzione distinta nella tendina, accanto a "OH".
  const US_STATE_BY_NAME = (() => {
    const index = new Map();
    Object.keys(US_STATE_NAMES).forEach((abbr) => {
      const entry = US_STATE_NAMES[abbr];
      const names = typeof entry === 'string' ? [entry] : [entry.it, entry.en];
      names.forEach((name) => index.set(normalizeText(name), abbr));
    });
    return index;
  })();

  // Sigla USPS a partire da come lo stato sta scritto nel Gist: la sigla
  // stessa, o il nome esteso in una delle due lingue. Un valore non
  // riconosciuto torna indietro in maiuscolo, cosi' resta comunque
  // un'opzione (con la sua etichetta grezza) invece di sparire.
  function stateAbbr(value) {
    const raw = String(value || '').trim();
    const upper = raw.toUpperCase();
    if (US_STATE_NAMES[upper]) return upper;
    return US_STATE_BY_NAME.get(normalizeText(raw)) || upper;
  }

  function stateSearchTerms(value) {
    const key = stateAbbr(value);
    const entry = US_STATE_NAMES[key];
    if (!entry) return key ? [key] : [];
    return typeof entry === 'string' ? [key, entry] : [key, entry.it, entry.en];
  }

  function stateName(value) {
    const key = stateAbbr(value);
    const entry = US_STATE_NAMES[key];
    if (!entry) return key;
    return typeof entry === 'string' ? entry : entry[LANG] || entry.it;
  }

  // Etichetta mostrata per un valore grezzo del Gist. E' anche la chiave con
  // cui si raggruppano le opzioni e si confrontano i brand (normalizzata):
  // il Gist ammette per lo stesso posto scritture diverse — "Japan" e
  // "Giappone", "OH" e "Ohio" — che countryName/stateName riportano gia' a
  // un nome solo. Raggruppando per valore grezzo invece che per etichetta,
  // quelle scritture diventavano DUE opzioni con lo stesso identico testo a
  // schermo, ognuna delle quali trovava solo meta' dei brand.
  // Quanto occupa in cima allo schermo l'header sticky del tema, se c'e'.
  // Serve a non mandare mai un dot (o l'elenco di un cluster, che si apre
  // sopra al dot) a finire dietro all'header: dal punto di vista dello
  // scorrimento la viewport comincia sotto di lui, non a zero. Il valore
  // arriva da --header-height, che il tema tiene aggiornata in
  // sections/header.liquid; senza header sticky la variabile non c'e' e la
  // zona sicura resta il bordo dello schermo.
  function stickyHeaderHeight() {
    if (!document.querySelector('sticky-header')) return 0;
    const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    return parseFloat(value) || 0;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Richiama la callback quando la pagina ha smesso di scorrere. Con lo
  // scorrimento morbido la posizione finale arriva diversi fotogrammi dopo
  // la chiamata, e chi deve APRIRE qualcosa in base a dove sta il dot (es.
  // l'elenco di un cluster, che si apre sopra o sotto a seconda dello spazio
  // libero) deve decidere sulla posizione di arrivo, non su quella di
  // partenza. Il tetto di tempo evita di restare in attesa per sempre se lo
  // scorrimento non parte nemmeno.
  function afterScroll(callback) {
    let last = window.scrollY;
    let stable = 0;
    const started = Date.now();
    const tick = () => {
      const now = window.scrollY;
      stable = now === last ? stable + 1 : 0;
      last = now;
      if (stable >= 3 || Date.now() - started > 800) {
        callback();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // Dove atterra il risultato di una ricerca dentro la fascia di schermo
  // libera (vedi scrollTargetIntoView): un terzo dall'alto. Non a meta',
  // perche' quasi sempre il bersaglio ha attorno qualcosa che lo spiega (la
  // mappa attorno a un dot, il titolo della regione sopra a una card) e
  // quel contesto sta sopra di lui.
  const FOCUS_LANDING_RATIO = 0.3;
  // Fin dove, nella stessa fascia, un bersaglio gia' a schermo si considera
  // sistemato bene e non lo si sposta: oltre questa quota si e' comunque
  // troppo in basso per leggerlo come "ecco, e' questo".
  const FOCUS_COMFORT_LIMIT = 0.55;

  // Distanza tra l'elenco di un cluster e il proprio dot. Prima era zero,
  // cioe' l'elenco appoggiato esattamente sul bordo del dot: cliccando un
  // risultato che sta in un cluster, l'alone e l'anello della pulsazione
  // (che si allargano ATTORNO al dot) finivano per meta' dietro all'elenco,
  // e su desktop si vedeva solo l'arco inferiore. Con questo margine il
  // segno resta intero e l'elenco smette anche di sembrare incollato.
  const CLUSTER_POPOVER_GAP = 16;

  // Quante righe di risultati si mostrano prima di ripiegare su "e altre N".
  // Sei perche' l'elenco deve restare una scorciatoia sotto al campo di
  // ricerca, non una seconda lista di tutti i marchi: oltre a questo
  // punto conviene comunque restringere il filtro.
  const RESULT_LIST_LIMIT = 6;

  // Riga di luogo di un brand, la stessa forma usata nella scheda: citta' e
  // stato per gli USA, citta' e nazione per gli altri.
  function brandPlace(brand) {
    const parts =
      brand.tipo === 'brand_usa'
        ? [brand.city, stateName(brand.state)]
        : [brand.city, countryName(brand.country)];
    return parts.filter(Boolean).join(', ');
  }

  function filterLabelFor(group, raw) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return '';
    return (group.label ? group.label(trimmed) : trimmed) || trimmed;
  }

  function brandFilterValue(group, brand) {
    return normalizeText(filterLabelFor(group, group.raw(brand)));
  }

  // Testo su cui lavora la ricerca libera: nome, vendor Shopify, citta', stato
  // (sigla e nome esteso, vedi stateSearchTerms) e nazione — sia come sta nel
  // Gist sia tradotta, cosi' "Japan" a sito inglese e "Giappone" a sito
  // italiano trovano entrambi lo stesso brand. La narrativa e' volutamente
  // esclusa: e' testo lungo, e una parola comune come "cotone" farebbe
  // combaciare mezza mappa.
  // Un termine cercato deve trovarsi all'INIZIO di una parola, non in un
  // punto qualsiasi. Cercando "Port" la sottostringa nuda restituiva mezzo
  // catalogo — "Coed SPORTswear", "Gear for SPORTs", "FreePORT" — con il
  // risultato giusto (Port and Company) sepolto in mezzo: parole che
  // contengono la sequenza cercata senza avere niente a che fare con quello
  // che si sta cercando. Il confine e' "qualunque cosa non sia una lettera o
  // una cifra", cosi' funziona anche dopo punti e trattini ("L.L. Bean"
  // trovato con "bean") sul testo gia' normalizzato da normalizeText.
  function matchesWordStart(haystack, token) {
    if (!token) return true;
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}`).test(haystack);
  }

  function brandSearchText(brand) {
    return normalizeText(
      [
        brand.label,
        brand.shopify_vendor,
        brand.city,
        ...stateSearchTerms(brand.state),
        brand.country,
        countryName(brand.country),
      ]
        .filter(Boolean)
        .join(' ')
    );
  }

  // Opzioni di un gruppo, ricavate dall'intero insieme dei brand visibili (non
  // dal sottoinsieme filtrato): l'elenco delle chip resta lo stesso a ogni
  // selezione, e a cambiare e' solo il contatore. Un elenco che si accorcia
  // sotto le dita mentre si filtra e' molto piu' difficile da usare.
  // Ordinamento alfabetico: in una tendina si cerca a colpo d'occhio un nome
  // che si ha gia' in mente ("Ohio", "Carhartt"), e l'ordine alfabetico e'
  // l'unico in cui si sa dove guardare. Il conteggio per opzione non si
  // mostra (vedi applySectionFilters): serve solo a sapere quali opzioni
  // disabilitare, e quanti marchi resta il filtro lo dice gia' il
  // contatore sotto la barra.
  function filterOptionsFor(group, brands) {
    const options = new Map();
    brands.forEach((brand) => {
      const label = filterLabelFor(group, group.raw(brand));
      if (!label) return;
      // Stessa chiave usata da brandFilterValue: l'opzione e il brand che le
      // corrisponde non possono divergere, perche' passano dalla stessa
      // funzione.
      const value = normalizeText(label);
      if (!options.has(value)) options.set(value, { value, label, count: 0 });
      options.get(value).count += 1;
    });
    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label, LANG));
  }

  // Il marchio chiesto dall'indirizzo: /pages/atlante?brand=velva-sheen.
  //
  // Serve alla targhetta sulla scheda prodotto (vedi
  // snippets/manifattura-targhetta.liquid nel tema), che deve poter portare
  // DIRETTAMENTE alla scheda di un marchio: mandare alla pagina Atlante e
  // basta vorrebbe dire far ricominciare la ricerca da capo fra i marchi,
  // cioe' non mantenere la promessa del link.
  //
  // Il nome del parametro e' in inglese come le chiavi del Gist, che sono
  // quelle che ci finiscono dentro, e resta lo stesso sulle due lingue del
  // sito: un indirizzo copiato da /pages/atlante deve funzionare anche
  // incollato su /en/pages/atlante.
  const DEEP_LINK_PARAM = 'brand';

  const MANIFATTURE_PRODUCTS_SECTION = 'manifatture-products';

  // Rotta di ricerca del sito, presa da routes.search_url (data-search-url):
  // su una lingua secondaria Shopify la serve con il prefisso della lingua
  // ("/en/search"). Con il "/search" fisso di prima, la sezione dei prodotti
  // veniva resa nella lingua PREDEFINITA anche a sito in inglese, e dentro un
  // pannello ormai inglese ricomparivano i filtri in italiano.
  let SEARCH_URL = '/search';

  // La scheda vendor non costruisce piu' le proprie card prodotto: chiede al
  // tema di renderizzare la sezione manifatture-products (che a sua volta
  // rende snippets/card-product.liquid) sulla rotta di ricerca, e ne inserisce
  // l'HTML. Cosi' CTA, badge "Esaurito"/"In saldo" e blocco "In arrivo" del
  // drop-gate sono quelli veri del sito, non una loro imitazione che andrebbe
  // riallineata a mano a ogni modifica del tema.
  function buildVendorSectionUrl(vendor) {
    const safeVendor = vendor.replace(/"/g, '');
    const params = new URLSearchParams();
    params.set('q', `vendor:"${safeVendor}"`);
    params.set('type', 'product');
    params.set('section_id', MANIFATTURE_PRODUCTS_SECTION);
    return `${SEARCH_URL}?${params.toString()}`;
  }

  // --- Precaricamento dei prodotti ---------------------------------------
  //
  // Il render della sezione costa ~500ms lato Shopify e NON viene messo in
  // cache (misurato sul negozio: riaprendo lo stesso vendor il tempo non
  // scende, anzi). Mezzo secondo tra il tocco sul dot e la comparsa dei
  // prodotti e' tutto tempo in cui il pannello mostra "Caricamento".
  // Facendo partire la richiesta quando il dito TOCCA il dot invece che
  // quando lo rilascia, o quando il mouse ci passa sopra, quel tempo scorre
  // mentre l'utente sta ancora completando il gesto.
  //
  // Un solo precaricamento alla volta, e quello precedente viene annullato:
  // sulla mappa i dot sono fitti, e passarci sopra col mouse senza questo
  // limite scatenerebbe una raffica di render da mezzo secondo l'uno — un
  // rimedio peggiore del male.
  //
  // Il risultato viene CONSUMATO una volta sola e non conservato: questo e'
  // un vantaggio di partenza, non una cache dei contenuti. Riaprire lo
  // stesso vendor rifa' la richiesta, cosi' lo stato di disponibilita' resta
  // quello vero.
  // Otto in volo: e' la dimensione del cluster piu' grande della mappa
  // attuale (mobile), quindi aprendolo si copre ogni scelta possibile
  // invece di lasciarne fuori una parte. Il tetto serve comunque, perche'
  // ogni precaricamento e' un render su Shopify e ~20KB scaricati: su un
  // cluster pieno, se ne sprecano fino a sette per la scheda che poi non
  // viene aperta. E' il prezzo accettato per non far mai aspettare la
  // scelta.
  //
  // Se la mappa cresce e nasce un cluster piu' grande, i brand oltre l'
  // ottavo non restano scoperti: ricadono sul precaricamento al tocco del
  // singolo dot, che vale ~100ms invece di mezzo secondo. Vale la pena
  // rivedere questo numero quando succede, non prima.
  const PREFETCH_MAX = 8;

  // Un precaricamento non consumato entro mezzo minuto viene buttato: cosi'
  // resta un vantaggio di partenza e non diventa una cache dei contenuti,
  // che mostrerebbe uno stato di disponibilita' vecchio.
  const PREFETCH_TTL_MS = 30000;

  const prefetches = new Map();

  function prunePrefetches() {
    const now = Date.now();
    prefetches.forEach((entry, vendor) => {
      if (now - entry.startedAt > PREFETCH_TTL_MS) {
        entry.controller.abort();
        prefetches.delete(vendor);
      }
    });
  }

  function cancelPrefetches(keep) {
    prefetches.forEach((entry, vendor) => {
      if (keep && keep.indexOf(vendor) !== -1) return;
      entry.controller.abort();
      prefetches.delete(vendor);
    });
  }

  function startPrefetch(vendor) {
    if (!vendor || prefetches.has(vendor) || prefetches.size >= PREFETCH_MAX) return;

    const controller = new AbortController();
    const promise = fetch(buildVendorSectionUrl(vendor), { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : null))
      // Un precaricamento fallito o annullato non deve far rumore: chi apre
      // davvero la scheda rifa' la richiesta per conto suo.
      .catch(() => null);

    prefetches.set(vendor, { promise, controller, startedAt: Date.now() });
  }

  // replace = true quando il gesto CAMBIA quel che l'utente sta guardando
  // (passa su un altro dot, apre un altro cluster): i precaricamenti di
  // prima non servono piu' e vanno annullati, altrimenti attraversare la
  // mappa col mouse ne lascerebbe dietro una scia. E' false quando il gesto
  // RESTRINGE una scelta gia' in corso — passare su una voce dell'elenco di
  // un cluster — perche' li' gli altri brand dello stesso cluster sono
  // ancora candidati validi.
  function prefetchVendors(vendors, replace) {
    const wanted = [];
    vendors.forEach((vendor) => {
      if (vendor && wanted.indexOf(vendor) === -1) wanted.push(vendor);
    });
    if (!wanted.length) return;

    const capped = wanted.slice(0, PREFETCH_MAX);
    if (replace) cancelPrefetches(capped);
    prunePrefetches();
    capped.forEach(startPrefetch);
  }

  function takePrefetchedProducts(vendor) {
    prunePrefetches();
    const entry = prefetches.get(vendor);
    if (!entry) return null;
    prefetches.delete(vendor);
    return entry.promise;
  }

  async function fetchVendorProductsMarkup(vendor) {
    const prefetched = takePrefetchedProducts(vendor);
    if (prefetched) {
      const markup = await prefetched;
      if (markup) return markup;
    }

    const response = await fetch(buildVendorSectionUrl(vendor));
    if (!response.ok) throw new Error(`Products fetch failed: ${response.status}`);
    return response.text();
  }

  // Un <script> arrivato dentro una stringa HTML non viene MAI eseguito dal
  // browser, ne' via innerHTML ne' importando il nodo con importNode: e' una
  // regola della spec, non una svista. La sezione porta con se' quick-add.js
  // e product-form.js (e, sui prodotti con drop_date, drop-gate.js), quindi
  // senza questo passaggio il pulsante "Aggiungi al carrello" resterebbe
  // inerte su una pagina che non carica gia' quegli script per conto suo.
  // Ricrearli come nodi nuovi e' l'unico modo per farli partire.
  //
  // Un asset gia' presente in pagina viene pero' SCARTATO invece che
  // ricreato: la sezione riporta gli stessi <link> e <script> a ogni
  // apertura di scheda, e senza questo filtro il documento accumulerebbe
  // una copia di ciascuno per ogni vendor aperto, per tutta la sessione.
  // I tre script del tema si autoproteggono dalla riesecuzione
  // (customElements.get(...) in quick-add.js e product-form.js,
  // window.__dropGateInit in drop-gate.js), quindi il duplicato non
  // romperebbe nulla — ma resta spazzatura nel DOM che cresce senza fine.
  // Gli asset gia' attivati in questa pagina, per src/href. Non basta
  // cercarli nel DOM: il pannello svuota il proprio contenuto a ogni
  // apertura, quindi lo <script> inserito per il vendor precedente non
  // esiste piu' e verrebbe ricreato — e rieseguito — a ogni scheda aperta.
  // Innocuo (i tre script del tema si autoproteggono) ma inutile: cosi'
  // ognuno viene eseguito esattamente una volta per caricamento di pagina.
  const activatedAssets = new Set();

  // root e' il frammento appena inserito: i suoi nodi NON contano come
  // "gia' in pagina". card-product.liquid include drop-gate.js una volta
  // per ogni card con drop_date, quindi lo stesso src compare piu' volte
  // nello stesso frammento; considerandoli l'uno prova dell'altro, ogni
  // copia risulterebbe duplicata e verrebbero scartate tutte — con il
  // drop-gate mai eseguito e le card bloccate nello stato fail-closed
  // (niente prezzo, niente pulsante). Contano solo gli script che la
  // pagina aveva gia' per conto suo, piu' quelli gia' attivati da qui.
  function assetAlreadyLoaded(el, root) {
    const src = el.getAttribute('src') || el.getAttribute('href');
    if (!src) return false;
    if (activatedAssets.has(src)) return true;

    // :not([media="print"]) perche' un foglio caricato con lo schema non
    // bloccante del tema (media="print" + onload="this.media='all'", vedi
    // sections/manifatture.liquid e layout/theme.liquid) e' SI' in pagina,
    // ma finche' non ha finito di scaricarsi non si applica a schermo.
    // Contarlo come gia' attivo significherebbe scartare la copia che
    // arriva col frammento e lasciare le card senza stili proprio nel caso
    // in cui il foglio non e' ancora pronto — o non lo sara' mai, se quella
    // richiesta fallisce. media e' un attributo riflesso, quindi appena
    // onload lo porta ad 'all' il link torna a contare normalmente.
    const selector = el.tagName === 'SCRIPT' ? 'script[src]' : 'link[href]:not([media="print"])';
    const attr = el.tagName === 'SCRIPT' ? 'src' : 'href';
    return Array.from(document.querySelectorAll(selector)).some(
      (existing) => existing !== el && !root.contains(existing) && existing.getAttribute(attr) === src
    );
  }

  function markAssetActivated(el) {
    const src = el.getAttribute('src') || el.getAttribute('href');
    if (src) activatedAssets.add(src);
  }

  // I fogli di stile vengono RIPIANTATI in <head>, non lasciati dove sono
  // arrivati. Un <link rel="stylesheet"> applica le sue regole solo finche'
  // sta NEL documento: toglierlo dal DOM equivale a cancellarlo. Il pannello
  // pero' si svuota a ogni apertura di scheda (renderPanelHeader ->
  // panelContent.innerHTML = ''), quindi il link rimasto dentro il frammento
  // moriva insieme alla scheda precedente — e la copia che arrivava con la
  // scheda nuova veniva scartata, perche' quell'href risultava gia'
  // "attivato". Dalla SECONDA scheda in poi le card restavano senza
  // component-card.css: titolo del prodotto ripetuto due volte (la regola
  // che nasconde quello dentro .card__inner sta li') e immagine invisibile
  // (.card .media e' in position: absolute, e senza quel file il
  // contenitore che le da' altezza ne ha zero). Sulla pagina /manifatture
  // il difetto era garantito, perche' li' nessun'altra sezione carica
  // component-card.css per conto proprio.
  //
  // In <head> il foglio resta per tutta la vita della pagina e viene
  // caricato una volta sola. Ci finisce PRIMA di component-manifatture.css,
  // che la section carica nel corpo: a parita' di specificita' vincono gli
  // stili del pannello, che e' la direzione giusta (e in pratica non
  // cambia nulla, le regole del pannello sulle card sono tutte piu'
  // specifiche di quelle del tema).
  function adoptStylesheets(root) {
    root.querySelectorAll('link[href]').forEach((link) => {
      if (!assetAlreadyLoaded(link, root)) {
        markAssetActivated(link);
        const fresh = document.createElement('link');
        for (const attr of link.attributes) fresh.setAttribute(attr.name, attr.value);
        document.head.appendChild(fresh);
      }
      link.remove();
    });
  }

  function activateScripts(root) {
    adoptStylesheets(root);

    root.querySelectorAll('script').forEach((old) => {
      if (old.src && assetAlreadyLoaded(old, root)) {
        old.remove();
        return;
      }
      markAssetActivated(old);

      const fresh = document.createElement('script');
      for (const attr of old.attributes) fresh.setAttribute(attr.name, attr.value);
      if (!old.src) fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  }

  // Misura la larghezza REALE di ogni etichetta con getComputedTextLength(),
  // usando un elemento <text> temporaneo con la stessa classe (stesso
  // font-size/famiglia) invece di stimarla: una formula fissa per carattere
  // non puo' essere insieme sicura per lettere piu' larghe della media e
  // abbastanza precisa da sfruttare spazi realmente disponibili ma stretti
  // (es. vicino al bordo della mappa). +3 di margine per anti-aliasing.
  // `labelsGroup` e' esplicito (non piu' implicito this.labelsGroup) perche'
  // questa funzione e' condivisa dalla mappa nazionale desktop/singola e da
  // ciascuna mini-mappa regionale mobile (vedi USA_REGIONS), che hanno
  // ciascuna il proprio <g> di etichette. `fontSize` opzionale (in unita' di
  // viewBox) sovrascrive il font-size di base SOLO sull'elemento di misura,
  // cosi' la larghezza misurata riflette il font davvero usato per il
  // rendering finale in quella card (vedi buildRegionCanvas) invece del
  // font-size di base della classe CSS condivisa.
  // Misurare il testo nel DOM costringe il browser a un layout sincrono, e
  // lo stesso testo viene misurato piu' volte quando si PROVANO piu' layout
  // alternativi prima di sceglierne uno (vedi toggleRegionCluster). La
  // larghezza di una stringa a un dato font-size non cambia mai tra una
  // prova e l'altra: tenerla da parte fa la differenza tra un tap
  // istantaneo e uno percettibilmente lento (misurato: 2,2 s -> 30 ms sul
  // cluster piu' denso).
  // Larghezze gia' misurate, per l'intera pagina e non piu' per singola card:
  // la chiave tiene dentro il font-size, che e' l'unica cosa che cambia la
  // misura di uno stesso testo tra la mappa nazionale e una card regionale.
  // Prima la cache era una per card (creata in buildRegionCanvas) e la mappa
  // desktop non ne aveva affatto: ogni ridisegno rimisurava tutte le sue
  // etichette da capo.
  const LABEL_WIDTH_CACHE = new Map();

  // Un web font che finisce di caricarsi DOPO la prima misura cambia la
  // larghezza reale del testo: le misure prese con il font di ripiego non
  // valgono piu' e la cache va svuotata, altrimenti resterebbero valide per
  // tutta la vita della pagina (prima non poteva succedere perche' la cache
  // moriva con la card).
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => LABEL_WIDTH_CACHE.clear()).catch(() => {});
  }

  // Testo dell'etichetta di un punto sulla mappa (vedi il commento dentro
  // layoutPoints per il perche' delle tre forme). Sta in una funzione a se'
  // perche' serve anche all'apertura di una zona sul desktop
  // (openDesktopZone), che scrive le etichette dei membri senza passare da
  // placeLabels e deve usare esattamente lo stesso testo.
  function pointLabelText(group, isMobileRegion) {
    if (group.brands.length > 1) {
      // Il qualificatore si traduce: era scritto fisso in italiano, e sul sito
      // in inglese le card mobile mostravano "Zona · 5".
      return group.city ? `${group.city} · ${group.brands.length}` : t('areaLabel')(group.brands.length);
    }
    return mapLabelText(stripParenthetical(group.brands[0].label), isMobileRegion);
  }

  function measureLabelWidths(points, labelsGroup, fontSize) {
    if (!labelsGroup) return;
    const keyFor = (text) => `${fontSize || 0}|${text}`;
    const pending = [];
    const seen = new Set();
    const want = (text) => {
      const key = keyFor(text);
      if (LABEL_WIDTH_CACHE.has(key) || seen.has(key)) return;
      seen.add(key);
      pending.push(text);
    };
    points.forEach((point) => {
      want(point.labelText);
      // Anche le righe delle forme spezzate (vedi labelLineVariants) vanno
      // misurate, e nello stesso giro: e' la loro larghezza REALE che dice a
      // placeLabels se una di quelle forme entra davvero nel ritaglio della
      // card. Misurarle qui insieme a tutte le altre costa un solo layout,
      // esattamente come prima.
      if (point.labelVariants) {
        point.labelVariants.forEach((lines) => lines.forEach(want));
      }
    });

    // Testi per cui getComputedTextLength() ha restituito 0: non finiscono in
    // cache (sarebbe un valore sbagliato reso permanente) ma servono comunque
    // qui sotto, per distinguere "non ancora misurato" da "misurato male".
    const unmeasured = new Map();

    if (pending.length) {
      // Un <text> per ciascun testo da misurare, appesi tutti insieme prima
      // di leggerne uno solo: getComputedTextLength() ha bisogno del layout
      // aggiornato, quindi riusare un unico nodo cambiandogli il testo
      // costringeva il browser a rifare il layout PER OGNI etichetta (46
      // layout sincroni sulla sola mappa nazionale, il singolo costo piu'
      // alto dell'intero disegno). Scrivendo prima tutti i nodi e leggendo
      // dopo tutte le misure, il layout si ricalcola una volta sola.
      const frame = document.createDocumentFragment();
      const nodes = pending.map((text) => {
        const measure = document.createElementNS(SVG_NS, 'text');
        measure.setAttribute('class', 'manifatture-map__label');
        if (fontSize) measure.style.fontSize = `${fontSize}px`;
        measure.style.visibility = 'hidden';
        measure.textContent = text;
        frame.appendChild(measure);
        return measure;
      });
      labelsGroup.appendChild(frame);
      nodes.forEach((measure, index) => {
        const width = measure.getComputedTextLength() + 3;
        // Una larghezza pari al solo margine (getComputedTextLength() = 0,
        // succede se l'<svg> non ha ancora una larghezza risolta al momento
        // della misura) non va messa in cache: sarebbe un valore sbagliato
        // reso permanente, mentre senza cache il rendering successivo lo
        // correggeva da solo.
        if (width > 3) LABEL_WIDTH_CACHE.set(keyFor(pending[index]), width);
        else unmeasured.set(pending[index], width);
      });
      nodes.forEach((measure) => labelsGroup.removeChild(measure));
    }

    const widthOf = (text) => {
      const cached = LABEL_WIDTH_CACHE.get(keyFor(text));
      return cached !== undefined ? cached : unmeasured.get(text);
    };

    points.forEach((point) => {
      const width = widthOf(point.labelText);
      if (width !== undefined) point.width = width;
      if (!point.labelVariants) return;
      // Per una forma spezzata conta la riga PIU' LARGA: e' lei a decidere
      // se il blocco entra nel ritaglio. Se anche una sola riga non e'
      // misurabile, l'intera variante resta senza misura reale e placeLabels
      // ricade sulla stima prudente, come per il testo su una riga sola.
      point.variantWidths = point.labelVariants.map((lines) => {
        let widest = 0;
        for (let i = 0; i < lines.length; i++) {
          const lineWidth = widthOf(lines[i]);
          if (lineWidth === undefined || lineWidth <= 3) return undefined;
          if (lineWidth > widest) widest = lineWidth;
        }
        return widest;
      });
    });
  }

  // Disegna dot/etichette per un array di gruppi dentro un dotsGroup/
  // labelsGroup espliciti, con un bounds esplicito per placeLabels (vedi
  // DEFAULT_BOUNDS) — corpo di ManifattureSection.paintGroups estratto in
  // funzione libera cosi' la mappa nazionale (desktop, un solo canvas) e le
  // mini-mappe regionali mobile (una card per regione, vedi USA_REGIONS)
  // possono condividere la STESSA logica di piazzamento/disegno invece di
  // mantenerne due copie che rischierebbero di divergere nel tempo.
  //
  // burst (opzionale) fa apparire i punti con indice in burst.indices
  // partendo dal centroide fuso (burst.fromX/fromY) invece che gia' nella
  // posizione finale: un doppio requestAnimationFrame applica prima la
  // posizione di partenza, forza un reflow, poi passa a quella vera, cosi'
  // la transizione CSS su cx/cy anima davvero il movimento (senza reflow di
  // mezzo le due modifiche si accorperebbero in un solo frame e non ci
  // sarebbe nulla da animare).
  //
  // regionKey (opzionale) viene scritto su ogni dot come data-region-key,
  // cosi' il click delegato (ManifattureSection.handleDotActivate) sa
  // smistare il tap sulla card regionale giusta invece che sulla mappa
  // nazionale singola.
  //
  // labelFontSize/badgeFontSize (opzionali, in unita' di viewBox) applicano
  // un font-size diverso da quello di base SOLO alle etichette/badge di
  // questo disegno (via inline style, che vince sempre sulla classe CSS
  // condivisa): servono a una card regionale mobile per compensare il
  // proprio viewBox piu' ampio (vedi buildRegionCanvas/
  // MOBILE_LABEL_FONT_REFERENCE_WIDTH) cosi' che il testo risulti della
  // stessa dimensione fisica reale in ogni card, indipendentemente da
  // quante unita' di viewBox copre quella regione. Quando sono assenti
  // (il caso della mappa nazionale desktop, vedi paintGroups) non viene
  // scritto alcuno stile inline: l'output resta identico a prima. Lo stesso
  // rapporto (fontScale, derivato sotto da labelFontSize) ingrandisce anche
  // il raggio dei dot: per lo stesso identico motivo del font, un dot di
  // raggio fisso in unita' di viewBox risultava visibilmente piu' piccolo
  // nelle regioni con viewBox piu' ampio (es. "Sud") che in quelle piu'
  // strette (es. "Nord-est"), pur essendo lo stesso identico dot — vedi
  // dotRadius sotto e dotObstacleRect (che deve conoscere lo stesso
  // ingrandimento per continuare a tenere le etichette lontane dal dot
  // davvero reso a schermo, non da quello di dimensione base).
  // Costruisce i punti (posizione + testo dell'etichetta) e ne calcola il
  // layout completo (misura del testo nel DOM + placeLabels). Separata dal
  // disegno vero e proprio perche' serve anche "a secco", per PROVARE un
  // layout prima di adottarlo: vedi explosionIsReadable/toggleRegionCluster,
  // dove un cluster viene esploso solo se il layout risultante e' leggibile.
  function layoutPoints({ groups, bounds, regionKey, labelFontSize, disableOverrideIndices, labelsGroup, crowdingFrom }) {
    const points = groups.map((group, index) => {
      // Per un dot singolo l'etichetta mostra il nome del brand (identifica
      // meglio il punto sulla mappa); per un cluster mostra citta' + conteggio
      // in forma abbreviata ("Citta' · N", non "Citta' · N marchi"): il
      // testo piu' corto lascia piu' spazio libero intorno nelle zone dense,
      // dove piu' nomi brand comunque non ci starebbero in un'unica etichetta
      // — l'elenco completo resta disponibile nel popover al click sul dot, e
      // la parola "marchi" resta nell'aria-label per l'accessibilita'.
      // Un cluster per vicinanza (solo mobile) puo' unire brand di citta'
      // diverse: group.city e' null in quel caso (vedi clusterByProximity),
      // e non esiste un nome di luogo singolo corretto da mostrare. Prima
      // questi dot restavano "muti" (solo il badge numerico dentro al dot),
      // ma un numero da solo non dice cosa sia: si usa quindi la stessa
      // forma dei cluster "stessa citta'" con un qualificatore generico al
      // posto del nome, "Zona · N". E' anche la forma piu' CORTA possibile,
      // il che conta: e' quella che si piazza piu' vicino al proprio dot
      // (misurato sul rendering reale nella card "Sud", la piu' densa: 17px
      // contro i 41px delle alternative provate allora, "8 manifatture" e
      // "8 vendor" — la misura e' di prima che l'unita' si chiamasse
      // "marchio", ma il confronto che conta e' quello con "Zona · N", che
      // resta il piu' corto di tutti). La forma estesa "N marchi in questa
      // zona" resta nell'aria-label del dot per l'accessibilita'.
      const labelText = pointLabelText(group, !!regionKey);
      // group.preferAngle: presente solo sui membri di un cluster appena
      // esploso (toggleRegionCluster) — la direzione radiale in cui il dot
      // si e' aperto rispetto al centroide, usata da labelCandidatesFor per
      // far uscire anche l'etichetta nella stessa direzione.
      // labelVariants: le forme alternative dell'etichetta (il testo intero
      // su una riga, poi le versioni spezzate su due e tre righe — vedi
      // labelLineVariants). Si preparano SOLO dentro una card regionale
      // mobile (regionKey presente), dove il font ingrandito rende un nome
      // lungo largo quanto mezza card e nessuna posizione pulita esiste piu'.
      // Sulla mappa nazionale desktop restano assenti e placeLabels lavora
      // esattamente come prima. Un'etichetta vuota (i cluster per vicinanza)
      // non ha nulla da spezzare.
      return {
        index,
        x: group.x,
        y: group.y,
        labelText,
        labelVariants:
          regionKey && labelText && labelIsWideForBounds(labelText, bounds, labelFontSize)
            ? labelLineVariants(labelText)
            : undefined,
        group,
        preferAngle: group.preferAngle,
      };
    });

    const fontScale = labelFontSize ? labelFontSize / BASE_LABEL_FONT_SIZE : 1;
    measureLabelWidths(points, labelsGroup, labelFontSize);
    placeLabels(points, bounds, fontScale, disableOverrideIndices, !!regionKey, crowdingFrom);
    return points;
  }

  // Quanto e' "sporco" un layout gia' calcolato da layoutPoints: somma delle
  // aree (in unita' di viewBox) in cui un'etichetta finisce sopra un'altra
  // etichetta o sopra un dot, piu' una penalita' per un'etichetta che esce
  // dal ritaglio. E' la stessa nozione di difetto che placeLabels cerca di
  // minimizzare candidato per candidato — qui misurata sul risultato finale,
  // per poter confrontare due layout alternativi tra loro.
  //
  // `onlyIndices` opzionale: conta solo i conflitti che coinvolgono quei
  // punti. Serve a giudicare un'esplosione per quello che causa DAVVERO —
  // le etichette che rivela — senza sommarci i difetti preesistenti altrove
  // nella card (es. un override che gia' sfiora il proprio dot), che
  // sarebbero identici in entrambe le alternative e falserebbero il
  // confronto.
  function layoutConflictArea(points, bounds, fontScale, onlyIndices) {
    const scale = fontScale || 1;
    const involved = (point) => !onlyIndices || onlyIndices.has(point.index);
    const rects = points
      .filter((point) => point.labelText && point.labelOffset)
      .map((point) => ({
        point,
        rect: labelRect(
          point,
          point.labelOffset,
          // Stesso criterio di placeLabels: una larghezza pari al solo
          // margine e' una misura fallita, non una misura.
          point.width !== undefined && point.width > 3
            ? point.width
            : estimateLabelWidth(point.labelText, scale),
          // L'altezza sopra l'ancora resta quella di UNA riga: il testo
          // parte da li' e le righe successive scendono.
          14 * scale,
          // Un'etichetta spezzata su piu' righe (gli override con `lines`,
          // es. "Delta Pro Weight") occupa in altezza il passo verticale
          // reale tra le righe (1.15em) per ogni riga in piu'. Contarla come
          // una riga sola rendeva questa misura ottimista proprio dove serve
          // essere severi: un ventaglio veniva giudicato accettabile (23 di
          // conflitto) mentre nel DOM le etichette si sovrapponevano per
          // davvero (tre difetti misurati sul rendering). Le righe in piu'
          // vanno pero' contate SOTTO l'ancora, insieme al discendente: la
          // prima versione di questa correzione le sommava all'altezza,
          // cioe' sopra (vedi labelRect: y1 = ancora - height), allungando
          // il rettangolo dalla parte sbagliata — misurava sovrapposizioni
          // in una fascia dove non c'e' testo e non ne misurava nessuna
          // dove il testo c'e' davvero.
          labelDescenderFor(point.labelOffset.lines ? point.labelOffset.lines.length : 1, scale)
        ),
      }));
    const dots = points.map((point) => ({ point, rect: dotObstacleRect(point, scale) }));
    let area = 0;
    rects.forEach((a) => {
      if (!involved(a.point)) return;
      // Un'etichetta puo' non sovrapporsi a nulla ed essere comunque
      // inservibile: se finisce piu' vicina al dot di qualcun altro che al
      // proprio (ambiguityInfo, la stessa nozione che placeLabels cerca di
      // evitare candidato per candidato) il lettore non sa a che punto si
      // riferisca. E' esattamente il difetto che rende illeggibile un
      // ventaglio troppo denso: verificato sul cluster NC, dove le etichette
      // si sovrapponevano pochissimo (144px² nel caso peggiore) ma
      // "Sports Image", "Tee Jays" e "Hanes" galleggiavano lontano dal
      // proprio dot, sopra stati che non c'entravano. Senza questo termine
      // la misura giudicava accettabile un ventaglio palesemente sbagliato.
      const amb = ambiguityInfo(a.point, a.rect, points);
      if (amb.ambiguous) area += AMBIGUOUS_LABEL_PENALTY * scale * scale;
      // Anche senza ambiguita' vera e propria, un'etichetta molto lontana dal
      // proprio dot e' un legame debole: pesa in proporzione a quanto supera
      // la distanza confortevole (il raggio massimo della ricerca normale).
      const slack = amb.ownDist - LABEL_COMFORT_DISTANCE * scale;
      if (slack > 0) area += slack * slack;
    });
    rects.forEach((a, i) => {
      for (let j = i + 1; j < rects.length; j++) {
        const b = rects[j];
        if (!involved(a.point) && !involved(b.point)) continue;
        area += rectOverlapArea(a.rect, b.rect);
      }
      dots.forEach((dot) => {
        if (!involved(a.point) && !involved(dot.point)) return;
        area += rectOverlapArea(a.rect, dot.rect);
      });
      if (involved(a.point) && !isWithinBounds(a.rect, bounds)) area += 500 * scale * scale;
    });
    return area;
  }

  // ---------------------------------------------------------------------
  // Zone sulla mappa desktop
  // ---------------------------------------------------------------------
  // Sul desktop i punti restano tutti separati, e sono le etichette a doversi
  // fare spazio (placeLabels, piu' gli override fissati a mano). Funziona
  // finche' attorno a un punto c'e' posto: nel distretto tessile fra North
  // Carolina e Virginia non c'era piu'. Con l'arrivo di Mayo Spruce (Mayodan,
  // a meta' strada fra Martinsville e Greensboro) nessuna combinazione di
  // spostamenti delle etichette dava un risultato leggibile: provate 84
  // varianti di Wrangler/Stedman/Mayo Spruce, e anche la migliore lasciava il
  // dot di Mayodan fra quelli di Martinsville e Wrangler, con l'etichetta una
  // riga sotto, accanto al dot sbagliato.
  //
  // Una zona e' la stessa idea dei cluster per vicinanza del mobile
  // (clusterByProximity, "Zona · N" nelle card regionali): un solo dot con il
  // numero dei marchi, che al clic si apre e mostra ogni punto col suo nome.
  // A differenza del mobile, pero', sul desktop una zona nasce SOLO dove serve:
  // un gruppo di punti vicini diventa zona se le sue etichette, misurate sul
  // layout vero (layoutConflictArea), non trovano posto. Dove trovano posto la
  // mappa resta esattamente com'era, byte per byte — e quando un marchio
  // nuovo affolla una zona che prima reggeva, la zona nasce da sola.

  // Distanza (unita' di viewBox della mappa nazionale) entro cui due punti
  // fanno parte dello stesso gruppo: la somma dei raggi di due dot
  // (6 + 8, singolo e cluster) piu' un'unita' per il bordo bianco. Sotto
  // questa distanza i due cerchi a schermo si toccano. Il gruppo si costruisce
  // per concatenamento (A vicino a B, B vicino a C), non per centroide come
  // clusterByProximity: col centroide il distretto tessile si spezzava in
  // modo casuale — Stedman e Sports Image restavano fuori, a ridosso del dot
  // della zona, a seconda dell'ordine dei dati.
  const DESKTOP_ZONE_DISTANCE = 15;

  // Quanto devono stare male le etichette di un gruppo perche' diventi zona:
  // l'equivalente di due etichette illeggibili (AMBIGUOUS_LABEL_PENALTY e'
  // il peso di una sola). NON e' la tolleranza del ventaglio mobile
  // (EXPLODE_CONFLICT_TOLERANCE, 30): la misura dipende dalla larghezza vera
  // del testo, e se il primo disegno avviene prima che il font del tema sia
  // caricato le larghezze sono quelle del font di ripiego. Misurato sul
  // dataset vero: New York e Salem passano da 13 e 7 a 123 e 120 senza il
  // font, e con 30 diventavano zone a seconda di quanto in fretta arrivava
  // il font; il distretto tessile sta fra 1980 e 2219 senza Mayo Spruce, e
  // fra 3376 e 3753 con. 800 sta lontano da entrambi.
  const DESKTOP_ZONE_MIN_CONFLICT = 2 * AMBIGUOUS_LABEL_PENALTY;

  // Passo verticale fra i membri di una zona aperta: dot singolo (6) piu'
  // dot cluster (8) di raggio, piu' un'unita' perche' i bordi bianchi non si
  // tocchino; l'altezza di una riga di testo (11px di font) ci sta con
  // margine. A 14 il dot di Martinsville e quello di Mayo Spruce si
  // sfioravano.
  const ZONE_ROW_STEP = 15;
  // Quanto si allarga in orizzontale la posizione vera di ogni membro
  // rispetto al centro della zona, e fino a dove: il membro a ovest resta a
  // ovest, ma senza allontanarsi tanto da uscire dall'alone.
  const ZONE_GEO_SPREAD = 1.6;
  const ZONE_MAX_SHIFT = 26;
  // Distanza fra il dot di un membro e l'inizio della sua etichetta, e
  // margine dell'alone tratteggiato attorno al membro piu' lontano.
  const ZONE_LABEL_GAP = 9;
  const ZONE_HALO_PADDING = 10;

  // Raggruppa in zone i punti che ne hanno bisogno. `points` e' il layout di
  // `groups` gia' calcolato da layoutPoints (stessi indici): e' su quello che
  // si misura se le etichette di un gruppo trovano posto. Restituisce
  // `groups` stesso, senza copie, quando non nasce nessuna zona — il
  // chiamante lo usa per riusare il layout appena calcolato.
  function desktopZoneGroups(groups, points, bounds) {
    const n = groups.length;
    const parent = groups.map((_, i) => i);
    const find = (i) => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const limitSq = DESKTOP_ZONE_DISTANCE * DESKTOP_ZONE_DISTANCE;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = groups[i].x - groups[j].x;
        const dy = groups[i].y - groups[j].y;
        if (dx * dx + dy * dy <= limitSq) parent[find(j)] = find(i);
      }
    }

    const components = new Map();
    groups.forEach((_, i) => {
      const root = find(i);
      if (!components.has(root)) components.set(root, []);
      components.get(root).push(i);
    });

    const zoneOf = new Map();
    components.forEach((indices) => {
      if (indices.length < 2) return;
      // Stessa misura con cui il mobile decide se un ventaglio e' leggibile
      // (layoutConflictArea, vedi toggleRegionCluster), con la soglia di
      // DESKTOP_ZONE_MIN_CONFLICT: sotto, le etichette del gruppo si
      // arrangiano, e il gruppo resta com'e'.
      if (layoutConflictArea(points, bounds, 1, new Set(indices)) <= DESKTOP_ZONE_MIN_CONFLICT) return;
      const members = indices.map((i) => groups[i]);
      const total = members.reduce((sum, g) => sum + g.brands.length, 0);
      const zone = {
        x: members.reduce((sum, g) => sum + g.x * g.brands.length, 0) / total,
        y: members.reduce((sum, g) => sum + g.y * g.brands.length, 0) / total,
        // Nessun nome di luogo: l'etichetta diventa "Zona · N", come sul
        // mobile (vedi pointLabelText).
        city: null,
        brands: members.reduce((all, g) => all.concat(g.brands), []),
        multiCity: true,
        sourceGroups: members,
      };
      indices.forEach((i) => zoneOf.set(i, zone));
    });
    if (!zoneOf.size) return groups;

    // La zona prende il posto del suo primo membro: l'ordine degli altri
    // punti resta quello di sempre.
    const out = [];
    const placed = new Set();
    groups.forEach((group, i) => {
      const zone = zoneOf.get(i);
      if (!zone) out.push(group);
      else if (!placed.has(zone)) {
        placed.add(zone);
        out.push(zone);
      }
    });
    return out;
  }

  function rectHitsRect(a, b) {
    return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
  }

  // Dove vanno i membri di una zona aperta. Non e' il ventaglio del mobile
  // (explodePositions): li' le etichette seguono ciascuna la direzione del
  // proprio dot, e nel distretto tessile finivano a sinistra sopra Healthknit
  // e Delta Pro Weight. Qui i membri si dispongono in righe, da nord a sud
  // nell'ordine vero, ciascuno spostato a est o a ovest quanto lo e' davvero
  // (allargato di ZONE_GEO_SPREAD); le etichette stanno TUTTE dallo stesso
  // lato, quello dove c'e' spazio. Con una riga per membro due etichette non
  // possono sovrapporsi per costruzione.
  //
  // `rows`: un elemento per membro, con `group`, `labelText` e `width` gia'
  // misurata. `outsiders`: i punti del layout a zona chiusa che restano a
  // schermo. Restituisce le posizioni, il lato, l'alone e quali punti esterni
  // vanno nascosti finche' la zona e' aperta perche' finirebbero sotto
  // un'etichetta o un dot dei membri (gli altri vengono solo attenuati).
  function zoneOpenLayout(zone, rows, outsiders, bounds) {
    const sorted = rows.slice().sort((a, b) => a.group.y - b.group.y || a.group.x - b.group.x);
    const n = sorted.length;
    sorted.forEach((row) => {
      const shift = (row.group.x - zone.x) * ZONE_GEO_SPREAD;
      row.x = zone.x + Math.max(-ZONE_MAX_SHIFT, Math.min(ZONE_MAX_SHIFT, shift));
    });
    const rowY = (i, lift) => zone.y + (i - (n - 1) / 2) * ZONE_ROW_STEP + lift;

    // Ingombro di cio' che resta fuori dalla zona: dot ed etichette. Per il
    // testo si usa l'altezza delle maiuscole (9 unita' su un font da 11), non
    // quella di 14 che layoutConflictArea usa per prudenza: quella conta anche
    // lo spazio vuoto sopra le lettere, e qui farebbe nascondere punti che a
    // schermo non toccano niente.
    const outsiderRects = outsiders.map((point) => {
      const rects = [dotObstacleRect(point, 1)];
      if (point.labelText && point.labelOffset) {
        const width = point.width !== undefined && point.width > 3 ? point.width : estimateLabelWidth(point.labelText, 1);
        const lines = point.labelOffset.lines ? point.labelOffset.lines.length : 1;
        rects.push(labelRect(point, point.labelOffset, width, 9, labelDescenderFor(lines, 1)));
      }
      return { point, rects };
    });

    const footprintFor = (side, lift) => {
      const rects = [];
      sorted.forEach((row, i) => {
        const y = rowY(i, lift);
        const radius = (row.group.brands.length > 1 ? 8 : 6) + 2;
        rects.push({ x1: row.x - radius, y1: y - radius, x2: row.x + radius, y2: y + radius });
        const width = row.width;
        const x1 = side === 'start' ? row.x + ZONE_LABEL_GAP : row.x - ZONE_LABEL_GAP - width;
        // Il testo si scrive con la linea di base 4 unita' sotto il centro
        // del dot (dy: 4): l'ingombro va dalle maiuscole ai discendenti.
        rects.push({ x1, y1: y - 6, x2: x1 + width, y2: y + 7 });
      });
      return rects;
    };

    // Si provano i due lati e, per ciascuno, la colonna centrata sulla zona
    // o alzata/abbassata di poco (al massimo mezza riga per parte, cosi'
    // l'ordine nord-sud resta leggibile attorno al punto della zona). Vince
    // la combinazione che resta dentro la mappa e nasconde meno punti
    // altrui; a parita', quella piu' centrata, e la destra prima della
    // sinistra (ordine di prova). Caso reale: con Mayo Spruce la colonna
    // centrata finiva con Sports Image sopra Delta Pro Weight, che spariva
    // finche' la zona restava aperta; alzandola di qualche unita' resta a
    // schermo.
    const lifts = [0, -3, 3, -6, 6];
    let best = null;
    ['start', 'end'].forEach((side) => {
      lifts.forEach((lift) => {
        const footprint = footprintFor(side, lift);
        const inside = footprint.every(
          (r) => r.x1 >= bounds.x1 && r.x2 <= bounds.x2 && r.y1 >= bounds.y1 && r.y2 <= bounds.y2
        );
        const hidden = new Set();
        outsiderRects.forEach(({ point, rects }) => {
          if (rects.some((r) => footprint.some((f) => rectHitsRect(r, f)))) hidden.add(point);
        });
        const score = (inside ? 0 : 1000) + hidden.size;
        if (!best || score < best.score) best = { score, side, lift, hidden };
      });
    });

    sorted.forEach((row, i) => {
      row.y = rowY(i, best.lift);
    });
    const radius =
      sorted.reduce((max, row) => Math.max(max, Math.hypot(row.x - zone.x, row.y - zone.y)), 0) + ZONE_HALO_PADDING;
    sorted.forEach((row) => {
      row.labelOffset = {
        dx: best.side === 'start' ? ZONE_LABEL_GAP : -ZONE_LABEL_GAP,
        dy: 4,
        anchor: best.side,
      };
    });
    return { rows: sorted, side: best.side, hidden: best.hidden, halo: { x: zone.x, y: zone.y, r: radius } };
  }

  function paintPointsInto({ dotsGroup, labelsGroup, groups, bounds, burst, regionKey, labelFontSize, badgeFontSize, disableOverrideIndices, points: precomputed }) {
    dotsGroup.innerHTML = '';
    labelsGroup.innerHTML = '';
    // Dot ed etichette si costruiscono dentro due frammenti staccati dal
    // documento e si appendono in un colpo solo: appendere nodo per nodo
    // dentro un <svg> gia' in pagina invalida il layout a ogni giro, e i
    // nodi qui sono due o tre per punto.
    const dotsFrame = document.createDocumentFragment();
    const labelsFrame = document.createDocumentFragment();

    const fontScale = labelFontSize ? labelFontSize / BASE_LABEL_FONT_SIZE : 1;
    const points =
      precomputed ||
      layoutPoints({ groups, bounds, regionKey, labelFontSize, disableOverrideIndices, labelsGroup });

    points.forEach((point) => {
      const isCluster = point.group.brands.length > 1;
      // Quali brand stanno dietro a questo dot: e' l'aggancio dei filtri
      // della section (applyFilterHighlight), che opacizza a schermo i dot
      // fuori dal filtro senza ridisegnare la mappa.
      const brandIds = point.group.brands.map((brand) => brand.id).join(' ');
      const dotRadius = (isCluster ? 8 : 6) * fontScale;
      const isBurst = !!(burst && burst.indices.has(point.index));
      const startX = isBurst ? burst.fromX : point.x;
      const startY = isBurst ? burst.fromY : point.y;

      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', startX);
      circle.setAttribute('cy', startY);
      circle.setAttribute('r', dotRadius);
      circle.setAttribute(
        'class',
        `manifatture-map__dot${isCluster ? ' manifatture-map__dot--cluster' : ''}${isBurst ? ' manifatture-map__dot--burst' : ''}`
      );
      circle.setAttribute('tabindex', '0');
      circle.setAttribute('role', 'button');
      circle.setAttribute(
        'aria-label',
        isCluster
          ? point.group.city
            ? t('clusterCity')(point.group.city, point.group.brands.length)
            : t('clusterArea')(point.group.brands.length)
          : stripParenthetical(point.group.brands[0].label)
      );
      circle.dataset.manifattureDot = '';
      circle.dataset.brandIds = brandIds;
      circle.dataset.groupIndex = String(point.index);
      if (regionKey) circle.dataset.regionKey = regionKey;
      dotsFrame.appendChild(circle);

      let badge = null;
      if (isCluster) {
        badge = document.createElementNS(SVG_NS, 'text');
        badge.setAttribute('x', startX);
        badge.setAttribute('y', startY);
        badge.setAttribute('class', 'manifatture-map__dot-badge');
        badge.setAttribute('text-anchor', 'middle');
        badge.setAttribute('dy', '0.35em');
        badge.style.pointerEvents = 'none';
        if (badgeFontSize) badge.style.fontSize = `${badgeFontSize}px`;
        badge.textContent = String(point.group.brands.length);
        badge.dataset.brandIds = brandIds;
        dotsFrame.appendChild(badge);
      }

      // Un'etichetta vuota (solo i cluster per vicinanza su mobile, vedi
      // sopra) non va disegnata affatto: point.labelOffset esiste comunque
      // (placeLabels gira su tutti i punti, anche questo, per tenerne il
      // dot tra gli ostacoli delle etichette vicine), ma non c'e' un testo
      // sensato da mostrare fuori dal dot.
      if (point.labelText) {
        const offset = point.labelOffset;

        const label = document.createElementNS(SVG_NS, 'text');
        label.setAttribute('x', point.x + offset.dx);
        label.setAttribute('y', point.y + offset.dy);
        label.setAttribute('text-anchor', offset.anchor);
        label.setAttribute('class', `manifatture-map__label${isBurst ? ' manifatture-map__label--burst' : ''}`);
        label.dataset.brandIds = brandIds;
        label.style.pointerEvents = 'none';
        if (labelFontSize) label.style.fontSize = `${labelFontSize}px`;
        if (offset.lines) {
          // Etichetta su piu' righe (vedi LABEL_POSITION_OVERRIDES): ogni riga
          // e' un tspan che eredita x/text-anchor dal <text> padre e scende di
          // 1.15em rispetto alla precedente.
          offset.lines.forEach((line, lineIndex) => {
            const tspan = document.createElementNS(SVG_NS, 'tspan');
            tspan.setAttribute('x', point.x + offset.dx);
            tspan.setAttribute('dy', lineIndex === 0 ? '0' : '1.15em');
            tspan.textContent = line;
            label.appendChild(tspan);
          });
        } else {
          label.textContent = point.labelText;
        }
        labelsFrame.appendChild(label);
        if (isBurst) burst.elements.push({ circle, badge, label, targetX: point.x, targetY: point.y });
      } else if (isBurst) {
        burst.elements.push({ circle, badge, label: null, targetX: point.x, targetY: point.y });
      }
    });

    dotsGroup.appendChild(dotsFrame);
    labelsGroup.appendChild(labelsFrame);

    if (burst && burst.elements.length) {
      // Forza un reflow: senza questo, il browser puo' accorpare lo stato
      // di partenza (appena scritto sopra) e quello finale (scritto qui
      // sotto) in un solo frame, saltando la transizione.
      //
      // Si legge getBoundingClientRect() e non offsetWidth: offsetWidth
      // esiste solo su HTMLElement, mentre questo e' un <g> SVG
      // (SVGGElement) e la proprieta' vale semplicemente undefined —
      // leggerla non costringeva a nessun ricalcolo, quindi la riga non
      // faceva quello che dice di fare. In pratica l'animazione si vedeva
      // lo stesso, perche' tra il tap e la callback di requestAnimationFrame
      // passa comunque un aggiornamento di rendering: dipendeva pero' da un
      // dettaglio di temporizzazione del browser invece che da questa riga.
      dotsGroup.getBoundingClientRect();
      requestAnimationFrame(() => {
        burst.elements.forEach(({ circle, badge, label, targetX, targetY }) => {
          circle.setAttribute('cx', targetX);
          circle.setAttribute('cy', targetY);
          if (badge) {
            badge.setAttribute('x', targetX);
            badge.setAttribute('y', targetY);
          }
          if (label) label.classList.add('manifatture-map__label--burst-in');
        });
      });
    }

    // I punti con il layout gia' risolto: il chiamante li conserva per
    // sapere dove sono le etichette gia' a schermo (vedi labelRectsFor).
    return points;
  }

  class ManifattureSection extends HTMLElement {
    constructor() {
      super();
      this.usaGroups = [];
      this.usaGroupsBase = [];
      // Mappa desktop a zone chiuse (vedi desktopZoneGroups) e il suo
      // layout, da cui si riparte richiudendo una zona; la zona aperta, se
      // c'e' (vedi openDesktopZone).
      this.desktopCollapsed = [];
      this.desktopCollapsedPoints = null;
      this.desktopZone = null;
      this.lastRenderWasMobile = null;
      this.regionCanvases = [];
      this.brandsById = new Map();
      this.allBrands = [];
      this.filtersBar = null;
      this.filterGroups = [];
      this.filterQuery = '';
      this.matchingIds = null;
      this.pulseEl = null;
      this.haloEl = null;
      this.markedCard = null;
      this.highlightMuted = false;
      this.searchFocused = false;
      this.focusToken = 0;
      this.hasInternational = false;
      this.searchIndex = new Map();
      this.openBrandId = null;
      this.openVendor = null;
      this.sheetExpanded = false;
      this.sheetDrag = null;
      this.suppressGrabberClick = false;
      this.onGrabberPointerDown = this.onGrabberPointerDown.bind(this);
      this.onGrabberPointerMove = this.onGrabberPointerMove.bind(this);
      this.onGrabberPointerUp = this.onGrabberPointerUp.bind(this);
      this.onGrabberClick = this.onGrabberClick.bind(this);
      this.onSheetScroll = this.onSheetScroll.bind(this);
      this.onSheetTouchStart = this.onSheetTouchStart.bind(this);
      this.onSheetTouchMove = this.onSheetTouchMove.bind(this);
      this.onSheetTouchEnd = this.onSheetTouchEnd.bind(this);
      this.sheetPull = null;
      this.lastSheetScrollTop = 0;
      this.onSectionClick = this.onSectionClick.bind(this);
      this.onSectionKeydown = this.onSectionKeydown.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onSectionPointerOver = this.onSectionPointerOver.bind(this);
      this.onSectionTouchStart = this.onSectionTouchStart.bind(this);
      this.syncPanelTop = this.syncPanelTop.bind(this);
      this.panelTopFrame = null;
      this.lastPanelTop = null;
    }

    connectedCallback() {
      this.gistUrl = this.dataset.gistUrl;
      this.sectionId = this.dataset.sectionId || '';
      // Lingua e rotta di ricerca arrivano dal Liquid: vanno lette prima di
      // qualunque render, perche' STRINGS, countryName e le richieste dei
      // prodotti le usano gia' alla prima passata. Dell'iso_code si tengono
      // solo le prime due lettere: una lingua regionale ("en-GB") resta
      // l'inglese, e una lingua per cui non c'e' una tabella ricade
      // sull'italiano, in cui il dataset e' scritto per intero.
      const iso = (this.dataset.lang || '').slice(0, 2).toLowerCase();
      LANG = STRINGS[iso] ? iso : 'it';
      SEARCH_URL = this.dataset.searchUrl || SEARCH_URL;

      this.statusEl = this.querySelector('[data-manifatture-status]');
      this.loadingEl = this.querySelector('[data-manifatture-loading]');
      this.errorEl = this.querySelector('[data-manifatture-error]');
      this.emptyEl = this.querySelector('[data-manifatture-empty]');
      this.mapEl = this.querySelector('[data-manifatture-map]');
      this.mapWrapperEl = this.querySelector('[data-manifatture-map-wrapper]');
      this.mapRegionsEl = this.querySelector('[data-manifatture-map-regions]');
      this.statesGroup = this.querySelector('.manifatture-map__states');
      this.dotsGroup = this.querySelector('[data-manifatture-dots]');
      this.labelsGroup = this.querySelector('[data-manifatture-labels]');
      this.clusterPopover = this.querySelector('[data-manifatture-cluster-popover]');
      this.brandsEl = this.querySelector('[data-manifatture-brands]');
      this.intlEl = this.querySelector('[data-manifatture-international]');
      this.regionsEl = this.querySelector('[data-manifatture-regions]');
      this.panel = this.querySelector('[data-manifatture-panel]');
      this.panelContent = this.querySelector('[data-manifatture-panel-content]');
      this.panelCloseBtn = this.querySelector('[data-manifatture-panel-close]');
      this.panelSheet = this.querySelector('.manifatture-panel__sheet');
      this.panelGrabber = this.querySelector('[data-manifatture-panel-grabber]');

      // I gesti della maniglia restano agganciati anche sopra i 990px: non
      // servono (il CSS la nasconde nel drawer desktop) ma nemmeno danno
      // fastidio, e cosi' non c'e' da riagganciarli passando la soglia con
      // un resize. Sono i gestori stessi a fermarsi se il layout non e'
      // quello a foglio (vedi isSheetLayout).
      if (this.panelGrabber) {
        this.panelGrabber.addEventListener('pointerdown', this.onGrabberPointerDown);
        this.panelGrabber.addEventListener('click', this.onGrabberClick);
      }
      if (this.panelSheet) {
        this.panelSheet.addEventListener('scroll', this.onSheetScroll, { passive: true });
        this.panelSheet.addEventListener('touchstart', this.onSheetTouchStart, { passive: true });
        // passive: false perche' questo gestore, quando prende il gesto,
        // deve poter chiamare preventDefault per fermare lo scroll nativo.
        this.panelSheet.addEventListener('touchmove', this.onSheetTouchMove, { passive: false });
        this.panelSheet.addEventListener('touchend', this.onSheetTouchEnd);
        this.panelSheet.addEventListener('touchcancel', this.onSheetTouchEnd);
      }

      this.addEventListener('click', this.onSectionClick);
      this.addEventListener('keydown', this.onSectionKeydown);
      this.addEventListener('pointerover', this.onSectionPointerOver);
      this.addEventListener('touchstart', this.onSectionTouchStart, { passive: true });
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.onWindowResize);

      // Il pannello e' un overlay in position: fixed, quindi resta agganciato
      // allo schermo anche quando la sezione a cui si riferisce e' ormai
      // scrollata via: continuare a mostrare la scheda di un brand mentre la
      // mappa da cui e' stata aperta non e' piu' in vista non ha molto senso
      // (e su desktop il drawer coprirebbe contenuti di tutt'altra sezione).
      // Quando la section esce DEL TUTTO dalla viewport — scrollando in su
      // verso l'inizio pagina o in giu' oltre le card internazionali — il
      // pannello si chiude da solo. Finche' ne resta visibile anche solo una
      // parte non si tocca nulla: dentro la "zona mappa" il drawer deve
      // restare aperto. IntersectionObserver invece di un listener di scroll:
      // non costa nulla quando non succede niente.
      this.sectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) return;
        if (this.panel && !this.panel.hidden) this.closePanel();
      });
      this.sectionObserver.observe(this);

      this.activeVendors = this.readActiveVendors();
      this.watchCartNotification();
      this.load();
    }

    // La notifica non emette eventi: l'unico segnale del suo stato e' la
    // classe .active che cart-notification.js aggiunge e toglie. Si osserva
    // quella. L'elevazione scatta solo se il pannello e' aperto: se non lo
    // e', la notifica funziona gia' benissimo da sola e non c'e' motivo di
    // toccare l'impilamento dell'header.
    watchCartNotification() {
      const notification = document.querySelector('cart-notification');
      const panel = notification && notification.querySelector('#cart-notification, .cart-notification');
      if (!panel) return;

      // Va richiamata anche all'apertura/chiusura del pannello, non solo al
      // cambio di stato della notifica: se la notifica e' gia' aperta e la
      // scheda vendor si apre dopo, l'observer non scatterebbe e la
      // notifica finirebbe dietro al pannello (verificato: e' proprio cosi'
      // che si comportava).
      this.syncCartNotificationElevation = () => {
        const notificationOpen = panel.classList.contains('active');
        const panelOpen = this.panel && !this.panel.hidden;
        if (notificationOpen && panelOpen) {
          // Sempre uno sopra il pannello, qualunque quota abbia raggiunto
          // misurando i widget: e' l'invariante che tiene visibile la
          // notifica quando si aggiunge al carrello dalla scheda.
          elevateAncestors(notification, (this.panelZIndex || PANEL_Z_BASE) + 1);
        } else {
          restoreElevatedAncestors();
        }
      };
      const sync = this.syncCartNotificationElevation;

      this.cartNotificationObserver = new MutationObserver(sync);
      this.cartNotificationObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.onWindowResize);
      window.clearTimeout(this.resizeTimeout);
      window.clearTimeout(this.filterTimeout);
      window.clearTimeout(this.searchBlurTimeout);
      window.clearTimeout(this.pulseTimeout);
      window.clearTimeout(this.markTimeout);
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.cartNotificationObserver) this.cartNotificationObserver.disconnect();
      window.clearTimeout(this.prefetchTimeout);
      cancelPrefetches();
      restoreElevatedAncestors();
      this.stopPanelTopSync();
    }

    // Il raggruppamento per vicinanza (clusterByProximity) si applica solo
    // sotto MOBILE_BREAKPOINT_QUERY: se la finestra viene ridimensionata
    // attraverso quella soglia (es. Chrome DevTools, o l'anteprima
    // responsive dell'editor Shopify, che ridimensiona l'iframe senza
    // ricaricare la pagina) la mappa va ricalcolata da zero sui gruppi base
    // (this.usaGroupsBase), altrimenti resterebbe nello stato mobile/desktop
    // in cui e' stata caricata la prima volta. Il controllo su
    // lastRenderWasMobile evita di rifare tutto il lavoro (placeLabels
    // incluso) a ogni singolo pixel di resize, quando la soglia non e' stata
    // attraversata.
    onWindowResize() {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => {
        if (!this.usaGroupsBase.length) return;
        if (isMobileViewport() === this.lastRenderWasMobile) return;
        this.renderMap(this.usaGroupsBase);
      }, 150);
    }

    // Il Gist e' la fonte di verita' e resta la prima scelta. Quello che
    // cambia e' cosa succede quando non risponde: prima si finiva
    // direttamente su "Impossibile caricare i marchi", cioe' una pagina che
    // non ha niente da dire.
    //
    // La scala e' la stessa gia' adottata per le recensioni, che hanno
    // esattamente lo stesso problema sullo stesso host (vedi
    // fixes/recensioni-resilienza nel repo grid-garments-websitemods):
    //
    //   1. copia in cache piu' recente di 24h  -> si usa, NESSUNA richiesta
    //   2. altrimenti il Gist                  -> si usa e si salva in cache
    //   3. Gist muto, copia in cache scaduta   -> si usa lo stesso
    //   4. niente copia                        -> il seme scritto nel tema
    //   5. niente seme                         -> l'elenco dei marchi
    //
    // Il quinto gradino e' l'unico in piu' rispetto alle recensioni, e non
    // costa niente: i nomi dei marchi sono gia' nella pagina, resi dal tema.
    async load() {
      this.setStatus('loading');
      const data = await this.loadData();
      if (data) {
        const hasContent = this.renderData(data);
        this.setStatus(hasContent ? 'ready' : 'empty');
        // Solo a disegno fatto: prima di qui brandsById e' vuota e i dot non
        // esistono ancora, quindi non ci sarebbe niente da centrare.
        if (hasContent) this.openRequestedBrand();
        return;
      }
      // Se anche l'elenco non e' possibile (tema senza il contenitore nuovo,
      // o negozio senza vendor) si torna al messaggio di prima: meglio dire
      // che non si e' riusciti che lasciare la pagina muta.
      if (this.renderBrandList()) {
        this.setStatus('ready');
        return;
      }
      this.setStatus('error');
    }

    async loadData() {
      const cached = this.readCache();
      if (cached && Date.now() - cached.at < CACHE_FRESH_MS) {
        // Si disegna subito con la copia, e il controllo parte per conto suo:
        // non si aspetta, altrimenti si sarebbe tornati a far dipendere il
        // primo disegno da GitHub, che e' il problema di partenza.
        this.revalidazione = this.revalidaInSottofondo(cached);
        return cached.data;
      }

      try {
        const { data, text } = await this.fetchGist();
        // Non si mette in copia un insieme vuoto. Un Gist momentaneamente
        // svuotato (un errore di scrittura, un file troncato) diventerebbe
        // altrimenti la verita' del browser per 24 ore, e la pagina direbbe
        // "nessun marchio" invece di ripiegare sul seme. Vuoto = non
        // affidabile: lo si usa per questo caricamento, non lo si conserva.
        if (haContenuto(data)) this.storeCache(text);
        return data;
      } catch (error) {
        console.warn('[manifatture] gist non disponibile, passo alle copie', error);
      }

      if (cached) return cached.data;
      return this.readSeed();
    }

    async fetchGist() {
      if (!this.gistUrl) throw new Error('data-gist-url mancante');
      // Il parametro serve a scavalcare la cache di
      // gist.githubusercontent.com (vedi README, "Nota sulla cache"), ma
      // con Date.now() ogni singola visita ne generava uno diverso: URL
      // sempre nuovo, quindi nessuna cache possibile nemmeno nel browser,
      // e il file (una entry per vendor, con due descrizioni ciascuna)
      // riscaricato per intero a ogni pagina aperta, anche ricaricando la
      // stessa pagina due volte di fila. Arrotondando l'istante a una
      // finestra di pochi minuti l'URL resta lo stesso per tutte le
      // visite dentro quella finestra: chi naviga il sito scarica il Gist
      // una volta sola invece che a ogni pagina, e una modifica al Gist
      // continua ad arrivare a tutti entro la finestra, esattamente come
      // prima. Il `cache: 'no-store'` di prima e' stato tolto per lo
      // stesso motivo: vietava di conservare la risposta.
      //
      // La finestra resta utile anche con la cache qui sotto: copre chi
      // apre la pagina per la prima volta in piu' schede, e il primo
      // caricamento dopo le 24h.
      const bucket = Math.floor(Date.now() / GIST_CACHE_WINDOW_MS);
      const url = `${this.gistUrl}?v=${bucket}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Gist fetch failed: ${response.status}`);
      // Il testo serve alla copia in cache: ristringere l'oggetto appena
      // parsato costerebbe una seconda passata su un file grosso, per
      // riottenere byte che abbiamo gia' in mano.
      const text = await response.text();
      return { data: JSON.parse(text), text };
    }

    storeCache(text) {
      try {
        // Il dato entra cosi' com'e' arrivato, senza passare da
        // JSON.stringify: e' gia' testo JSON valido, e su un file di queste
        // dimensioni rifare il giro costerebbe per niente.
        window.localStorage.setItem(
          CACHE_KEY,
          `{"at":${Date.now()},"fp":${impronta(text)},"data":${text}}`
        );
      } catch (error) {
        // Quota piena, storage negato, navigazione privata: la copia e' un
        // extra, e non deve mai rompere la pagina che sta andando bene.
      }
    }

    // Il controllo in sottofondo. Non tocca quello che e' gia' a schermo:
    // aggiorna la copia, e la pagina successiva parte dal dato nuovo.
    //
    // Non ridisegna la mappa a pagina viva, e non e' una dimenticanza:
    // renderData() ripassa da initSectionFilters(), che RIAGGANCIA i
    // listener della barra di ricerca e delle tendine. Chiamarla una seconda
    // volta li raddoppierebbe. Farlo in sicurezza vuol dire rendere
    // idempotente quella funzione, cioe' mettere mano al codice
    // dell'interazione: si puo' fare, ma va guardato in un browser, non
    // dedotto.
    async revalidaInSottofondo(cached) {
      const finestra = String(Math.floor(Date.now() / GIST_CACHE_WINDOW_MS));
      try {
        if (window.localStorage.getItem(REVALIDATE_KEY) === finestra) return false;
        // Si segna PRIMA di partire, cosi' due schede aperte insieme non
        // fanno due richieste.
        window.localStorage.setItem(REVALIDATE_KEY, finestra);
      } catch (error) {
        // Senza storage non c'e' modo di limitare il controllo fra schede, e
        // nemmeno una copia da aggiornare: si lascia perdere.
        return false;
      }

      try {
        const { data, text } = await this.fetchGist();
        if (!haContenuto(data)) return false;
        this.storeCache(text);
        return impronta(text) !== cached.fp;
      } catch (error) {
        // Gist muto mentre la pagina e' gia' disegnata dalla copia: non e'
        // successo niente di visibile, e non c'e' niente da dire.
        return false;
      }
    }

    readCache() {
      try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.at !== 'number') return null;
        if (!haContenuto(parsed.data)) return null;
        // fp puo' mancare su una copia scritta da una versione precedente:
        // vale null, e il primo controllo in sottofondo la rimpiazza.
        return { at: parsed.at, fp: typeof parsed.fp === 'number' ? parsed.fp : null, data: parsed.data };
      } catch (error) {
        return null;
      }
    }

    // Il seme: i marchi scritti nel tema, dentro snippets/manifatture-seme.
    // Non e' una richiesta di rete — e' markup gia' arrivato con la pagina —
    // ed e' l'unico gradino che funziona alla PRIMA visita di qualcuno
    // mentre GitHub e' giu'.
    readSeed() {
      const el = this.querySelector('[data-manifatture-seme]');
      if (!el) return null;
      try {
        // Un seme vuoto (snippet presente ma non ancora riempito) non e' un
        // dato: va trattato come assente, altrimenti la pagina direbbe
        // "nessun marchio" invece di scendere all'elenco.
        const data = JSON.parse(el.textContent || '{}');
        return haContenuto(data) ? data : null;
      } catch (error) {
        console.error('[manifatture] seme illeggibile', error);
        return null;
      }
    }

    // I nomi dei marchi, dal blocco JSON che il tema rende insieme alla
    // pagina. E' lo stesso dato di readActiveVendors(), ma tenuto com'e'
    // scritto invece che normalizzato per il confronto: qui si mostra, non
    // si confronta.
    readActiveVendorNames() {
      const el = this.querySelector('[data-manifatture-active-vendors]');
      if (!el) return [];
      try {
        const list = JSON.parse(el.textContent || '[]');
        if (!Array.isArray(list)) return [];
        return list
          .map((vendor) => String(vendor).trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
      } catch (error) {
        return [];
      }
    }

    renderBrandList() {
      if (!this.brandsEl) return false;
      const names = this.readActiveVendorNames();
      if (!names.length) return false;

      const note = document.createElement('p');
      note.className = 'manifatture-section__brands-note';
      note.textContent = STRINGS[LANG].offlineNote;

      const list = document.createElement('ul');
      list.className = 'manifatture-section__brands-list';
      names.forEach((name) => {
        const item = document.createElement('li');
        item.className = 'manifatture-section__brands-item';
        item.textContent = name;
        list.appendChild(item);
      });

      this.brandsEl.innerHTML = '';
      this.brandsEl.appendChild(note);
      this.brandsEl.appendChild(list);
      this.brandsEl.hidden = false;
      return true;
    }

    setStatus(state) {
      if (this.loadingEl) this.loadingEl.hidden = state !== 'loading';
      if (this.errorEl) this.errorEl.hidden = state !== 'error';
      if (this.emptyEl) this.emptyEl.hidden = state !== 'empty';
      if (this.statusEl) this.statusEl.hidden = state === 'ready';
    }

    // I vendor con almeno un prodotto nel catalogo visibile, letti dal
    // blocco JSON reso dalla sezione. Null quando il dato non c'e'
    // (sezione non aggiornata, o negozio senza prodotti): in quel caso non
    // si filtra NULLA. E' una scelta deliberata — un errore qui
    // svuoterebbe la mappa, che e' molto peggio di un punto che apre una
    // scheda senza prodotti.
    readActiveVendors() {
      const el = this.querySelector('[data-manifatture-active-vendors]');
      if (!el) return null;
      try {
        const list = JSON.parse(el.textContent || '[]');
        if (!Array.isArray(list) || !list.length) return null;
        // Confronto senza maiuscole/minuscole e senza spazi ai bordi: la
        // ricerca prodotti di Shopify e' gia' tollerante su questo, quindi
        // un vendor scritto "Levi's" invece di "Levi'S" nel Gist funziona
        // sul pannello. Sarebbe assurdo che il punto sparisse dalla mappa
        // per una differenza che poi non impedisce ai prodotti di caricarsi.
        return new Set(list.map((vendor) => String(vendor).trim().toLowerCase()));
      } catch (error) {
        console.error('[manifatture] elenco vendor attivi illeggibile', error);
        return null;
      }
    }

    vendorHasProducts(entry) {
      if (!this.activeVendors) return true;
      const vendor = (entry.shopify_vendor || '').trim().toLowerCase();
      return vendor ? this.activeVendors.has(vendor) : false;
    }

    renderData(rawData) {
      const entries = Object.keys(rawData || {}).map((key) => ({ id: key, ...rawData[key] }));
      const visible = entries
        .filter((entry) => entry.status === 'published' && entry.show_on_map === true)
        .filter((entry) => this.vendorHasProducts(entry));

      // L'insieme completo dei brand pubblicati resta a disposizione: e' la
      // base su cui i filtri lavorano (e su cui si costruiscono le opzioni
      // delle tendine), mentre a schermo resta sempre tutto: i filtri
      // opacizzano la mappa e nascondono le card, non ridisegnano.
      this.allBrands = visible;
      this.brandsById = new Map(visible.map((entry) => [entry.id, entry]));

      this.initSectionFilters(visible);

      return this.renderBrands(visible) > 0;
    }

    // Disegna a schermo l'insieme dei brand pubblicati. Viene chiamata una
    // volta sola, con l'insieme COMPLETO: i filtri non ridisegnano nulla,
    // opacizzano (mappa) o nascondono (card internazionali) quello che e'
    // gia' in pagina — vedi applyFilterHighlight. Restituisce quanti brand
    // ha reso tra mappa e card internazionali.
    renderBrands(brands) {
      const usaBrands = brands.filter(
        (entry) =>
          entry.tipo === 'brand_usa' &&
          typeof entry.lat === 'number' &&
          typeof entry.lng === 'number'
      );
      const intlBrands = brands.filter((entry) => entry.tipo === 'brand_international');

      if (usaBrands.length) {
        this.usaGroupsBase = clusterUsaBrands(usaBrands);
        // La mappa va resa VISIBILE PRIMA di disegnarla: measureLabelWidths
        // misura il testo con getComputedTextLength(), che su un elemento in
        // display:none restituisce 0. Rendendo visibile il contenitore dopo
        // il primo rendering, ogni etichetta delle card mobile ripiegava
        // sulla larghezza STIMATA — che e' volutamente prudente e, sui nomi
        // reali, fino al ~39% piu' larga di quella vera ("Carhartt": 102,6
        // unita' stimate contro 74 reali). Con quella misura gonfiata il
        // controllo dei bordi scartava posizioni perfettamente valide e le
        // etichette venivano spinte lontano dal proprio dot senza motivo.
        if (this.mapEl) this.mapEl.hidden = false;
        this.renderMap(this.usaGroupsBase);
      }

      this.hasInternational = intlBrands.length > 0;
      if (intlBrands.length) {
        this.renderInternational(intlBrands);
        if (this.intlEl) this.intlEl.hidden = false;
      }

      return usaBrands.length + intlBrands.length;
    }

    // Applica a schermo la selezione corrente dei filtri, senza ridisegnare
    // nulla: this.matchingIds e' l'insieme dei brand che la soddisfano
    // (null = nessun filtro attivo, tutto torna in chiaro).
    //
    // Va richiamata dopo OGNI ridisegno della mappa, non solo al cambio di
    // filtro: la mappa si ridisegna anche da sola — al passaggio della
    // soglia mobile/desktop e all'esplosione di un cluster al tap — e i dot
    // appena creati nascerebbero tutti in chiaro. Per questo la chiamano
    // paintGroups e paintRegionCanvas, che sono i due punti da cui passa
    // ogni ridisegno.
    applyFilterHighlight() {
      // Sospesa: si torna a vedere tutto, ma i filtri restano compilati e il
      // segno sul dot trovato resta dov'e' (vedi muteHighlight).
      const ids = this.highlightMuted ? null : this.matchingIds;

      // Dot, badge ed etichette della mappa (nazionale e card regionali
      // mobile), piu' le voci del popover di un cluster aperto. Un dot che
      // ne raccoglie piu' d'uno resta in chiaro finche' ALMENO UNO dei suoi
      // brand rientra nel filtro: opacizzarlo per gli altri nasconderebbe
      // un risultato valido.
      this.querySelectorAll('[data-brand-ids]').forEach((el) => {
        const dimmed = ids
          ? !el.dataset.brandIds.split(' ').some((id) => ids.has(id))
          : false;
        el.classList.toggle('is-dimmed', dimmed);
      });

      // Card regionali mobile: quelle senza risultati scendono in fondo e il
      // titolo di quelle che ne hanno porta il conteggio ("Sud · 2"). Non e'
      // un riordino per numero di risultati ma una sola partizione, e
      // l'ordine geografico abituale (Nord-est, Midwest, Sud, Ovest) resta
      // tra le card che restano in alto: le card che ballano a ogni battuta
      // fanno perdere il punto di riferimento, mentre "le regioni vuote
      // stanno sotto" e' una regola che si impara al primo colpo. Si usa la
      // proprieta' CSS `order` invece di spostare i nodi: nessun ridisegno,
      // e un elemento che ha il fuoco non lo perde.
      (this.regionCanvases || []).forEach((canvas) => {
        if (!canvas.cardEl || !canvas.titleEl) return;
        const base = canvas.titleEl.dataset.regionTitle || canvas.titleEl.textContent;
        if (!ids) {
          canvas.cardEl.style.order = '';
          canvas.cardEl.classList.remove('is-empty');
          canvas.titleEl.textContent = base;
          return;
        }
        const matches = (canvas.brandIds || []).filter((id) => ids.has(id)).length;
        canvas.cardEl.style.order = matches ? '0' : '1';
        canvas.cardEl.classList.toggle('is-empty', matches === 0);
        canvas.titleEl.textContent = matches ? `${base} · ${matches}` : base;
      });

      if (!this.regionsEl) return;
      this.regionsEl.querySelectorAll('.manifatture-international__region').forEach((region) => {
        let visible = 0;
        region.querySelectorAll('[data-brand-id]').forEach((card) => {
          const hidden = ids ? !ids.has(card.dataset.brandId) : false;
          card.hidden = hidden;
          if (!hidden) visible += 1;
        });
        region.hidden = visible === 0;
      });
      if (this.intlEl && this.hasInternational) {
        this.intlEl.hidden = !this.regionsEl.querySelector(
          '.manifatture-international__region:not([hidden])'
        );
      }
    }

    // Aggancia la barra di ricerca e costruisce le tendine a partire dai
    // brand visibili. Se la barra non c'e' (filtri disattivati dal
    // Customizer, vedi show_filters in sections/manifatture.liquid) non fa
    // NULLA e la section resta quella di prima: nessun listener, nessun
    // lavoro in piu', il dataset completo sempre in chiaro.
    initSectionFilters(brands) {
      this.filtersBar = this.querySelector('[data-manifatture-section-filters]');
      if (!this.filtersBar) return;

      this.searchInput = this.filtersBar.querySelector('[data-manifatture-search]');
      this.filterGroupsEl = this.filtersBar.querySelector('[data-manifatture-filter-groups]');
      this.filterCountEl = this.filtersBar.querySelector('[data-manifatture-filter-count]');
      this.filterResetEl = this.filtersBar.querySelector('[data-manifatture-filter-reset]');
      this.filterEmptyEl = this.filtersBar.querySelector('[data-manifatture-filter-empty]');
      this.filterResultsEl = this.filtersBar.querySelector('[data-manifatture-filter-results]');

      // Con un solo marchio non c'e' niente da cercare tra cosa: la
      // barra resta nascosta invece di presentare filtri a un solo esito.
      if (brands.length < 2) return;

      this.searchIndex = new Map(brands.map((brand) => [brand.id, brandSearchText(brand)]));
      this.filterGroups = this.buildFilterGroups(brands);
      this.filtersBar.hidden = false;

      if (this.searchInput) {
        // Debounce anche senza ridisegno della mappa: ogni battuta rivaluta
        // tutti i brand e riscrive i contatori di ogni opzione di ogni
        // tendina, e a schermo il testo cercato deve poter arrivare a meta'
        // parola senza che tutto lampeggi.
        this.searchInput.addEventListener('input', () => {
          window.clearTimeout(this.filterTimeout);
          this.filterTimeout = window.setTimeout(() => {
            this.filterQuery = normalizeText(this.searchInput.value);
            this.applySectionFilters();
          }, 200);
        });
      }

      if (this.searchInput) {
        // L'elenco dei risultati e' la tendina DI QUESTO CAMPO: si vede
        // mentre il campo e' attivo e c'e' qualcosa di scritto, e sparisce
        // appena si va altrove. Prima restava a schermo anche filtrando
        // dalle sole tendine, dove non e' un suggerimento di ricerca ma un
        // riquadro fermo in mezzo alla mappa.
        this.searchInput.addEventListener('focus', () => {
          this.searchFocused = true;
          this.refreshFilterResults();
        });
        // Il ritardo serve al click su una riga: il campo perde il fuoco
        // (mousedown) PRIMA che il click arrivi, e nascondendo subito
        // l'elenco la riga sparirebbe da sotto il dito.
        this.searchInput.addEventListener('blur', (event) => {
          // Fuoco passato DENTRO l'elenco (tab dal campo alla prima riga):
          // e' come restare nel campo, l'elenco non si tocca. Senza questo
          // controllo chi naviga da tastiera se lo vedeva sparire proprio
          // mentre ci stava entrando.
          if (this.resultsHaveFocus(event.relatedTarget)) return;
          window.clearTimeout(this.searchBlurTimeout);
          this.searchBlurTimeout = window.setTimeout(() => {
            if (this.resultsHaveFocus(document.activeElement)) return;
            this.searchFocused = false;
            this.refreshFilterResults();
          }, 150);
        });
      }

      if (this.filterResultsEl) {
        // Uscendo dall'elenco con il tab (e non tornando nel campo) si
        // chiude, come uscendo dal campo.
        this.filterResultsEl.addEventListener('focusout', (event) => {
          const dove = event.relatedTarget;
          if (this.resultsHaveFocus(dove) || dove === this.searchInput) return;
          this.searchFocused = false;
          this.refreshFilterResults();
        });
      }

      if (this.filterGroupsEl) {
        this.filterGroupsEl.addEventListener('change', () =>
          this.applySectionFilters({ daTendina: true })
        );
      }

      if (this.filterResetEl) {
        this.filterResetEl.addEventListener('click', () => this.resetSectionFilters());
      }

      this.applySectionFilters();
    }

    buildFilterGroups(brands) {
      if (!this.filterGroupsEl) return [];
      this.filterGroupsEl.innerHTML = '';

      const built = [];
      SECTION_FILTER_GROUPS.forEach((group) => {
        const options = filterOptionsFor(group, brands);
        // Un gruppo con una sola opzione non e' una scelta: filtrerebbe
        // sempre su tutto. Zero opzioni vuol dire campo mai valorizzato nel
        // Gist (es. "Nazione" quando non c'e' nessun brand internazionale).
        if (options.length < 2) return;

        const select = document.createElement('select');
        select.className = 'manifatture-search__select';
        select.dataset.filterSelect = group.name;
        select.setAttribute('aria-label', t('filterGroups')[group.name] || group.name);

        // La prima opzione fa da etichetta della tendina ("Tutti gli stati"):
        // a tendina chiusa si legge sempre di cosa si sta scegliendo, senza
        // dover mettere una <label> sopra ogni menu.
        const all = document.createElement('option');
        all.value = '';
        all.dataset.label = t('filterAllOf')[group.name] || group.name;
        select.appendChild(all);

        options.forEach((option) => {
          const el = document.createElement('option');
          el.value = option.value;
          el.dataset.label = option.label;
          select.appendChild(el);
        });

        this.filterGroupsEl.appendChild(select);
        built.push({ group, select });
      });

      return built;
    }

    // Selezione corrente: la stringa cercata piu' il valore attivo di ogni
    // tendina (stringa vuota = nessun vincolo).
    sectionFilterSelection() {
      const state = { q: this.filterQuery };
      this.filterGroups.forEach(({ group, select }) => {
        state[group.name] = select.value;
      });
      return state;
    }

    brandMatchesFilters(brand, state) {
      if (state.q) {
        const haystack = this.searchIndex.get(brand.id) || '';
        // Tutti i termini devono comparire, in qualunque ordine: "cincinnati
        // velva" trova Velva Sheen come "velva cincinnati".
        if (!state.q.split(/\s+/).every((token) => matchesWordStart(haystack, token))) {
          return false;
        }
      }
      return this.filterGroups.every(
        ({ group }) => !state[group.name] || brandFilterValue(group, brand) === state[group.name]
      );
    }

    // `daTendina`: la selezione e' cambiata scegliendo da un menu, non
    // scrivendo. Cambia una cosa sola — vedi in fondo — ma e' una differenza
    // che conta: scegliere da una tendina e' un gesto unico e concluso,
    // scrivere e' una raffica di stati intermedi.
    applySectionFilters(opzioni) {
      if (!this.filtersBar || this.filtersBar.hidden) return;
      const daTendina = !!(opzioni && opzioni.daTendina);

      const state = this.sectionFilterSelection();
      const matching = this.allBrands.filter((brand) => this.brandMatchesFilters(brand, state));
      const filtered = Boolean(state.q) || this.filterGroups.some(({ group }) => state[group.name]);

      // Cambiata la ricerca, il segno sul dot trovato prima non vuol dire
      // piu' niente, e l'elenco torna a mostrarsi (era stato ritirato dal
      // click su un risultato). Si chiude anche l'elenco di un cluster
      // aperto dalla ricerca precedente: resterebbe a schermo puntato su un
      // dot che il filtro nuovo ha appena spento.
      this.clearDotMark();
      this.highlightMuted = false;
      this.focusToken += 1;
      this.hideClusterPopover();
      (this.regionCanvases || []).forEach((canvas) => this.hideClusterPopover(canvas.popoverEl));

      // Nessun filtro attivo: matchingIds resta null e applyFilterHighlight
      // rimette tutto in chiaro, invece di marcare "compreso nel filtro" ogni
      // singolo brand.
      this.matchingIds = filtered ? new Set(matching.map((brand) => brand.id)) : null;
      this.applyFilterHighlight();

      // Un'opzione che non porterebbe ad alcun risultato si disabilita, ma
      // mai quella attiva: altrimenti si resterebbe bloccati su una scelta
      // da cui non si puo' piu' uscire (stessa regola della barra filtri dei
      // prodotti). Il numero NON si mostra: in una tendina di nomi propri —
      // dove quasi ogni voce vale 1 — era rumore accanto a ogni riga, e a
      // quanti marchi resta il filtro lo dice gia' il conteggio sotto
      // la barra. Serve ancora calcolarlo, ma solo per sapere cosa spegnere.
      this.filterGroups.forEach(({ group, select }) => {
        // Quanti marchi resterebbero scegliendo CIASCUNA opzione di
        // questa tendina: e' sempre la stessa selezione con il solo vincolo
        // di questo gruppo sostituito, quindi basta una passata sola sui
        // brand — quelli che soddisfano tutto il resto — raggruppandoli per
        // il valore che hanno in questo gruppo. Prima si rifaceva un
        // filtro completo per OGNI opzione: con un'opzione per marchio (piu'
        // citta', stato e nazione) erano migliaia di confronti a ogni
        // battuta sulla tastiera, tutti per ricavare gli stessi numeri.
        const senzaQuestoGruppo = Object.assign({}, state, { [group.name]: '' });
        const perValore = new Map();
        let totale = 0;
        this.allBrands.forEach((brand) => {
          if (!this.brandMatchesFilters(brand, senzaQuestoGruppo)) return;
          totale += 1;
          const value = brandFilterValue(group, brand);
          perValore.set(value, (perValore.get(value) || 0) + 1);
        });
        Array.from(select.options).forEach((option) => {
          // L'opzione vuota ("Tutti i marchi") non pone vincoli: vale il
          // totale senza questo gruppo.
          const count = option.value ? perValore.get(option.value) || 0 : totale;
          option.textContent = option.dataset.label;
          option.disabled = count === 0 && option.value !== select.value;
        });
      });

      if (this.filterCountEl) {
        this.filterCountEl.textContent = t('filterCount')(matching.length, this.allBrands.length);
      }
      if (this.filterResetEl) this.filterResetEl.hidden = !filtered;
      if (this.filterEmptyEl) this.filterEmptyEl.hidden = matching.length > 0;
      this.refreshFilterResults();

      // Scelta da una tendina: si va a vedere cosa ha lasciato in piedi. Le
      // tendine non hanno un elenco di suggerimenti (e' la tendina del campo
      // di ricerca, e li' il campo non e' in gioco), quindi senza questo
      // scegliere "Marchio: Carhartt" opacizzava tutto il resto senza dire
      // dove fosse finito Carhartt — e con la mappa sotto la piega
      // l'opacizzazione non si vede nemmeno.
      //
      // Solo da tendina, mai scrivendo: si passa per una sequenza di stati
      // intermedi che possono valere 1 per una lettera sola, e la pagina si
      // metterebbe a saltare sotto le dita a meta' parola. Scegliere da una
      // tendina invece e' un gesto unico e concluso.
      //
      // Un solo risultato ha un posto preciso: ci si va con il salto e il
      // segno di quando si clicca una riga dei risultati. Con piu' risultati
      // non c'e' un dot da marcare, ma un POSTO da mostrare c'e' eccome:
      // scegliendo "Stato: Michigan" — o una nazione, o una citta' con due
      // marchi — la pagina restava dov'era, e le tendine che lasciano un
      // solo marchio portavano a vederlo mentre "Tutti gli stati" no. Si
      // porta a schermo l'insieme dei risultati, senza aprire cluster.
      if (daTendina && matching.length === 1) {
        this.focusBrand(matching[0].id);
      } else if (daTendina && filtered && matching.length > 1) {
        this.focusBrandGroup(this.matchingIds);
      }
    }

    // Elenco dei risultati sotto ai comandi: righe cliccabili che aprono
    // direttamente la scheda del brand (e portano la mappa sul suo dot, vedi
    // focusBrand). E' il pezzo che risponde a "dove sta quello che ho
    // cercato": l'opacizzazione dice QUALI sono i risultati, ma se il dot o
    // la card stanno sotto la piega non dice DOVE, e restava da cercarli
    // scorrendo. Compare solo a filtro attivo: senza filtro sarebbe l'elenco
    // di tutti i marchi, che e' gia' la pagina.
    resultsHaveFocus(node) {
      return !!(node && this.filterResultsEl && this.filterResultsEl.contains(node));
    }

    // Ridisegna l'elenco a partire dalla selezione corrente. Un solo posto
    // decide se si vede: serve una ricerca scritta E il campo attivo.
    refreshFilterResults() {
      if (!this.filterResultsEl) return;
      if (!this.filterQuery || !this.searchFocused || !this.matchingIds) {
        this.renderFilterResults([]);
        return;
      }
      this.renderFilterResults(
        Array.from(this.matchingIds)
          .map((id) => this.brandsById.get(id))
          .filter(Boolean)
      );
    }

    renderFilterResults(brands) {
      if (!this.filterResultsEl) return;
      this.filterResultsEl.innerHTML = '';
      this.filterResultsEl.hidden = brands.length === 0;
      if (!brands.length) return;

      // role="group" e non role="list": le righe sono <button>, e su un
      // <button> il role="listitem" che serviva a completare la lista NON
      // si aggiunge al ruolo nativo, lo SOSTITUISCE — a uno screen reader
      // le righe venivano annunciate come voci di un elenco, senza piu'
      // dire che si possono premere. Un gruppo etichettato tiene insieme
      // le righe lasciando a ciascuna il proprio ruolo di pulsante (e
      // accetta come figlio anche la riga "e altre N", che dentro una
      // lista non era un figlio valido). Quante sono lo dice gia' il
      // conteggio sotto la barra, che e' aria-live.
      this.filterResultsEl.setAttribute('role', 'group');
      this.filterResultsEl.setAttribute('aria-label', t('filterResults'));

      // Ordine alfabetico: e' l'unico in cui si sa dove guardare quando si
      // ha gia' in mente il nome che si sta cercando.
      const ordered = brands.slice().sort((a, b) => a.label.localeCompare(b.label, LANG));

      ordered.slice(0, RESULT_LIST_LIMIT).forEach((brand) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'manifatture-search__result';
        row.dataset.manifattureResult = '';
        row.dataset.brandId = brand.id;

        const name = document.createElement('span');
        name.className = 'manifatture-search__result-name';
        name.textContent = (brand.flag ? `${brand.flag} ` : '') + brand.label;
        row.appendChild(name);

        const place = brandPlace(brand);
        if (place) {
          const meta = document.createElement('span');
          meta.className = 'manifatture-search__result-meta';
          meta.textContent = place;
          row.appendChild(meta);
        }

        // Freccia quando il brand, in questo momento, sta fuori dallo
        // schermo: dice in anticipo che il risultato e' altrove e che
        // toccando la riga la pagina si sposta. Senza, la riga sembrava
        // un'etichetta e non una scorciatoia — soprattutto per i brand
        // internazionali, che stanno in fondo alla pagina.
        const verso = this.offscreenDirection(brand.id);
        if (verso) {
          const arrow = document.createElement('span');
          arrow.className = 'manifatture-search__result-arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = verso === 'giu' ? '↓' : '↑';
          row.appendChild(arrow);
        }

        this.filterResultsEl.appendChild(row);
      });

      if (ordered.length > RESULT_LIST_LIMIT) {
        const more = document.createElement('p');
        more.className = 'manifatture-search__result-more';
        more.textContent = t('filterMoreResults')(ordered.length - RESULT_LIST_LIMIT);
        this.filterResultsEl.appendChild(more);
      }
    }

    // Toglie l'opacizzazione senza toccare nient'altro: filtri compilati,
    // elenco dei risultati e segno sul dot restano. Serve al click sul vuoto
    // della mappa: trovato quello che si cercava, si vuole tornare a
    // guardare la mappa intera senza perdere la ricerca — e senza doverla
    // riscrivere per rivedere il risultato, che basta ricliccare nell'elenco.
    // Torna da sola appena si tocca un filtro o si clicca un risultato.
    muteHighlight() {
      // Senza un filtro attivo non c'e' niente da sospendere.
      if (this.highlightMuted || !this.matchingIds) return false;
      this.highlightMuted = true;
      this.applyFilterHighlight();

      // L'elenco NON si riapre da solo: e' la tendina del campo di ricerca e
      // il campo qui non e' attivo (si e' appena cliccato sulla mappa). La
      // via per tornare al brand trovato e' rimetterci sopra il cursore, che
      // lo fa ricomparire.
      return true;
    }

    unmuteHighlight() {
      if (!this.highlightMuted) return;
      this.highlightMuted = false;
      this.applyFilterHighlight();
    }

    // 'giu' / 'su' se il brand e' fuori dalla parte di schermo utile in
    // questo momento, stringa vuota se e' gia' sotto gli occhi. La misura e'
    // presa quando si costruisce l'elenco e non viene aggiornata scorrendo:
    // e' un'indicazione, non uno strumento, e una freccia che cambia sotto
    // le dita mentre si scorre sarebbe piu' fastidiosa che utile.
    offscreenDirection(brandId) {
      const el = this.elementForBrand(brandId);
      if (!el) return '';
      const rect = el.getBoundingClientRect();
      if (rect.bottom < stickyHeaderHeight()) return 'su';
      if (rect.top > window.innerHeight) return 'giu';
      return '';
    }

    // Porta a schermo il dot di un brand e lo fa pulsare. Serve dopo il
    // click su una riga dei risultati: la scheda si apre, ma quando la si
    // chiude l'occhio deve sapere dove tornare, invece di ripartire da capo
    // su una mappa di puntini opacizzati.
    dotForBrand(brandId) {
      return Array.from(this.querySelectorAll('[data-manifatture-dot]')).find((el) =>
        (el.dataset.brandIds || '').split(' ').includes(brandId)
      );
    }

    // Dove sta un brand a schermo: il suo dot sulla mappa se e' americano,
    // la sua card se e' internazionale. Le card sono l'unica forma in cui i
    // brand internazionali esistono in pagina, e senza questo un risultato
    // internazionale non portava da nessuna parte — il click non faceva
    // proprio niente.
    elementForBrand(brandId) {
      return (
        this.dotForBrand(brandId) ||
        this.querySelector(`[data-manifatture-card][data-brand-id="${brandId}"]`)
      );
    }

    // I rettangoli a schermo di un insieme di brand: i dot (mappa nazionale
    // o card regionali mobile) e le card dei brand internazionali. Si scarta
    // cio' che non occupa spazio — la mappa nascosta dal layout dell'altra
    // soglia tiene comunque i propri dot nel DOM, e un rettangolo a zero
    // trascinerebbe l'unione su un punto che nessuno sta guardando.
    rectsForBrands(brandIds) {
      const wanted = brandIds instanceof Set ? brandIds : new Set(brandIds || []);
      if (!wanted.size) return [];
      const rects = [];
      const consider = (el, ids) => {
        if (!ids.some((id) => wanted.has(id))) return;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;
        rects.push(rect);
      };
      this.querySelectorAll('[data-manifatture-dot]').forEach((el) =>
        consider(el, (el.dataset.brandIds || '').split(' '))
      );
      this.querySelectorAll('[data-manifatture-card][data-brand-id]').forEach((el) =>
        consider(el, [el.dataset.brandId])
      );
      return rects;
    }

    // Porta a schermo i risultati di un filtro quando sono piu' d'uno: si
    // prende il rettangolo che li contiene tutti e lo si tratta come un
    // bersaglio unico. Se non ci sta nella fascia utile — un filtro largo
    // copre mezza mappa — scrollRectIntoView allinea in cima, che e' il
    // massimo mostrabile e comunque il punto da cui si comincia a guardare.
    focusBrandGroup(brandIds) {
      const rects = this.rectsForBrands(brandIds);
      if (!rects.length) return;
      const top = Math.min.apply(null, rects.map((rect) => rect.top));
      const bottom = Math.max.apply(null, rects.map((rect) => rect.bottom));
      this.scrollRectIntoView({ top: top, bottom: bottom, height: bottom - top });
    }

    focusBrand(brandId) {
      const el = this.elementForBrand(brandId);
      if (!el) return;

      // Dal click alla marcatura del dot passano lo scorrimento della pagina
      // e l'animazione di apertura del cluster: quasi un secondo, in cui si
      // puo' benissimo aver gia' cambiato ricerca. Il gettone fa scadere il
      // salto vecchio invece di lasciarlo arrivare in ritardo e aprire il
      // cluster di un brand che non si sta piu' cercando.
      const token = (this.focusToken += 1);

      this.scrollTargetIntoView(el);

      // Il cluster si apre DOPO che la pagina ha finito di scorrere: si apre
      // sopra o sotto al dot a seconda dello spazio libero, e decidendolo
      // sulla posizione di partenza finiva mezzo fuori schermo (o dietro
      // all'header) una volta arrivati.
      afterScroll(() => {
        if (token !== this.focusToken) return;
        let current = this.elementForBrand(brandId);
        if (!current) return;

        // Una zona aperta sul desktop che non contiene il brand cercato va
        // richiusa: il suo dot potrebbe essere tra quelli nascosti sotto ai
        // membri della zona, e si segnerebbe un punto che non si vede.
        if (
          this.desktopZone &&
          current.hasAttribute('data-manifatture-dot') &&
          !current.dataset.regionKey &&
          !this.desktopZone.indices.has(Number(current.dataset.groupIndex))
        ) {
          this.collapseDesktopZone();
          current = this.elementForBrand(brandId);
          if (!current) return;
        }

        // Un brand internazionale non ha un dot: si segna la sua card.
        if (!current.hasAttribute('data-manifatture-dot')) {
          this.markCard(current);
          return;
        }
        // L'elenco di un cluster aperto da una ricerca precedente resterebbe
        // a schermo, magari lontanissimo dal dot che si sta guardando ora.
        this.hideClusterPopover();
        (this.regionCanvases || []).forEach((canvas) => this.hideClusterPopover(canvas.popoverEl));
        // Se il brand cercato sta dentro un dot che ne raccoglie altri, il
        // cluster si apre esattamente come si aprirebbe al tap: elenco sulla
        // mappa singola, ventaglio nelle card regionali mobile. Altrimenti si
        // finiva su un puntino che pulsa senza poter vedere quale dei brand
        // fusi li' sotto fosse quello cercato.
        this.openClusterAt(current);
        // Un ventaglio ridisegna i dot della card: quello del brand cercato
        // ora e' un elemento nuovo, e va ripescato prima di segnarlo.
        let target = this.dotForBrand(brandId) || current;

        // Il ventaglio separa i LUOGHI, non i brand: aprendo una tasca densa
        // (Seattle e Bothell fuse per vicinanza) si torna ai due luoghi, ma
        // Seattle resta un dot con due marchi dentro e del brand cercato
        // non si vedeva ancora niente. Se dopo l'apertura il dot ne raccoglie
        // ancora piu' d'uno, si apre anche il suo elenco. Un solo passaggio
        // in piu': dopo l'elenco non c'e' altro da aprire.
        if (target !== current && (target.dataset.brandIds || '').split(' ').length > 1) {
          this.openClusterAt(target);
          target = this.dotForBrand(brandId) || target;
        }

        this.markDot(target, prefersReducedMotion());
      });
    }

    // Porta a schermo il dot (o la card) di un singolo brand: e' il caso
    // di chi ha appena cercato UNA cosa e vuole sapere dove sta.
    scrollTargetIntoView(el) {
      const isCluster = (el.dataset.brandIds || '').split(' ').length > 1;
      // Un cluster apre il proprio elenco SOPRA al dot: gli si lascia lo
      // spazio per starci, altrimenti si apre e non si vede.
      this.scrollRectIntoView(el.getBoundingClientRect(), isCluster ? 120 : 0);
    }

    // Il calcolo vero, su un rettangolo qualsiasi: cosi' lo stesso
    // atterraggio vale sia per un singolo bersaglio sia per l'insieme dei
    // risultati di un filtro, che un elemento solo non e' (focusBrandGroup).
    //
    // Non e' uno scorrimento qualsiasi: e' la risposta a "dove sta quello
    // che ho cercato", quindi il bersaglio deve ATTERRARE IN UN POSTO
    // GUARDABILE, non semplicemente rientrare per un pelo. Prima qui c'era
    // uno scorrimento minimo: ci si fermava appena il bersaglio toccava il
    // bordo. Misurato sul rendering reale, il risultato finiva
    // sistematicamente incollato al fondo dello schermo (fra l'81% e il 92%
    // dell'altezza, cioe' su un telefono dietro alla barra del browser), e
    // cio' che riempiva la vista era tutto quello che gli sta sopra:
    // filtrando per un brand internazionale si vedevano le prime card e non
    // quella cercata. Peggio: essendo il bersaglio tecnicamente "in vista",
    // cambiando valore alla stessa tendina lo scorrimento successivo valeva
    // zero e la pagina non si muoveva piu' affatto, come se l'auto scroll si
    // fosse rotto. Prima ancora si centrava sempre il dot al 42%
    // dell'altezza: su mobile, dove la mappa comincia poco sotto ai filtri,
    // quel calcolo scorreva verso l'alto oltre la barra di ricerca, e ci si
    // ritrovava sopra ai filtri dopo aver cliccato un risultato.
    scrollRectIntoView(rect, headroom) {
      const safeTop = stickyHeaderHeight() + 24 + (headroom || 0);
      // Con la scheda aperta come foglio dal basso, la meta' inferiore dello
      // schermo e' coperta e non conta come spazio utile.
      const sheetOpen = this.panel && !this.panel.hidden && isSheetLayout();
      const safeBottom = (sheetOpen ? window.innerHeight * 0.5 : window.innerHeight) - 24;
      const usable = Math.max(0, safeBottom - safeTop);

      // Si lascia stare solo quello che e' gia' dove lo si vorrebbe portare:
      // tutto dentro la fascia utile E nella sua parte alta. Un bersaglio
      // che sta nella meta' bassa viene comunque risistemato, ed e' proprio
      // il caso che prima passava per "gia' a posto".
      const tuttoDentro = rect.top >= safeTop && rect.bottom <= safeBottom;
      const nellaParteAlta = rect.top <= safeTop + usable * FOCUS_COMFORT_LIMIT;
      if (tuttoDentro && nellaParteAlta) return;

      // Un bersaglio piu' alto della fascia utile non puo' stare al terzo:
      // si allinea in cima, che e' il massimo che si puo' mostrare.
      const atterraggio = safeTop + Math.max(0, (usable - rect.height) * FOCUS_LANDING_RATIO);
      const delta = rect.top - atterraggio;
      if (Math.abs(delta) < 1) return;

      window.scrollBy({ top: delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    // Apre il cluster che contiene il dot, se e' un cluster: la stessa cosa
    // che farebbe un tap sul dot (vedi handleDotActivate), meno il caso del
    // brand singolo — li' il tap aprirebbe la scheda, che e' proprio cio'
    // che qui non si vuole.
    openClusterAt(dot) {
      const index = Number(dot.dataset.groupIndex);
      const regionKey = dot.dataset.regionKey;

      if (regionKey) {
        const canvas = (this.regionCanvases || []).find((c) => c.key === regionKey);
        if (!canvas) return;
        const group = canvas.current[index];
        if (!group || group.brands.length === 1) return;
        if (group.sourceGroups && group.sourceGroups.length > 1) {
          // toggleRegionCluster e' un interruttore: su un cluster gia'
          // esploso lo richiuderebbe.
          if (canvas.explodedGroup !== group) this.toggleRegionCluster(canvas, index);
          return;
        }
        this.showClusterPopover(dot, group, canvas.popoverEl, canvas.wrapperEl);
        return;
      }

      const group = (this.usaGroups || [])[index];
      if (!group || group.brands.length === 1) return;
      if (group.sourceGroups && group.sourceGroups.length > 1) {
        this.openDesktopZone(index);
        return;
      }
      this.showClusterPopover(dot, group);
    }

    // Segna il dot raggiunto da una riga dei risultati con DUE cose: un alone
    // fisso, che resta finche' non si cerca altro, e una pulsazione che
    // richiama l'occhio nel momento in cui la pagina arriva li'.
    //
    // L'alone permanente e' la parte che conta: la sola pulsazione dura
    // quanto lo scorrimento della pagina, e chi guarda arriva a cose fatte
    // trovando un puntino uguale agli altri. L'alone risponde a "quale
    // era?" anche mezzo minuto dopo.
    //
    // Sono elementi a se' e non stili del dot: il dot ha gia' una
    // transizione sul raggio (l'ingrandimento al passaggio del mouse) e
    // animarlo qui significherebbe litigare con quella.
    markDot(dot, reduceMotion) {
      // Un dot appena rivelato dall'apertura di un cluster sta ancora
      // scivolando dal centro del gruppo verso la sua vera posizione (classe
      // --burst, transizione su cx/cy): in questo momento i suoi attributi
      // sono ancora quelli di PARTENZA, e alone e anello copiandoli
      // restavano piantati dove stava il cluster mentre il dot se ne andava
      // altrove. Si aspetta la fine della transizione — con un tetto di
      // tempo, perche' se la transizione non parte transitionend non arriva.
      if (dot.classList.contains('manifatture-map__dot--burst')) {
        const token = this.focusToken;
        const quandoFermo = () => {
          dot.removeEventListener('transitionend', quandoFermo);
          window.clearTimeout(this.markTimeout);
          if (token !== this.focusToken) return;
          this.placeDotMark(dot, reduceMotion);
        };
        dot.addEventListener('transitionend', quandoFermo);
        window.clearTimeout(this.markTimeout);
        this.markTimeout = window.setTimeout(quandoFermo, 450);
        return;
      }
      this.placeDotMark(dot, reduceMotion);
    }

    placeDotMark(dot, reduceMotion) {
      const group = dot.parentNode;
      if (!group) return;
      this.clearDotMark();

      const radius = Number(dot.getAttribute('r')) || 6;
      const cx = dot.getAttribute('cx');
      const cy = dot.getAttribute('cy');

      const halo = document.createElementNS(SVG_NS, 'circle');
      halo.setAttribute('class', 'manifatture-map__halo');
      halo.setAttribute('cx', cx);
      halo.setAttribute('cy', cy);
      halo.setAttribute('r', radius * 2.1);
      // Prima del dot nel gruppo: l'alone gli sta dietro, non sopra.
      group.insertBefore(halo, group.firstChild);
      this.haloEl = halo;

      if (reduceMotion) return;

      const ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('class', 'manifatture-map__pulse');
      ring.setAttribute('cx', cx);
      ring.setAttribute('cy', cy);
      ring.setAttribute('r', radius);
      group.appendChild(ring);
      this.pulseEl = ring;

      // Rimosso a mano invece che su animationend: se la mappa viene
      // ridisegnata mentre l'anello e' a schermo (soglia mobile/desktop),
      // l'evento non arriverebbe mai.
      this.pulseTimeout = window.setTimeout(() => {
        if (ring.parentNode) ring.parentNode.removeChild(ring);
        if (this.pulseEl === ring) this.pulseEl = null;
      }, 3200);
    }

    // Equivalente dell'alone sul dot, per una card internazionale: un
    // contorno che resta finche' non si cerca altro, piu' un lampeggio breve
    // che richiama l'occhio nel momento in cui la pagina arriva li'. Serve
    // esattamente a dire "il risultato e' questo, piu' in basso": senza, di
    // una card in fondo all'elenco non si accorgeva nessuno.
    markCard(card) {
      this.clearDotMark();
      card.classList.add('is-pinpointed');
      this.markedCard = card;
    }

    clearDotMark() {
      window.clearTimeout(this.pulseTimeout);
      window.clearTimeout(this.markTimeout);
      if (this.markedCard) this.markedCard.classList.remove('is-pinpointed');
      this.markedCard = null;
      [this.pulseEl, this.haloEl].forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
      this.pulseEl = null;
      this.haloEl = null;
    }

    resetSectionFilters() {
      this.filterQuery = '';
      window.clearTimeout(this.filterTimeout);
      if (this.searchInput) this.searchInput.value = '';
      this.filterGroups.forEach(({ select }) => {
        select.value = '';
      });
      this.applySectionFilters();
    }

    renderMap(baseGroups) {
      if (!this.dotsGroup || !this.labelsGroup) return;

      this.lastRenderWasMobile = isMobileViewport();

      // Solo su mobile: invece della mappa nazionale singola, una card per
      // regione (vedi renderMobileRegions/USA_REGIONS) — sostituisce del
      // tutto il vecchio comportamento "un'unica mappa compressa con
      // clusterByProximity + esplosione al tap", che restava comunque
      // troppo stretta per essere leggibile. Il desktop non e' toccato:
      // riceve sempre baseGroups invariato sulla mappa singola originale.
      if (this.lastRenderWasMobile) {
        if (this.mapWrapperEl) this.mapWrapperEl.hidden = true;
        // Nascondere il wrapper non basta: i dot che la mappa nazionale
        // aveva gia' disegnato (chi arriva da desktop e restringe la
        // finestra oltre la soglia) restano nel DOM, invisibili ma ancora
        // trovabili da querySelectorAll. dotForBrand() ne pescava uno —
        // sono i primi in pagina — e il salto di una ricerca finiva li':
        // scrollTargetIntoView misurava un rettangolo a zero, la pagina non
        // si muoveva e l'alone veniva appeso a un dot che non si vede.
        // Cliccare un risultato, dopo quel passaggio, non faceva piu'
        // niente. Si svuotano quindi insieme allo stato che li accompagna
        // (this.usaGroups, che su mobile non viene piu' popolato — vedi
        // handleDotActivate): la mappa nazionale torna inerte com'e' gia'
        // per il resto della section, e il prossimo ritorno al desktop la
        // ridisegna comunque da capo (paintGroups).
        this.dotsGroup.innerHTML = '';
        this.labelsGroup.innerHTML = '';
        this.usaGroups = [];
        this.renderMobileRegions(baseGroups);
        return;
      }

      if (this.mapWrapperEl) this.mapWrapperEl.hidden = false;
      if (this.mapRegionsEl) {
        this.mapRegionsEl.hidden = true;
        this.mapRegionsEl.innerHTML = '';
      }
      this.regionCanvases = [];
      // Prima il layout con tutti i punti separati, come e' sempre stato: e'
      // su quello che desktopZoneGroups misura dove le etichette non trovano
      // posto. Se non nasce nessuna zona lo stesso layout si disegna cosi'
      // com'e', senza ricalcolarlo.
      this.desktopZone = null;
      const basePoints = layoutPoints({ groups: baseGroups, bounds: DEFAULT_BOUNDS, labelsGroup: this.labelsGroup });
      const groups = desktopZoneGroups(baseGroups, basePoints, DEFAULT_BOUNDS);
      const points =
        groups === baseGroups
          ? basePoints
          : layoutPoints({ groups, bounds: DEFAULT_BOUNDS, labelsGroup: this.labelsGroup, crowdingFrom: basePoints });
      this.desktopCollapsed = groups;
      this.desktopCollapsedPoints = this.paintGroups(groups, undefined, points);
    }

    // Disegna dot/etichette della mappa nazionale singola (desktop) dentro
    // this.dotsGroup/this.labelsGroup, sull'intero viewBox (DEFAULT_BOUNDS).
    // Guscio sottile attorno a paintPointsInto (condivisa con le card
    // regionali mobile, vedi paintRegionCanvas). `points` opzionale: un
    // layout gia' calcolato da disegnare cosi' com'e'. Restituisce i punti
    // disegnati.
    paintGroups(groups, burst, points) {
      this.usaGroups = groups;
      const painted = paintPointsInto({
        dotsGroup: this.dotsGroup,
        labelsGroup: this.labelsGroup,
        groups,
        bounds: DEFAULT_BOUNDS,
        burst,
        points,
      });
      // I dot appena creati nascono tutti in chiaro: se un filtro e' attivo
      // va riapplicato qui, altrimenti un ridisegno (soglia mobile/desktop)
      // lo cancellerebbe da schermo pur restando attivo nella barra.
      this.applyFilterHighlight();
      return painted;
    }

    // Apre la zona di indice dato (in this.usaGroups) sulla mappa desktop:
    // la zona lascia il posto ai suoi membri, disposti da zoneOpenLayout
    // dentro un alone tratteggiato. Il resto della mappa si attenua, e i
    // punti che finirebbero sotto i membri spariscono finche' la zona resta
    // aperta. Una sola zona aperta alla volta.
    openDesktopZone(index) {
      const zone = this.usaGroups[index];
      if (!zone || !zone.sourceGroups || zone.sourceGroups.length < 2) return;
      if (this.desktopZone) {
        if (this.desktopZone.group === zone) return;
        this.collapseDesktopZone();
      }
      const zoneIndex = this.desktopCollapsed.indexOf(zone);
      if (zoneIndex === -1 || !this.desktopCollapsedPoints) return;

      this.hideClusterPopover();
      // Come per il ventaglio su mobile: mentre si sceglie un membro si
      // precaricano le schede dei marchi della zona.
      prefetchVendors(this.vendorsForGroup(zone), true);

      const rows = zone.sourceGroups.map((group) => ({ group, labelText: pointLabelText(group, false) }));
      measureLabelWidths(rows, this.labelsGroup);
      rows.forEach((row) => {
        if (row.width === undefined || row.width <= 3) row.width = estimateLabelWidth(row.labelText, 1);
      });
      const outsiders = this.desktopCollapsedPoints.filter((point) => point.group !== zone);
      const layout = zoneOpenLayout(zone, rows, outsiders, DEFAULT_BOUNDS);

      // Gruppi e punti della mappa con la zona aperta: i membri prendono il
      // posto della zona, gli altri punti restano esattamente dov'erano, con
      // la stessa etichetta (cambia solo l'indice).
      const members = layout.rows.map((row) => ({ ...row.group, x: row.x, y: row.y }));
      const before = this.desktopCollapsed.slice(0, zoneIndex);
      const after = this.desktopCollapsed.slice(zoneIndex + 1);
      const expanded = before.concat(members, after);
      const pointOf = new Map(this.desktopCollapsedPoints.map((point) => [point.group, point]));
      const indices = new Set();
      const points = expanded.map((group, index) => {
        const memberIndex = index - zoneIndex;
        if (memberIndex >= 0 && memberIndex < members.length) {
          indices.add(index);
          const row = layout.rows[memberIndex];
          return {
            index,
            x: group.x,
            y: group.y,
            labelText: row.labelText,
            width: row.width,
            labelOffset: row.labelOffset,
            group,
          };
        }
        return { ...pointOf.get(group), index };
      });

      this.desktopZone = { group: zone, indices };
      this.paintGroups(expanded, { fromX: zone.x, fromY: zone.y, indices, elements: [] }, points);

      // L'alone sta sotto ai dot: si inserisce in testa al loro gruppo, dopo
      // il disegno (paintPointsInto lo svuota).
      const backdrop = document.createElementNS(SVG_NS, 'g');
      backdrop.setAttribute('class', 'manifatture-map__zone');
      const halo = document.createElementNS(SVG_NS, 'circle');
      halo.setAttribute('class', 'manifatture-map__zone-halo');
      halo.setAttribute('cx', layout.halo.x);
      halo.setAttribute('cy', layout.halo.y);
      halo.setAttribute('r', layout.halo.r);
      backdrop.appendChild(halo);
      this.dotsGroup.insertBefore(backdrop, this.dotsGroup.firstChild);

      // Il resto della mappa: attenuato, oppure nascosto se finirebbe sotto
      // un membro. Si riconosce dai marchi che porta (data-brand-ids), che
      // dot, badge ed etichetta di uno stesso punto condividono.
      const hiddenIds = new Set();
      layout.hidden.forEach((point) => hiddenIds.add(point.group.brands.map((brand) => brand.id).join(' ')));
      const memberIds = new Set(members.map((group) => group.brands.map((brand) => brand.id).join(' ')));
      [this.dotsGroup, this.labelsGroup].forEach((layer) => {
        layer.querySelectorAll('[data-brand-ids]').forEach((el) => {
          const ids = el.dataset.brandIds;
          if (memberIds.has(ids)) return;
          el.classList.add(hiddenIds.has(ids) ? 'is-zone-hidden' : 'is-zone-faded');
        });
      });
    }

    collapseDesktopZone() {
      if (!this.desktopZone) return;
      this.desktopZone = null;
      this.desktopCollapsedPoints = this.paintGroups(this.desktopCollapsed, undefined, this.desktopCollapsedPoints);
    }

    // Costruisce le card regionali mobile (vedi USA_REGIONS/renderMap):
    // smista baseGroups (gia' fusi per citta') per regione, e per ognuna
    // che ha almeno un brand crea una card con titolo + una mini-mappa
    // ritagliata sulla sua sola area (buildRegionCanvas). Sostituisce del
    // tutto la mappa singola su mobile (mapWrapperEl viene nascosto da
    // renderMap prima di chiamare questo metodo).
    renderMobileRegions(baseGroups) {
      if (!this.mapRegionsEl) return;
      this.hideClusterPopover();
      this.mapRegionsEl.innerHTML = '';
      this.mapRegionsEl.hidden = false;

      const partitioned = partitionGroupsByRegion(baseGroups);
      this.regionCanvases = partitioned.map((region) => this.buildRegionCanvas(region));
    }

    // Crea il DOM di una singola card regionale (titolo + svg ritagliato +
    // popover proprio) e ci disegna dentro i brand della regione, fondendo
    // per vicinanza (clusterByProximity) i soli dot che restano troppo
    // vicini A SCHERMO in questa card: la soglia non e' piu' quella fissa
    // della vecchia mappa singola mobile, ma la stessa distanza fisica
    // riscalata sul viewBox della regione (mobileClusterDistance/
    // MOBILE_CLUSTER_BASE_DISTANCE). Il ritaglio regionale riduce
    // l'affollamento medio ma non elimina le zone dense (es. il distretto
    // tessile della Carolina del Nord nella regione "Sud", che continua a
    // fondersi in un unico dot).
    // Restituisce l'oggetto "canvas" con tutto lo stato serve a
    // toggleRegionCluster/collapseRegionCluster per gestire l'esplosione al
    // tap in modo indipendente da ogni altra card.
    buildRegionCanvas(region) {
      const card = document.createElement('div');
      card.className = 'manifatture-map__region';

      const regionTitle = t('usaRegions')[region.key] || region.title;

      const title = document.createElement('h4');
      title.className = 'manifatture-map__region-title';
      title.textContent = regionTitle;
      // Il titolo di base resta nel dataset: a filtro attivo il testo
      // diventa "Sud · 2" e va poi ricostruito da qui, non riletto da
      // se' stesso.
      title.dataset.regionTitle = regionTitle;
      card.appendChild(title);

      const wrapper = document.createElement('div');
      wrapper.className = 'manifatture-map__region-wrapper';

      const { x1, y1, x2, y2 } = region.bounds;
      const width = x2 - x1;
      const height = y2 - y1;

      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'manifatture-map__region-svg');
      svg.setAttribute('viewBox', `${x1} ${y1} ${width} ${height}`);
      svg.style.aspectRatio = `${width} / ${height}`;
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', t('regionMapLabel')(regionTitle));

      const statesGroup = document.createElementNS(SVG_NS, 'g');
      statesGroup.setAttribute('class', 'manifatture-map__states');
      if (this.statesGroup) {
        // Cloniamo i path degli stati dalla mappa nazionale (nascosta ma
        // sempre presente nel DOM): cloneNode non richiede che l'originale
        // sia renderizzato, a differenza di getBBox() — per questo i
        // bounds di ogni regione sono precalcolati staticamente in
        // USA_REGIONS invece di essere misurati qui a runtime.
        // I path si indicizzano una volta sola per section invece di
        // cercarli con un querySelector per ogni stato di ogni card (48
        // ricerche nel DOM a ogni costruzione delle card).
        if (!this.statePathsByName) {
          this.statePathsByName = new Map();
          this.statesGroup.querySelectorAll('[data-state-name]').forEach((path) => {
            this.statePathsByName.set(path.dataset.stateName, path);
          });
        }
        const frame = document.createDocumentFragment();
        region.states.forEach((stateName) => {
          const original = this.statePathsByName.get(stateName);
          if (original) frame.appendChild(original.cloneNode(true));
        });
        statesGroup.appendChild(frame);
      }
      svg.appendChild(statesGroup);

      const dotsGroup = document.createElementNS(SVG_NS, 'g');
      const labelsGroup = document.createElementNS(SVG_NS, 'g');
      svg.appendChild(dotsGroup);
      svg.appendChild(labelsGroup);

      wrapper.appendChild(svg);

      const popoverEl = document.createElement('div');
      popoverEl.className = 'manifatture-map__cluster-popover';
      popoverEl.hidden = true;
      wrapper.appendChild(popoverEl);

      card.appendChild(wrapper);
      this.mapRegionsEl.appendChild(card);

      // fontScale compensa il viewBox piu' ampio di questa regione rispetto
      // al riferimento "northeast" (vedi MOBILE_LABEL_FONT_REFERENCE_WIDTH):
      // tutte le card renderizzano alla stessa larghezza CSS, quindi una
      // regione con piu' unita' di viewBox ha bisogno di un font-size (in
      // unita' di viewBox) proporzionalmente piu' grande per apparire della
      // stessa dimensione fisica reale sullo schermo.
      const fontScale = width / MOBILE_LABEL_FONT_REFERENCE_WIDTH;

      const canvas = {
        key: region.key,
        bounds: region.bounds,
        fontScale,
        cardEl: card,
        titleEl: title,
        // Tutti i brand della regione, per sapere quanti ne lascia il filtro
        // corrente senza dover contare i dot a schermo (un dot puo' valere
        // piu' brand).
        brandIds: region.groups.reduce(
          (ids, group) => ids.concat(group.brands.map((brand) => brand.id)),
          []
        ),
        svg,
        statesGroup,
        dotsGroup,
        labelsGroup,
        popoverEl,
        wrapperEl: wrapper,
        collapsed: [],
        current: [],
        // Cache per card del test di terraferma per punto (arrotondato
        // all'unita' di viewBox): viene interrogato molte volte quando si
        // provano piu' ventagli alternativi al tap su un cluster. Le
        // larghezze misurate del testo non stanno piu' qui ma in una cache
        // di pagina (LABEL_WIDTH_CACHE), condivisa anche con la mappa
        // desktop.
        landCache: new Map(),
        explodedGroup: null,
        explodedIndices: null,
      };

      const clusterDistance = mobileClusterDistance(fontScale);
      const groups = clusterByProximity(region.groups, clusterDistance * clusterDistance);
      separateOverlappingDots(groups, fontScale, region.bounds);
      canvas.collapsed = groups;
      this.paintRegionCanvas(canvas, groups);
      return canvas;
    }

    // Equivalente di paintGroups, ma per una singola card regionale: scrive
    // nel dotsGroup/labelsGroup propri della card (non quelli della mappa
    // nazionale) e usa il ritaglio (bounds) della regione invece
    // dell'intero viewBox.
    paintRegionCanvas(canvas, groups, burst, points) {
      canvas.current = groups;
      // I punti effettivamente disegnati restano a disposizione della card:
      // labelRectsFor li usa per sapere dove sono le etichette gia' a
      // schermo quando si decide dove aprire un ventaglio.
      canvas.points = paintPointsInto({
        points,
        dotsGroup: canvas.dotsGroup,
        labelsGroup: canvas.labelsGroup,
        groups,
        bounds: canvas.bounds,
        burst,
        regionKey: canvas.key,
        labelFontSize: BASE_LABEL_FONT_SIZE * canvas.fontScale,
        badgeFontSize: BASE_BADGE_FONT_SIZE * canvas.fontScale,
        // Solo i membri del cluster ATTUALMENTE esploso (canvas.explodedIndices)
        // sono stati ricollocati a ventaglio (explodePositions) e
        // hanno quindi bisogno di ignorare il proprio override fisso (vedi
        // placeLabels): gli altri punti della card (es. "Healthknit" mentre
        // esplode il cluster da 8 vicino) restano nella loro posizione vera e
        // devono continuare a usare l'override come da stato collassato.
        disableOverrideIndices: canvas.explodedIndices || undefined,
      });
      // Come in paintGroups: l'esplosione di un cluster al tap ridisegna i
      // dot della card, che vanno riopacizzati secondo il filtro corrente.
      this.applyFilterHighlight();
    }

    // Esplode (o richiude) un cluster per vicinanza dentro UNA SOLA card
    // regionale (ventaglio via explodePositions, identita' per riferimento
    // all'oggetto invece che per indice, si riparte sempre da
    // canvas.collapsed — stessi accorgimenti usati per la vecchia
    // esplosione sulla mappa singola, ora sostituita dalle card regionali)
    // ma scoped al canvas passato: card diverse possono restare esplose
    // contemporaneamente senza interferire tra loro, dato che sono
    // visivamente separate (a differenza della vecchia mappa singola, dove
    // due esplosioni contemporanee sullo stesso canvas sarebbero state
    // confuse).
    // Rettangoli delle etichette di un layout dato, escluse quelle del
    // cluster che sta per esplodere (che spariranno). Servono a
    // explodePositions per non far atterrare un dot esploso sopra il testo di
    // un altro punto.
    //
    // Il layout va passato esplicitamente e dev'essere quello COLLASSATO, non
    // quello attualmente a schermo: se nella stessa card e' gia' esploso un
    // altro cluster, le etichette dei suoi membri sono a schermo ma stanno
    // per sparire (esplodere un cluster richiude sempre l'altro, il ventaglio
    // si costruisce su canvas.collapsed) — usarle come ostacoli farebbe
    // scegliere il ventaglio in base a testi che non esisteranno piu'.
    labelRectsFor(points, scale, excludedGroup) {
      if (!points) return [];
      return points
        .filter((point) => point.group !== excludedGroup && point.labelText && point.labelOffset)
        .map((point) =>
          labelRect(
            point,
            point.labelOffset,
            point.width !== undefined && point.width > 3 ? point.width : estimateLabelWidth(point.labelText, scale),
            14 * scale,
            4 * scale
          )
        );
    }

    // Elemento SVG del dot di indice dato dentro una card regionale: serve
    // per ancorare il popover al dot giusto quando il tap non esplode.
    dotElementFor(canvas, index) {
      return canvas.dotsGroup.querySelector(`[data-group-index="${index}"]`);
    }

    // Calcola "a secco" il layout di un insieme di gruppi in questa card
    // (posizioni + etichette), senza disegnarlo: serve a confrontare due
    // alternative prima di sceglierne una (vedi toggleRegionCluster).
    trialLayout(canvas, groups, disableOverrideIndices) {
      return layoutPoints({
        groups,
        bounds: canvas.bounds,
        regionKey: canvas.key,
        labelFontSize: BASE_LABEL_FONT_SIZE * canvas.fontScale,
        disableOverrideIndices,
        labelsGroup: canvas.labelsGroup,
      });
    }

    // Test "questo punto cade sulla terraferma disegnata in questa card?",
    // usato dall'esplosione di un cluster per non mandare un dot in mezzo al
    // mare o oltre il confine degli stati mostrati. Usa isPointInFill sui
    // path degli stati gia' clonati nella card (vedi buildRegionCanvas), cioe'
    // la sagoma vera, non un'approssimazione rettangolare. Restituisce null
    // per la card "Altre zone", che non ha sagome proprie: li' il vincolo non
    // ha significato e semplicemente non viene applicato.
    landTester(canvas) {
      if (!canvas.svg || !canvas.statesGroup) return null;
      const paths = canvas.statesGroup.querySelectorAll('path');
      if (!paths.length) return null;
      const point = canvas.svg.createSVGPoint();
      const cache = canvas.landCache;
      return (x, y) => {
        // isPointInFill attraversa la geometria del path: con decine di
        // migliaia di interrogazioni per tap (piu' ventagli provati x piu'
        // rotazioni x piu' raggi) va memoizzato. La chiave e' la coordinata
        // PRECISA, non arrotondata: i ventagli alternativi provano in larga
        // parte gli stessi identici punti (cambia il criterio di scelta, non
        // le posizioni candidate), quindi la cache funziona lo stesso —
        // mentre arrotondare all'unita' di viewBox falsava il test dove la
        // terraferma e' sottile (verificato: mandava "The Game Headwear"
        // fuori da Long Island, larga poche unita').
        const key = `${x.toFixed(3)},${y.toFixed(3)}`;
        const cached = cache.get(key);
        if (cached !== undefined) return cached;
        point.x = x;
        point.y = y;
        for (let i = 0; i < paths.length; i++) {
          try {
            if (paths[i].isPointInFill(point)) {
              cache.set(key, true);
              return true;
            }
          } catch (e) {
            // isPointInFill non e' disponibile ovunque (o il path non e'
            // renderizzato): in quel caso il vincolo semplicemente decade,
            // invece di far fallire l'esplosione.
            return true;
          }
        }
        cache.set(key, false);
        return false;
      };
    }

    toggleRegionCluster(canvas, tappedIndex) {
      const group = canvas.current[tappedIndex];
      if (!group || !group.sourceGroups || group.sourceGroups.length < 2) return;

      if (canvas.explodedGroup === group) {
        this.collapseRegionCluster(canvas);
        return;
      }

      // Equivalente mobile dell'apertura del popover: il ventaglio di dot
      // resta a schermo mentre l'utente sceglie, e quel tempo si usa per
      // precaricare i brand che ne fanno parte.
      prefetchVendors(this.vendorsForGroup(group), true);

      const collapsedIndex = canvas.collapsed.indexOf(group);
      if (collapsedIndex === -1) return; // difensivo, non dovrebbe succedere

      this.hideClusterPopover(canvas.popoverEl);
      const obstacles = canvas.collapsed.filter((g, i) => i !== collapsedIndex);
      // Margine dal bordo del ritaglio: il raggio massimo reale di un dot in
      // questa card (un membro esploso puo' essere a sua volta un cluster
      // "stessa citta'", quindi raggio 8) piu' 2 unita' per il contorno
      // (stroke-width in component-manifatture.css). Senza, il cerchio del
      // dot sconfina oltre il bordo della card e appare tagliato.
      const inset = 8 * canvas.fontScale + 2;
      // Layout dello stato collassato: e' la base su cui si costruisce ogni
      // ventaglio (aprire un cluster richiude sempre l'altro eventualmente
      // aperto nella stessa card). Va SEMPRE ricalcolato, mai riusato da
      // canvas.points: il layout disegnato al primo paint puo' essere stato
      // calcolato quando il testo non era ancora misurabile nel DOM (l'<svg>
      // senza larghezza risolta, vedi measureLabelWidths), quindi con
      // larghezze stimate invece che reali e posizioni leggermente diverse.
      // Usarlo rendeva la decisione "esplodere o mostrare l'elenco"
      // dipendente da COSA ERA SUCCESSO PRIMA nella card: verificato che lo
      // stesso cluster denso NC dava 985 di conflitto da stato pulito
      // (elenco) e 285 dopo un'altra esplosione (ventaglio, per giunta
      // illeggibile). Ricalcolarlo costa una passata di layout e rende la
      // scelta sempre la stessa a parita' di stato visibile.
      const collapsedPoints = this.trialLayout(canvas, canvas.collapsed, undefined);
      const labelObstacles = this.labelRectsFor(collapsedPoints, canvas.fontScale, group);
      const landTester = this.landTester(canvas);
      const baseOptions = {
        separation: EXPLODE_MIN_SEPARATION * canvas.fontScale,
        clearance: EXPLODE_MIN_SEPARATION * canvas.fontScale,
        bounds: canvas.bounds,
        inset,
        obstacles,
        labelObstacles,
        dotRadius: 8 * canvas.fontScale,
        isOnLand: landTester,
      };

      // Genera qualche ventaglio alternativo e sceglie quello che, misurato
      // sul layout finale VERO (etichette comprese), lascia meno
      // sovrapposizioni. Nessuna delle due scelte in gioco — evitare o no le
      // etichette altrui, quanto allargare il ventaglio — e' giusta sempre:
      // su una tasca densa conviene scansare le etichette, su un gruppo
      // isolato conviene restare stretti attorno al centroide, e provarlo e'
      // piu' affidabile che indovinarlo con un peso tarato a mano (tentato:
      // ogni taratura che sistemava un caso ne rompeva un altro).
      const variants = [
        { labelWeight: 1 },
        { labelWeight: 0 },
        { labelWeight: 1, radiusScales: [1.4, 1.7, 2] },
        { labelWeight: 0, radiusScales: [1.4, 1.7, 2] },
      ];
      let best = null;
      const evaluate = (variant) => {
        const positions = explodePositions(group.sourceGroups, group.x, group.y, { ...baseOptions, ...variant });
        const members = group.sourceGroups.map((sourceGroup, i) => ({
          ...sourceGroup,
          x: positions[i].x,
          y: positions[i].y,
          // Direzione in cui questo membro si e' aperto rispetto al
          // centroide: l'etichetta seguira' la stessa direzione (vedi
          // labelCandidatesFor), cosi' testo e dot si aprono a ventaglio
          // insieme invece di accavallarsi verso il centro del gruppo.
          preferAngle: Math.atan2(positions[i].y - group.y, positions[i].x - group.x),
        }));
        const candidate = canvas.collapsed.slice();
        candidate.splice(collapsedIndex, 1, ...members);
        const candidateIndices = new Set();
        for (let i = collapsedIndex; i < collapsedIndex + members.length; i++) candidateIndices.add(i);
        const points = this.trialLayout(canvas, candidate, candidateIndices);
        // Il confronto tra varianti guarda entrambi i difetti che contano, e
        // li guarda MISURATI sul risultato: quanto le etichette rivelate si
        // sovrappongono, e quanti dot finirebbero fuori dalla terraferma
        // disegnata in questa card (un dot in mezzo al nulla e' il difetto
        // da cui e' partita tutta questa revisione, quindi pesa molto). Il
        // solo punteggio interno di explodePositions non basta: e' una stima
        // fatta prima di sapere dove finiranno davvero le etichette.
        const offLand = landTester ? members.filter((m) => !landTester(m.x, m.y)).length : 0;
        const conflict = layoutConflictArea(points, canvas.bounds, canvas.fontScale, candidateIndices);
        // Anche la distanza tra i dot va misurata QUI, sul risultato finale,
        // e non lasciata solo al punteggio interno di explodePositions: una
        // variante puo' vincere sulle etichette e intanto aver spinto un dot
        // esploso a contatto con un dot vicino non coinvolto (caso reale: il
        // ventaglio Bowling Green/Nashville traslato verso sud finiva addosso
        // a Healthknit). Distanza minima richiesta: la stessa di
        // separateOverlappingDots, cioe' la somma dei raggi reali piu' due
        // unita' di margine, tutto scalato col fontScale della card.
        const radiusOf = (g) => (g.brands.length > 1 ? 8 : 6) * canvas.fontScale;
        let contact = 0;
        candidate.forEach((a, ai) => {
          // L'indice del secondo gruppo e' gia' quello del ciclo: cercarlo
          // con indexOf dentro a un doppio ciclo rendeva cubico un
          // controllo che e' quadratico, e questo gira per OGNI ventaglio
          // provato al tap.
          for (let bi = ai + 1; bi < candidate.length; bi++) {
            const bGroup = candidate[bi];
            if (!candidateIndices.has(ai) && !candidateIndices.has(bi)) continue;
            const needed = radiusOf(a) + radiusOf(bGroup) + 2 * canvas.fontScale;
            const dist = Math.hypot(a.x - bGroup.x, a.y - bGroup.y);
            if (dist < needed) contact += (needed - dist) ** 2;
          }
        });
        const score =
          conflict + contact + offLand * EXPLODE_OFF_LAND_PENALTY * canvas.fontScale * canvas.fontScale;
        if (!best || score < best.score)
          best = { score, conflict, contact, offLand, variant, groups: candidate, indices: candidateIndices, points };
      };

      for (const variant of variants) {
        evaluate(variant);
        if (best.score === 0) break;
      }

      // Seconda fase, solo se la prima non ha trovato un ventaglio pulito ed
      // e' ancora plausibile salvarlo: se le etichette si sovrappongono
      // gia' molte volte oltre la tolleranza, il problema e' la densita'
      // della zona, non dove sia centrato il ventaglio — provare altre otto
      // traslazioni non cambierebbe l'esito e renderebbe percettibilmente
      // lento proprio il tap sul cluster piu' denso (misurato: 760 ms contro
      // i ~30 ms di tutti gli altri).
      const rescuable = best.conflict <= EXPLODE_CONFLICT_TOLERANCE * canvas.fontScale * canvas.fontScale * 5;
      // si prova a spostare l'intero ventaglio di poco in ciascuna direzione.
      // Caso reale: Bowling Green (Kentucky) e Nashville sono uno a nord
      // dell'altro e il gruppo sta a ridosso del confine superiore degli
      // stati disegnati nella card "Sud" — aprendo il ventaglio sul posto, il
      // membro a nord finisce nell'Indiana, che appartiene al Midwest e in
      // questa card non e' disegnato. Spostare tutto il ventaglio verso sud
      // di mezza separazione risolve senza alterare le posizioni relative dei
      // due membri.
      if (best.score > 0 && rescuable) {
        const step = EXPLODE_MIN_SEPARATION * canvas.fontScale * 0.5;
        const shifts = [
          { dx: 0, dy: step }, { dx: 0, dy: -step }, { dx: step, dy: 0 }, { dx: -step, dy: 0 },
          { dx: step, dy: step }, { dx: -step, dy: step }, { dx: step, dy: -step }, { dx: -step, dy: -step },
        ];
        // Si traslano solo le varianti gia' risultate migliori nella prima
        // fase, non tutte: il ventaglio da spostare e' quello, e rifare ogni
        // combinazione moltiplicherebbe per quattro il costo del tap senza
        // cambiare l'esito (verificato sul dataset reale: stesso risultato,
        // quattro volte piu' veloce).
        const baseVariant = best.variant;
        for (const centerShift of shifts) {
          evaluate({ ...baseVariant, centerShift });
          if (best.score === 0) break;
        }
      }

      // L'esplosione si auto-verifica: se nemmeno il ventaglio migliore
      // riesce a mostrare le etichette dei membri senza accavallarle, aprirlo
      // toglie informazione invece di aggiungerla. Succede nella tasca piu'
      // densa del dataset reale (gli 8 brand del distretto tessile NC nella
      // card "Sud", 5 citta' entro ~10px l'una dall'altra): li' il tap apre
      // il popover con l'elenco dei brand — leggibile, e gia' il
      // comportamento dei cluster "stessa citta'". Verificato che allargare
      // il ventaglio non e' una via d'uscita: le etichette continuano a
      // sovrapporsi e in piu' i dot ricominciano a uscire dalla sagoma degli
      // stati, cioe' il difetto da cui si e' partiti.
      if (best.score > EXPLODE_CONFLICT_TOLERANCE * canvas.fontScale * canvas.fontScale) {
        // La card torna (o resta) allo stato collassato: se in questa card
        // era aperto un ALTRO cluster, aprire questo lo richiude comunque,
        // quindi lo stato di esplosione va azzerato prima di ridisegnare.
        // Senza, canvas.explodedGroup restava a puntare al cluster
        // precedente e il tap successivo su quel cluster veniva interpretato
        // come "richiudi" invece che "apri": il dot smetteva di rispondere
        // (verificato: tap sul cluster sparso, poi sul cluster denso che
        // ricade sul popover, poi di nuovo sul primo — non succedeva piu'
        // nulla).
        canvas.explodedGroup = null;
        canvas.explodedIndices = null;
        // I layout provati hanno misurato/posizionato le etichette su ipotesi
        // scartate: si ridisegna lo stato collassato per riportare la card
        // esattamente com'era. Il popover si ancora DOPO il ridisegno, al dot
        // appena creato: quello di prima e' stato rimosso dal DOM, e con un
        // altro cluster aperto l'indice dei dot a schermo non corrispondeva
        // nemmeno a quello dello stato collassato.
        this.paintRegionCanvas(canvas, canvas.collapsed, undefined, collapsedPoints);
        this.showClusterPopover(this.dotElementFor(canvas, collapsedIndex), group, canvas.popoverEl, canvas.wrapperEl);
        return;
      }

      const expanded = best.groups;
      const indices = best.indices;
      canvas.explodedGroup = group;
      canvas.explodedIndices = indices;
      this.paintRegionCanvas(canvas, expanded, { fromX: group.x, fromY: group.y, indices, elements: [] }, best.points);
    }

    collapseRegionCluster(canvas) {
      if (canvas.explodedGroup === null) return;
      canvas.explodedGroup = null;
      canvas.explodedIndices = null;
      this.paintRegionCanvas(canvas, canvas.collapsed);
    }

    // Richiude tutto cio' che e' esploso: i ventagli delle card mobile e la
    // zona aperta sulla mappa desktop.
    collapseAllRegionClusters() {
      this.regionCanvases.forEach((canvas) => this.collapseRegionCluster(canvas));
      this.collapseDesktopZone();
    }

    renderInternational(brands) {
      if (!this.regionsEl) return;
      this.regionsEl.innerHTML = '';

      const byRegion = new Map();
      brands.forEach((brand) => {
        const region = t('intlRegions')[brand.region] ? brand.region : 'other';
        if (!byRegion.has(region)) byRegion.set(region, []);
        byRegion.get(region).push(brand);
      });

      REGION_ORDER.forEach((region) => {
        const regionBrands = byRegion.get(region);
        if (!regionBrands || !regionBrands.length) return;
        // Ordine per nazione, non per nome del brand: dentro una regione le
        // card della stessa nazione restano cosi' vicine tra loro, e il
        // meta di ogni card (citta', nazione) legge come un elenco
        // raggruppato invece che come un alfabetico che alterna i paesi.
        // A parita' di nazione resta l'alfabetico sul nome, che era
        // l'ordinamento precedente. Un brand senza nazione (campo vuoto nel
        // dataset) finisce in fondo invece che in testa: una stringa vuota
        // ordinerebbe prima di tutto, e la prima card della regione sarebbe
        // proprio quella senza l'informazione su cui stiamo ordinando.
        regionBrands.sort((a, b) => {
          const countryA = countryName(a.country);
          const countryB = countryName(b.country);
          if (countryA !== countryB) {
            if (!countryA) return 1;
            if (!countryB) return -1;
            return countryA.localeCompare(countryB, LANG);
          }
          return a.label.localeCompare(b.label, LANG);
        });

        const regionEl = document.createElement('div');
        regionEl.className = 'manifatture-international__region';

        const title = document.createElement('h4');
        title.className = 'manifatture-international__region-title';
        title.textContent = t('intlRegions')[region];
        regionEl.appendChild(title);

        const grid = document.createElement('div');
        grid.className = 'manifatture-international__grid';

        regionBrands.forEach((brand) => {
          const card = document.createElement('button');
          card.type = 'button';
          card.className = 'manifatture-international__card';
          card.dataset.manifattureCard = '';
          card.dataset.brandId = brand.id;

          const flag = document.createElement('span');
          flag.className = 'manifatture-international__flag';
          flag.setAttribute('aria-hidden', 'true');
          flag.textContent = brand.flag || '';
          card.appendChild(flag);

          const name = document.createElement('span');
          name.className = 'manifatture-international__name';
          name.textContent = brand.label;
          card.appendChild(name);

          const meta = document.createElement('span');
          meta.className = 'manifatture-international__meta';
          const metaParts = [brand.city, countryName(brand.country)].filter(Boolean);
          meta.textContent =
            metaParts.join(', ') + (brand.founded ? ` · ${brand.founded}` : '');
          card.appendChild(meta);

          grid.appendChild(card);
        });

        regionEl.appendChild(grid);
        this.regionsEl.appendChild(regionEl);
      });
    }

    onSectionClick(event) {
      const dot = event.target.closest('[data-manifatture-dot]');
      const clusterItem = event.target.closest('[data-manifatture-cluster-item]');
      const card = event.target.closest('[data-manifatture-card]');
      const result = event.target.closest('[data-manifatture-result]');
      const closeBtn = event.target.closest('[data-manifatture-panel-close]');
      const backdrop = event.target.closest('[data-manifatture-panel-backdrop]');

      if (dot) {
        this.handleDotActivate(dot);
        return;
      }
      if (clusterItem) {
        this.openBrandPanel(clusterItem.dataset.brandId);
        // L'elenco del cluster NON si chiude aprendo una scheda: dentro una
        // tasca densa si guarda un brand e poi quello accanto, e richiudendo
        // l'elenco bisognava riaprire il cluster ogni volta. E' lo stesso
        // criterio gia' adottato per le esplosioni su mobile (vedi
        // handleRegionDotActivate), che restano aperte per la stessa
        // ragione. Un click fuori lo chiude come sempre.
        return;
      }
      if (result) {
        // La riga porta alla mappa, NON apre la scheda: il drawer e' un
        // overlay e finirebbe proprio sopra la zona in cui sta il dot appena
        // trovato. Chi vuole la scheda tocca il dot, che ora sa dove sta.
        // Se l'opacizzazione era stata sospesa (click sul vuoto), torna:
        // si sta chiedendo di nuovo "dov'e' questo".
        this.unmuteHighlight();
        this.focusBrand(result.dataset.brandId);
        // L'elenco si ritira dopo aver fatto il suo lavoro: e' in
        // sovrimpressione sulla mappa e resterebbe a coprire proprio quello
        // che si e' appena chiesto di vedere. Il filtro invece resta. Si
        // toglie anche il fuoco al campo, cosi' lo stato resta coerente:
        // elenco chiuso = campo non attivo.
        window.clearTimeout(this.searchBlurTimeout);
        this.searchFocused = false;
        if (this.searchInput) this.searchInput.blur();
        if (this.filterResultsEl) this.filterResultsEl.hidden = true;
        return;
      }
      if (card) {
        this.openBrandPanel(card.dataset.brandId);
        return;
      }
      if (closeBtn || backdrop) {
        this.closePanel();
        return;
      }
      // Cosa c'era di aperto PRIMA di chiudere qualcosa: il click sul vuoto
      // e' una scala a gradini — chiude l'elenco di un cluster o un
      // ventaglio se ce n'e' uno, altrimenti la scheda, e solo quando non
      // c'era piu' niente da chiudere toglie l'opacizzazione dei filtri
      // (vedi muteHighlight). Un click che facesse due cose insieme
      // sarebbe imprevedibile.
      const clusterApertoPrima = this.clusterUiIsOpen();
      const pannelloApertoPrima = !!(this.panel && !this.panel.hidden);
      // La barra di ricerca NON e' "il vuoto": e' il comando con cui si
      // sta filtrando proprio in quel momento, e toglierle da sotto i piedi
      // il filtro appena scelto non ha alcun senso.
      //
      // Contava piu' di quanto sembri: scegliendo un'opzione da una tendina
      // col mouse, il browser manda sul <select> anche un evento click, che
      // arriva qui DOPO il change. Il change applicava il filtro (nascondendo
      // le card fuori risultato) e portava la pagina sul brand trovato; il
      // click subito dopo cadeva in fondo a questa scala e sospendeva
      // l'opacizzazione, cioe' rimetteva a schermo TUTTE le card. Il
      // risultato: il layout si riallungava sotto a uno scorrimento gia'
      // partito, e il brand cercato finiva piu' in basso di dove la pagina
      // si era fermata (verificato: la card di "Finta", ultima regione,
      // scivolava dall'atterraggio corretto all'82% dello schermo, sotto le
      // card che erano appena ricomparse sopra di lei).
      const dentroLaBarra = !!event.target.closest('[data-manifatture-section-filters]');

      if (!event.target.closest('[data-manifatture-cluster-popover]')) {
        this.hideClusterPopover();
        this.regionCanvases.forEach((canvas) => this.hideClusterPopover(canvas.popoverEl));
        this.collapseAllRegionClusters();
      }
      // Chiude il pannello per qualunque altro click dentro la section che
      // non sia sul drawer stesso (contenuto, bottone chiudi — gia'
      // gestito sopra) — es. un punto vuoto della mappa, lo sfondo delle
      // card internazionali. Su desktop il drawer lascia passare i click
      // alla mappa sottostante (vedi .manifatture-panel, pointer-events:
      // none, in component-manifatture.css): senza questo controllo,
      // cliccare li' non avrebbe piu' alcun effetto, dato che il backdrop
      // non intercetta piu' il click al posto suo. Su mobile il backdrop
      // resta invece cliccabile come prima e intercetta il click prima che
      // arrivi qui, quindi per il bottom sheet questo ramo resta di fatto
      // inerte (nessun cambiamento rispetto a prima).
      if (this.panel && !this.panel.hidden && !event.target.closest('.manifatture-panel__sheet')) {
        this.closePanel();
        return;
      }

      // Ultimo gradino: niente era aperto, quindi il click sul vuoto vale
      // "torna a vedere la mappa intera". I filtri restano compilati e il
      // dot trovato resta segnato.
      if (!clusterApertoPrima && !pannelloApertoPrima && !dentroLaBarra) this.muteHighlight();
    }

    // C'e' un elenco di cluster aperto, o un ventaglio esploso, in una
    // qualsiasi delle mappe (nazionale o card regionali)?
    clusterUiIsOpen() {
      if (this.clusterPopover && !this.clusterPopover.hidden) return true;
      if (this.desktopZone) return true;
      return (this.regionCanvases || []).some(
        (canvas) => (canvas.popoverEl && !canvas.popoverEl.hidden) || canvas.explodedGroup
      );
    }

    onSectionKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const dot = event.target.closest('[data-manifatture-dot]');
      if (!dot) return;
      event.preventDefault();
      this.handleDotActivate(dot);
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      // Stessa scala a gradini del click sul vuoto (vedi onSectionClick):
      // Esc chiude prima cio' che e' aperto, e solo quando non resta piu'
      // niente toglie l'opacizzazione.
      const clusterApertoPrima = this.clusterUiIsOpen();
      const pannelloApertoPrima = !!(this.panel && !this.panel.hidden);
      if (this.panel && !this.panel.hidden) this.closePanel();
      // Esc chiude tutto cio' che e' "aperto sopra" la mappa, popover
      // compresi: prima li lasciava aperti (li chiudeva solo un click
      // altrove), incoerenza che si notava soprattutto navigando da
      // tastiera, dove il popover di un cluster non si poteva chiudere
      // senza usare il mouse.
      this.hideClusterPopover();
      this.regionCanvases.forEach((canvas) => this.hideClusterPopover(canvas.popoverEl));
      this.collapseAllRegionClusters();
      if (!clusterApertoPrima && !pannelloApertoPrima) this.muteHighlight();
    }

    // Chiude il pannello per un click FUORI da questo intero elemento
    // (es. l'header del tema, o qualunque altra section della pagina) —
    // solo rilevante ora che il drawer desktop non copre piu' l'header
    // (vedi component-manifatture.css) e lascia passare i click alla
    // pagina sottostante, quindi un click li' non passerebbe mai da
    // onSectionClick (che e' delegato su questo elemento, non su
    // document). this.contains(event.target) esclude i click che
    // ricadono dentro la section: quelli li' gestisce gia' onSectionClick
    // sopra, evitando che i due handler si sovrappongano sullo stesso click.
    onDocumentClick(event) {
      if (!this.panel || this.panel.hidden) return;
      // Un elemento che nel frattempo e' stato RIMOSSO dal DOM non e' un
      // click "fuori dalla section": e' un click su qualcosa di nostro che
      // abbiamo ridisegnato mentre il click stava ancora risalendo. Succede
      // davvero — bug reale: toccando un brand dentro un cluster esploso, il
      // gestore apriva la scheda e ridisegnava la card, il dot cliccato
      // spariva dal DOM e questo controllo, trovando this.contains(target)
      // falso su un nodo ormai staccato, richiudeva subito la scheda appena
      // aperta ("si vede per un attimo e poi si chiude"). isConnected
      // distingue i due casi, qualunque sia il ridisegno che l'ha causato.
      if (!event.target.isConnected) return;
      if (this.contains(event.target)) return;
      // La notifica del carrello (e il drawer, se un domani cambia
      // l'impostazione) vivono FUORI dalla section, nell'header: senza
      // questa eccezione, aggiungere al carrello dalla scheda e poi
      // toccare "vedi carrello" o la X della notifica chiuderebbe anche la
      // scheda vendor sotto, che non e' quello che si sta chiedendo.
      if (event.target.closest('cart-notification, cart-drawer')) return;
      this.closePanel();
    }

    // Che vendor aprirebbe questo elemento? Serve al precaricamento, che
    // deve sapere PRIMA del click cosa verra' chiesto. Restituisce null
    // quando il gesto non porta a una singola scheda: un dot che contiene
    // piu' brand apre il popover del cluster, e li' non c'e' ancora nulla
    // da precaricare.
    vendorForElement(el) {
      if (!el || typeof el.closest !== 'function') return null;

      const clusterItem = el.closest('[data-manifatture-cluster-item]');
      const card = el.closest('[data-manifatture-card]');
      const dot = el.closest('[data-manifatture-dot]');

      let brandId = null;
      if (clusterItem) {
        brandId = clusterItem.dataset.brandId;
      } else if (card) {
        brandId = card.dataset.brandId;
      } else if (dot) {
        const index = Number(dot.dataset.groupIndex);
        const regionKey = dot.dataset.regionKey;
        let group = null;
        if (regionKey) {
          const canvas = (this.regionCanvases || []).find((c) => c.key === regionKey);
          group = canvas ? canvas.current[index] : null;
        } else {
          group = (this.usaGroups || [])[index];
        }
        if (group && group.brands.length === 1) brandId = group.brands[0].id;
      }

      if (!brandId) return null;
      const brand = this.brandsById.get(brandId);
      return brand ? brand.shopify_vendor : null;
    }

    // I vendor di tutti i brand di un gruppo, nell'ordine in cui l'utente
    // se li vedra' elencati.
    vendorsForGroup(group) {
      if (!group || !group.brands) return [];
      return group.brands
        .map((brand) => this.brandsById.get(brand.id))
        .filter(Boolean)
        .map((brand) => brand.shopify_vendor);
    }

    onSectionPointerOver(event) {
      // Solo col mouse: al dito ci pensa touchstart (che arriva comunque
      // prima del click), e da tastiera l'attivazione e' immediata.
      if (event.pointerType && event.pointerType !== 'mouse') return;

      const vendor = this.vendorForElement(event.target);
      if (!vendor) return;

      // Passare su una voce dell'elenco di un cluster restringe una scelta
      // gia' in corso: gli altri brand dello stesso cluster restano
      // candidati e i loro precaricamenti non vanno buttati.
      const dentroUnCluster = !!event.target.closest('[data-manifatture-cluster-item]');

      window.clearTimeout(this.prefetchTimeout);
      // Attraversare la mappa col mouse passa sopra decine di dot: senza
      // questa attesa ognuno farebbe partire un render da mezzo secondo.
      this.prefetchTimeout = window.setTimeout(() => {
        // La scheda di questo vendor e' gia' aperta: non c'e' nulla da
        // anticipare.
        if (this.openVendor === vendor) return;
        prefetchVendors([vendor], !dentroUnCluster);
      }, 120);
    }

    onSectionTouchStart(event) {
      // Nessun ritardo: col dito non esiste il passaggio sopra, quindi un
      // touchstart e' gia' un'intenzione dichiarata. Il guadagno e' tutto
      // il tempo che passa tra il tocco e il rilascio.
      const vendor = this.vendorForElement(event.target);
      if (!vendor || this.openVendor === vendor) return;
      const dentroUnCluster = !!event.target.closest('[data-manifatture-cluster-item]');
      prefetchVendors([vendor], !dentroUnCluster);
    }

    handleDotActivate(dotEl) {
      // I dot delle card regionali mobile (vedi buildRegionCanvas/
      // USA_REGIONS) portano un data-region-key: li smista verso lo stato
      // della loro card invece che verso this.usaGroups (che su mobile non
      // viene piu' popolato — la mappa singola resta nascosta).
      const regionKey = dotEl.dataset.regionKey;
      if (regionKey) {
        this.handleRegionDotActivate(regionKey, dotEl);
        return;
      }

      const index = Number(dotEl.dataset.groupIndex);
      const group = this.usaGroups[index];
      if (!group) return;
      // Con una zona aperta, un dot che non ne fa parte e' attenuato: il
      // primo clic richiude la zona, come un clic sul vuoto, invece di
      // aprire la scheda di un punto che si vede appena.
      if (this.desktopZone && !this.desktopZone.indices.has(index)) {
        this.collapseDesktopZone();
        return;
      }
      if (group.sourceGroups && group.sourceGroups.length > 1) {
        this.openDesktopZone(index);
        return;
      }
      if (group.brands.length === 1) {
        this.toggleBrandPanel(group.brands[0].id);
        this.hideClusterPopover();
        return;
      }
      this.showClusterPopover(dotEl, group);
    }

    // Equivalente di handleDotActivate ma per un dot dentro una card
    // regionale mobile: legge/scrive lo stato del SOLO canvas di quella
    // regione (canvas.current, non this.usaGroups), cosi' l'esplosione di
    // un cluster in una regione non tocca le altre.
    handleRegionDotActivate(regionKey, dotEl) {
      const canvas = this.regionCanvases.find((c) => c.key === regionKey);
      if (!canvas) return;
      const index = Number(dotEl.dataset.groupIndex);
      const group = canvas.current[index];
      if (!group) return;
      if (group.brands.length === 1) {
        this.toggleBrandPanel(group.brands[0].id);
        this.hideClusterPopover(canvas.popoverEl);
        // NON si richiudono le esplosioni aperte: il modo normale di arrivare
        // alla scheda di un brand dentro una tasca densa e' proprio "apri il
        // cluster, poi tocca il brand", e richiudere l'esplosione qui
        // significava che alla chiusura della scheda si tornava alla mappa
        // raggruppata, dovendo riaprire il cluster per guardare il brand
        // accanto. Restando aperta, la scheda si chiude e si e' di nuovo
        // davanti ai membri esplosi. (Era anche la causa scatenante del bug
        // corretto in onDocumentClick: il ridisegno rimuoveva dal DOM il dot
        // appena cliccato.)
        return;
      }
      // Solo i cluster per vicinanza (sourceGroups.length > 1 — vedi
      // clusterByProximity) si "esplodono" al tap invece di aprire la
      // lista: un cluster "stessa citta'" (es. Martinsville) ha
      // sourceGroups di lunghezza 1 (se' stesso) perche' i suoi brand
      // condividono davvero lo stesso punto — esploderlo non rivelerebbe
      // posizioni diverse, quindi si comporta come sempre (lista al tap).
      if (group.sourceGroups && group.sourceGroups.length > 1) {
        this.toggleRegionCluster(canvas, index);
        return;
      }
      this.showClusterPopover(dotEl, group, canvas.popoverEl, canvas.wrapperEl);
    }

    // popoverEl/wrapperEl opzionali (default: la mappa nazionale singola)
    // permettono di riusare la stessa logica di posizionamento per il
    // popover proprio di ciascuna card regionale mobile (vedi
    // handleRegionDotActivate/buildRegionCanvas).
    showClusterPopover(dotEl, group, popoverEl, wrapperEl) {
      const popover = popoverEl || this.clusterPopover;
      const wrapper = wrapperEl || this.mapWrapperEl;
      if (!popover || !wrapper) return;

      // Aprire l'elenco di un cluster e' il momento in cui si sa QUALI
      // schede sono in gioco, e da qui alla scelta passa il tempo di
      // leggere i nomi: e' tempo utile per precaricarli tutti (fino al
      // tetto), invece di aspettare il secondo tocco per iniziare.
      prefetchVendors(this.vendorsForGroup(group), true);
      const wrapperRect = wrapper.getBoundingClientRect();
      const dotRect = dotEl.getBoundingClientRect();

      popover.innerHTML = '';
      const list = document.createElement('ul');
      list.className = 'manifatture-map__cluster-list';

      group.brands.forEach((brand) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'manifatture-map__cluster-item';
        button.dataset.manifattureClusterItem = '';
        button.dataset.brandId = brand.id;
        button.dataset.brandIds = brand.id;
        button.textContent = brand.label;
        item.appendChild(button);
        list.appendChild(item);
      });

      popover.appendChild(list);
      // Un cluster resta in chiaro se anche un solo brand rientra nel filtro
      // (vedi applyFilterHighlight): nel suo elenco, pero', si deve vedere
      // quale dei brand e' quello cercato.
      this.applyFilterHighlight();
      const preferredLeft = dotRect.left - wrapperRect.left + dotRect.width / 2;
      popover.style.left = `${preferredLeft}px`;
      popover.classList.remove('manifatture-map__cluster-popover--below');
      popover.style.top = `${dotRect.top - wrapperRect.top - CLUSTER_POPOVER_GAP}px`;
      popover.hidden = false;

      // L'elenco e' centrato sul proprio dot (transform: translateX(-50%)),
      // e per un dot vicino al bordo laterale della mappa questo lo manda
      // per meta' fuori: la meta' che sborda finisce contro il bordo dello
      // schermo su mobile e viene tagliata dal contenitore della mappa su
      // desktop (.manifatture-map__wrapper ha overflow-x: auto, che secondo
      // le regole CSS rende "auto" anche l'asse verticale: il contenitore
      // RITAGLIA, non e' solo un riferimento di posizione). Succede con
      // Seattle, il dot piu' a nord-ovest della mappa, e succederebbe con
      // qualsiasi cluster nascesse in un angolo.
      //
      // Si misura la posizione appena applicata e, se serve, si fa scorrere
      // l'elenco lungo l'orizzontale quel tanto che basta a rientrare —
      // l'elenco smette di essere centrato sul dot, ma resta attaccato a lui
      // (il dot e' comunque sotto l'elenco, che e' largo almeno 12rem) ed e'
      // leggibile per intero, che e' il punto. Il confine e' il piu' stretto
      // tra il contenitore della mappa e la finestra: su desktop la mappa e'
      // piu' larga della finestra (breakout a 100vw con scroll orizzontale),
      // su mobile e' la mappa a essere piu' stretta.
      const gutter = 8;
      const leftBound = Math.max(wrapperRect.left, 0) + gutter;
      const rightBound = Math.min(wrapperRect.right, window.innerWidth) - gutter;
      const openRect = popover.getBoundingClientRect();
      // Un elenco piu' largo dello spazio disponibile non puo' rientrare da
      // entrambi i lati: lo si allinea al bordo sinistro, dove comincia a
      // leggersi, invece di lasciarlo tagliato proprio all'inizio.
      let shift = 0;
      if (openRect.left < leftBound) shift = leftBound - openRect.left;
      else if (openRect.right > rightBound) shift = Math.max(rightBound - openRect.right, leftBound - openRect.left);
      if (shift) popover.style.left = `${preferredLeft + shift}px`;

      // Un cluster per vicinanza (vedi clusterByProximity) puo' arrivare a
      // raccogliere molti piu' brand di quanti ne capitasse mai a un
      // cluster "stessa citta'" (fino a 8+ contro un tipico 2-4): il
      // popover, aperto di default SOPRA al dot, puo' quindi risultare piu'
      // alto dello spazio libero sopra e finire in parte fuori dalla
      // viewport — specialmente per dot vicini alla cima della mappa.
      // Si misura la posizione gia' applicata e, solo se serve, si riapre
      // SOTTO al dot invece (stesso approccio "prova la posizione preferita,
      // misura, correggi solo se serve" usato per le etichette della mappa).
      const rect = popover.getBoundingClientRect();
      // Non basta "esce dalla viewport" (rect.top < 0): sopra alla mappa c'e'
      // roba che l'elenco non deve coprire. L'header sticky del tema, che lo
      // renderebbe invisibile esattamente come se fosse fuori schermo; e la
      // barra dei filtri, che su mobile sta appena sopra alla prima card
      // regionale — un elenco lungo (dieci brand nella zona tessile della
      // Carolina del Nord) le finiva davanti, coprendo tendine e conteggio.
      // Il confine e' il piu' basso dei due; scorrendo, una barra ormai
      // uscita dallo schermo ha bordo inferiore negativo e non conta piu'.
      const barRect = this.filtersBar && !this.filtersBar.hidden
        ? this.filtersBar.getBoundingClientRect().bottom
        : 0;
      // E c'e' un terzo confine, quando il contenitore della mappa RITAGLIA
      // (vedi sopra: overflow-x: auto su .manifatture-map__wrapper rende
      // "auto" anche l'asse verticale). Li' l'elenco non puo' uscire sopra
      // al contenitore, e un dot nella fascia alta della mappa — Seattle, ma
      // anche qualsiasi cluster nascesse piu' a nord — lo apriva a cavallo
      // del bordo, con le prime voci tagliate via: si vedeva solo l'ultima
      // meta' dell'elenco. Le card regionali mobile, che non ritagliano
      // niente, non sono toccate da questo confine (e non devono esserlo:
      // li' l'elenco puo' legittimamente sporgere sopra la propria card).
      const wrapperClipsTop =
        window.getComputedStyle(wrapper).overflowY !== 'visible' ? wrapperRect.top : -Infinity;
      if (rect.top < Math.max(stickyHeaderHeight(), barRect, wrapperClipsTop)) {
        popover.classList.add('manifatture-map__cluster-popover--below');
        popover.style.top = `${dotRect.bottom - wrapperRect.top + CLUSTER_POPOVER_GAP}px`;
      }
    }

    // popoverEl opzionale (default: il popover della mappa nazionale
    // singola) — vedi showClusterPopover.
    hideClusterPopover(popoverEl) {
      const popover = popoverEl || this.clusterPopover;
      if (popover) popover.hidden = true;
    }

    // La scheda di questo brand e' quella attualmente a schermo? Il
    // confronto e' sul brand aperto E sul pannello davvero aperto:
    // closePanel azzera openBrandId, quindi dopo una chiusura (anche
    // durante i 250ms dell'animazione di uscita) lo stesso brand torna a
    // comportarsi come uno mai aperto.
    // --- Bottom sheet mobile: due misure, tre modi per passarci ---

    // Le due misure in pixel, ricalcolate a ogni gesto perche' la viewport
    // visibile cambia mentre si naviga (barre del browser che entrano ed
    // escono, tastiera, rotazione).
    sheetSnapHeights() {
      const height = viewportHeight();
      return { peek: height * SHEET_PEEK_RATIO, full: height * SHEET_FULL_RATIO };
    }

    setSheetExpanded(expanded) {
      this.sheetExpanded = !!expanded;
      if (!this.panelSheet) return;
      this.panelSheet.classList.toggle('is-expanded', this.sheetExpanded);
      if (!this.panelGrabber) return;
      this.panelGrabber.setAttribute('aria-expanded', this.sheetExpanded ? 'true' : 'false');
      // L'etichetta descrive cosa FA il prossimo tocco, non lo stato
      // attuale: da foglio piccolo si legge "ingrandisci", da foglio
      // grande "rimpicciolisci". Le due stringhe arrivano tradotte dal
      // Liquid (vedi manifatture.liquid).
      const label = this.sheetExpanded
        ? this.panelGrabber.dataset.labelCollapse
        : this.panelGrabber.dataset.labelExpand;
      if (label) this.panelGrabber.setAttribute('aria-label', label);
    }

    // Toglie l'altezza scritta a mano dal trascinamento: da qui in poi
    // comanda di nuovo il CSS (la misura della classe is-expanded).
    clearSheetDragHeight() {
      if (this.panelSheet) this.panelSheet.style.removeProperty('--manifatture-sheet-height');
    }

    onGrabberPointerDown(event) {
      if (!isSheetLayout() || !this.panelSheet) return;
      // Solo il gesto primario: un secondo dito appoggiato durante un
      // trascinamento gia' in corso non deve ripartire da capo.
      if (this.sheetDrag) return;
      this.sheetDrag = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: this.panelSheet.getBoundingClientRect().height,
        height: this.panelSheet.getBoundingClientRect().height,
        moved: false
      };
      this.panelSheet.classList.add('is-dragging');
      // I successivi eventi arrivano alla maniglia anche se il dito esce
      // dai suoi pochi pixel di altezza — cioe' praticamente sempre.
      if (event.target.setPointerCapture) event.target.setPointerCapture(event.pointerId);
      event.target.addEventListener('pointermove', this.onGrabberPointerMove);
      event.target.addEventListener('pointerup', this.onGrabberPointerUp);
      event.target.addEventListener('pointercancel', this.onGrabberPointerUp);
    }

    onGrabberPointerMove(event) {
      const drag = this.sheetDrag;
      if (!drag || event.pointerId !== drag.pointerId || !this.panelSheet) return;
      // Verso l'alto il foglio cresce: il delta e' startY meno la
      // posizione corrente, non il contrario.
      const delta = drag.startY - event.clientY;
      if (Math.abs(delta) > 3) drag.moved = true;
      const { full } = this.sheetSnapHeights();
      // Il minimo non e' zero: il foglio non si accartoccia sotto al dito,
      // resta abbastanza alto da vedersi finche' non lo si lascia (ed e'
      // allo stacco che si decide se chiuderlo del tutto).
      const height = Math.min(full, Math.max(viewportHeight() * 0.2, drag.startHeight + delta));
      drag.height = height;
      this.panelSheet.style.setProperty('--manifatture-sheet-height', `${height}px`);
    }

    onGrabberPointerUp(event) {
      const drag = this.sheetDrag;
      if (!drag || (event.pointerId !== undefined && event.pointerId !== drag.pointerId)) return;
      const target = event.currentTarget || this.panelGrabber;
      if (target) {
        target.removeEventListener('pointermove', this.onGrabberPointerMove);
        target.removeEventListener('pointerup', this.onGrabberPointerUp);
        target.removeEventListener('pointercancel', this.onGrabberPointerUp);
      }
      this.sheetDrag = null;
      if (this.panelSheet) this.panelSheet.classList.remove('is-dragging');
      this.clearSheetDragHeight();

      // Un tocco senza spostamento non e' un trascinamento: lo gestisce il
      // click (che arriva subito dopo) come "cambia misura".
      if (!drag.moved) return;
      // Il click di chiusura del gesto va ignorato, altrimenti annullerebbe
      // subito lo scatto appena deciso qui.
      this.suppressGrabberClick = true;

      const { peek, full } = this.sheetSnapHeights();
      if (drag.height < peek * SHEET_DISMISS_RATIO) {
        this.closePanel();
        return;
      }
      // Scatta sulla misura piu' vicina a dove e' stato lasciato.
      this.setSheetExpanded(drag.height > (peek + full) / 2);
    }

    // --- Crescita col dito, dal contenuto ---
    //
    // Trascinando verso l'alto CON IL FOGLIO GIA' IN CIMA, il gesto non
    // scorre il contenuto ma alza il foglio, un pixel per pixel di dito,
    // esattamente come fanno i fogli di sistema. Prima l'ingrandimento
    // scattava tutto insieme al primo scroll: il bordo del foglio saliva di
    // 234px in un colpo mentre il dito ne aveva percorsi 16, e il contenuto
    // schizzava via — quel che si vedeva come uno scatto in ritardo. Qui
    // invece non c'e' nessun salto da assorbire: il contenuto segue il dito
    // perche' e' il dito a decidere quanto il foglio cresce.
    //
    // Il gesto si prende SOLO quando c'e' davvero qualcosa da guadagnare:
    // foglio piccolo, contenuto gia' in cima (scrollTop 0), dito che va in
    // su. In ogni altro caso non si tocca nulla e lo scroll resta quello
    // nativo, inerzia compresa.
    onSheetTouchStart(event) {
      if (!isSheetLayout() || !this.panelSheet) return;
      // La maniglia ha i suoi gestori (e touch-action: none): un gesto che
      // parte da li' e' gia' un trascinamento, non va raddoppiato.
      if (event.target.closest('[data-manifatture-panel-grabber]')) return;
      if (event.touches.length !== 1) return;
      this.sheetPull = {
        startY: event.touches[0].clientY,
        startHeight: this.panelSheet.getBoundingClientRect().height,
        active: false
      };
    }

    onSheetTouchMove(event) {
      const pull = this.sheetPull;
      if (!pull || this.sheetExpanded || !this.panelSheet) return;
      // Se il contenuto e' gia' stato scrollato, il gesto e' suo: prenderlo
      // a meta' strada significherebbe far crescere il foglio mentre si sta
      // leggendo qualcosa a meta' pagina.
      if (!pull.active && this.panelSheet.scrollTop > 0) return;
      const delta = pull.startY - event.touches[0].clientY;
      if (!pull.active && delta <= 0) return;

      const { full } = this.sheetSnapHeights();
      const height = Math.min(full, pull.startHeight + delta);
      if (height <= pull.startHeight && !pull.active) return;

      if (!pull.active) {
        pull.active = true;
        this.panelSheet.classList.add('is-dragging');
      }
      // Senza questo il browser scorrerebbe il contenuto DURANTE la
      // crescita: due movimenti sovrapposti, di nuovo.
      if (event.cancelable) event.preventDefault();
      pull.height = height;
      this.panelSheet.style.setProperty('--manifatture-sheet-height', `${height}px`);
    }

    onSheetTouchEnd() {
      const pull = this.sheetPull;
      this.sheetPull = null;
      if (!pull || !pull.active || !this.panelSheet) return;
      this.panelSheet.classList.remove('is-dragging');
      this.clearSheetDragHeight();
      const { peek, full } = this.sheetSnapHeights();
      // Solo verso l'alto: dal contenuto non si chiude niente, per quello
      // ci sono la maniglia e il backdrop. Al peggio si torna alla misura
      // piccola, che e' da dove si era partiti.
      this.setSheetExpanded(pull.height > (peek + full) / 2);
    }

    onGrabberClick() {
      if (this.suppressGrabberClick) {
        this.suppressGrabberClick = false;
        return;
      }
      if (!isSheetLayout()) return;
      this.setSheetExpanded(!this.sheetExpanded);
    }

    // Il primo scroll verso il basso alza il foglio da solo: se si sta
    // gia' scorrendo il contenuto, la richiesta implicita e' "voglio
    // vederne di piu'". Non fa il contrario (tornare in cima non
    // rimpicciolisce): si scorre su e giu' di continuo mentre si guardano
    // i prodotti, e un foglio che si abbassa da solo mentre lo si legge
    // sarebbe solo fastidioso — per rimpicciolirlo c'e' la maniglia.
    // Rete di sicurezza per chi non scorre col dito (rotellina, trackpad,
    // tasti freccia): li' non c'e' un gesto continuo da seguire, quindi il
    // foglio si porta alla misura grande in un colpo solo — ma senza
    // animazione e compensando lo scroll, cosi' non si vede scattare.
    // Sul tocco questo caso non si presenta quasi mai: ci arriva prima
    // onSheetTouchMove, che il gesto se lo prende tutto.
    onSheetScroll() {
      if (!this.panelSheet) return;
      const scrollTop = this.panelSheet.scrollTop;
      // Serve la DIREZIONE, non solo la posizione: rimpicciolendo la scheda
      // dalla maniglia mentre si e' a meta' contenuto, lo scroll resta dov'e'
      // (e deve restarci). Guardando solo "scrollTop > soglia", il primo
      // movimento successivo — anche uno scroll all'INDIETRO, verso il
      // titolo — trovava la condizione gia' vera e riapriva il foglio
      // grande, disfacendo cio' che si era appena chiesto con la maniglia.
      // Ingrandire ha senso solo scendendo: e' li' che si sta chiedendo di
      // vedere piu' roba.
      const versoIlBasso = scrollTop > (this.lastSheetScrollTop || 0);
      this.lastSheetScrollTop = scrollTop;
      if (this.sheetPull && this.sheetPull.active) return;
      if (this.sheetExpanded || this.sheetDrag || !isSheetLayout()) return;
      if (!versoIlBasso) return;
      if (scrollTop <= SHEET_AUTO_EXPAND_SCROLL) return;

      // Qui l'ingrandimento NON si anima, al contrario di quello chiesto
      // con la maniglia. Animare l'altezza mentre il dito sta gia'
      // scorrendo mette due movimenti l'uno sull'altro — il foglio che
      // cresce e il contenuto che scorre — e per giunta ogni frame
      // dell'animazione e' un ricalcolo di layout: si vedeva scattare,
      // come se fosse in ritardo sul dito.
      //
      // In piu' si compensa lo scroll. Il foglio e' ancorato in basso:
      // crescendo, il suo bordo superiore sale, e con lui tutto il
      // contenuto — che quindi schizzava via sotto il dito di quanto il
      // foglio era cresciuto. Sottraendo la stessa quantita' allo
      // scrollTop, cio' che si sta leggendo resta esattamente dov'era e
      // l'unica cosa che si muove e' il bordo del foglio, che scopre altro
      // contenuto sopra. Il tutto nello stesso frame: nessun passaggio
      // intermedio viene mai disegnato.
      const primaDi = this.panelSheet.getBoundingClientRect().height;
      this.panelSheet.classList.add('is-resizing');
      this.setSheetExpanded(true);
      const dopo = this.panelSheet.getBoundingClientRect().height;
      this.panelSheet.scrollTop = Math.max(0, this.panelSheet.scrollTop - (dopo - primaDi));
      // Il ripristino va rimandato di un frame: togliendo la classe subito,
      // la transizione tornerebbe attiva prima che il browser abbia
      // disegnato la nuova altezza, e l'animazione ripartirebbe da capo.
      requestAnimationFrame(() => {
        if (this.panelSheet) this.panelSheet.classList.remove('is-resizing');
      });
    }

    brandPanelIsOpen(brandId) {
      return this.openBrandId === brandId && !!this.panel && !this.panel.hidden;
    }

    // Attivazione di un brand dal SUO dot sulla mappa: il dot e' un
    // interruttore: se la scheda di quel brand e' gia' quella aperta, il
    // secondo click la chiude invece di riaprirla. Su mobile funzionava
    // gia' cosi', ma solo di rimbalzo: il backdrop del bottom sheet copre
    // la mappa e intercetta il click prima del dot, chiudendo il
    // pannello. Su desktop il drawer ha pointer-events: none (vedi
    // component-manifatture.css) e non c'e' backdrop, quindi il click
    // arrivava davvero al dot e la scheda restava aperta. Qui il toggle e'
    // esplicito, cosi' le due versioni si comportano allo stesso modo per
    // lo stesso motivo, non per un effetto collaterale del layout.
    toggleBrandPanel(brandId) {
      if (this.brandPanelIsOpen(brandId)) {
        this.closePanel();
        return;
      }
      this.openBrandPanel(brandId);
    }

    // Il marchio chiesto dall'indirizzo, se c'e' e se esiste.
    //
    // Un marchio sconosciuto (chiave vecchia, scheda tolta dalla mappa, o
    // semplicemente un parametro scritto a mano) non e' un errore da
    // mostrare: la pagina resta l'Atlante normale, che e' comunque una
    // risposta sensata a chi ci e' arrivato.
    openRequestedBrand() {
      let chiesto = null;
      try {
        chiesto = new URLSearchParams(window.location.search).get(DEEP_LINK_PARAM);
      } catch (error) {
        return;
      }
      const brandId = this.resolveBrandId(chiesto);
      if (!brandId) return;
      // Il marchio del capo da cui si arriva: serve al testo di «Torna al
      // capo» nelle schede degli altri marchi (vedi backLabel).
      this.originBrandId = brandId;
      this.focusBrand(brandId);
      this.openBrandPanel(brandId);
    }

    resolveBrandId(value) {
      const chiesto = String(value || '').trim().toLowerCase();
      if (!chiesto) return null;
      if (this.brandsById.has(chiesto)) return chiesto;
      // Anche per shopify_vendor, con lo stesso confronto tollerante di
      // readActiveVendors: un link scritto a mano con "Velva Sheen" invece
      // della chiave del Gist deve funzionare lo stesso.
      let trovato = null;
      this.brandsById.forEach((brand, id) => {
        if (trovato) return;
        if (String(brand.shopify_vendor || '').trim().toLowerCase() === chiesto) trovato = id;
      });
      return trovato;
    }

    // L'indirizzo segue la scheda aperta, cosi' quello che si vede e' anche
    // quello che si condivide (o si ritrova ricaricando la pagina).
    //
    // replaceState e non pushState: ogni dot toccato aggiungerebbe una voce
    // alla cronologia, e uscire dall'Atlante dopo averne guardati dieci
    // vorrebbe dire premere "indietro" dieci volte.
    syncUrl(brandId) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        const url = new URL(window.location.href);
        if (brandId) url.searchParams.set(DEEP_LINK_PARAM, brandId);
        else url.searchParams.delete(DEEP_LINK_PARAM);
        window.history.replaceState(window.history.state, '', url);
      } catch (error) {
        /* un indirizzo che non si lascia riscrivere non deve fermare la scheda */
      }
    }

    async openBrandPanel(brandId) {
      const brand = this.brandsById.get(brandId);
      if (!brand) return;

      // Riattivare lo STESSO brand mentre la sua scheda e' gia' aperta non
      // deve rifare nulla: prima ogni click ripeteva renderPanelHeader (che
      // svuota panelContent) e loadBrandProducts, quindi la scheda gia' a
      // schermo lampeggiava tornando a "Caricamento prodotti…" e ricaricava
      // gli stessi identici prodotti — perdendo anche lo scroll e i filtri
      // gia' impostati. Succede riaprendo dallo stesso item di un popover o
      // dalla stessa card. (Sul dot il secondo click non arriva nemmeno
      // qui: e' un toggle e chiude la scheda — vedi brandPanelIsOpen.)
      if (this.brandPanelIsOpen(brandId)) return;

      // Un hover appena prima del click lascia in coda un precaricamento
      // che scatterebbe subito DOPO l'apertura, per il vendor che si sta
      // gia' aprendo: una richiesta sprecata due volte, perche' quella
      // precaricata e' stata appena consumata. Il timer va spento qui, e il
      // vendor aperto memorizzato per non ripescarlo dal passaggio del
      // mouse mentre e' gia' a schermo.
      window.clearTimeout(this.prefetchTimeout);
      // Ogni scheda comincia dall'inizio: passando da un brand all'altro
      // col pannello gia' aperto, senza questo si resterebbe fermi allo
      // scroll della scheda precedente, cioe' a meta' della nuova.
      if (this.panelSheet) this.panelSheet.scrollTop = 0;
      this.lastSheetScrollTop = 0;
      // La misura del foglio si azzera solo su una scheda aperta da
      // chiuso: se e' gia' a schermo e si tocca un altro dot, chi guarda
      // l'ha ingrandito apposta e vedersela rimpicciolire sotto le dita
      // sarebbe un dispetto.
      if (this.panel && this.panel.hidden) this.setSheetExpanded(false);
      this.openBrandId = brandId;
      this.openVendor = brand.shopify_vendor;
      this.syncUrl(brandId);
      this.renderPanelHeader(brand);
      this.showPanel();
      await this.loadBrandProducts(brand);
    }

    // L'indirizzo del capo da cui si e' arrivati, se si e' arrivati da un
    // capo: stesso sito, pagina /products/ (anche /en/products/). Letto una
    // volta sola — il referrer non cambia per tutta la vita della pagina.
    // Chi apre l'Atlante dal menu, da un link esterno o scrivendo
    // l'indirizzo non vede niente.
    readReturnUrl() {
      if (this.returnUrl !== undefined) return this.returnUrl;
      this.returnUrl = null;
      try {
        const from = new URL(document.referrer);
        if (from.origin === window.location.origin && /\/products\//.test(from.pathname)) {
          this.returnUrl = from.href;
        }
      } catch (error) {
        /* nessun referrer, o uno che non si lascia leggere: niente link */
      }
      return this.returnUrl;
    }

    // Nella scheda del marchio del capo basta «Torna al capo». Nelle altre
    // si dice di QUALE capo: guardando Carhartt, «Torna al capo» farebbe
    // pensare a un capo Carhartt, e invece riporta al Velva Sheen da cui si
    // e' partiti. Se il marchio di partenza non si conosce (si e' arrivati da
    // un capo ma senza ?brand=), resta la forma breve ovunque.
    backLabel(brand) {
      const origin = this.originBrandId ? this.brandsById.get(this.originBrandId) : null;
      if (!origin || origin === brand) return t('backToProduct');
      // "Signal (Apparel Company)" -> "Signal": la ragione sociale non serve
      // a riconoscere il capo, e allunga il link.
      const name = String(origin.label || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
      return name ? t('backToBrandProduct')(name) : t('backToProduct');
    }

    // Tornare INDIETRO, non aprire di nuovo il capo: cosi' la scheda
    // prodotto si ritrova dove la si era lasciata, alla stessa altezza, e
    // la cronologia non si allunga. Si puo' perche' l'Atlante non aggiunge
    // voci (syncUrl usa replaceState): la voce precedente e' il capo.
    // Se l'Atlante e' stato aperto in una scheda nuova non c'e' niente
    // dietro, e il link fa il suo mestiere normale.
    onBackToProduct(event) {
      if (window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    }

    renderPanelHeader(brand) {
      if (!this.panelContent) return;
      this.panelContent.innerHTML = '';

      const header = document.createElement('div');
      header.className = 'manifatture-panel__header';

      // Chi e' arrivato qui dalla targhetta di un capo trova in cima alla
      // scheda la strada per tornarci. Serve soprattutto nel browser interno
      // di Instagram, dove il tasto indietro e' poco visibile e chi lo cerca
      // spesso esce dall'app invece di tornare al capo che stava guardando.
      const returnUrl = this.readReturnUrl();
      if (returnUrl) {
        const back = document.createElement('p');
        back.className = 'manifatture-panel__back';
        back.style.margin = '0 0 1.2rem';
        const link = document.createElement('a');
        // Solo .caption-with-letter-spacing, come «Vedi sull'Atlante» sulla
        // targhetta: la classe .link di Dawn imporrebbe 1,4rem e vincerebbe
        // (viene dopo in base.css), facendolo grande come un bottone.
        link.className = 'caption-with-letter-spacing';
        link.style.color = 'rgb(var(--color-foreground))';
        link.style.textDecoration = 'none';
        link.href = returnUrl;
        link.textContent = '← ' + this.backLabel(brand);
        link.addEventListener('click', this.onBackToProduct);
        back.appendChild(link);
        header.appendChild(back);
      }

      const title = document.createElement('h3');
      title.className = 'manifatture-panel__title';
      title.id = `manifatture-panel-title-${this.sectionId}`;
      title.textContent = (brand.flag ? `${brand.flag} ` : '') + brand.label;
      header.appendChild(title);

      const place =
        brand.tipo === 'brand_usa'
          ? [brand.city, brand.state].filter(Boolean).join(', ')
          : [brand.city, countryName(brand.country)].filter(Boolean).join(', ');
      const locationParts = [place, brand.founded ? t('since')(brand.founded) : null].filter(
        Boolean
      );
      if (locationParts.length) {
        const location = document.createElement('p');
        location.className = 'manifatture-panel__location';
        location.textContent = locationParts.join(' · ');
        header.appendChild(location);
      }

      const narrativeText = brandNarrative(brand);
      if (narrativeText) {
        const narrative = document.createElement('p');
        narrative.className = 'manifatture-panel__narrative';
        narrative.textContent = narrativeText;
        header.appendChild(narrative);
      }

      this.panelContent.appendChild(header);

      const productsTitle = document.createElement('h4');
      productsTitle.className = 'manifatture-panel__products-title';
      productsTitle.textContent = t('productsTitle');
      this.panelContent.appendChild(productsTitle);

      const productsSection = document.createElement('div');
      productsSection.className = 'manifatture-panel__products';
      productsSection.dataset.manifattureProducts = '';
      this.panelContent.appendChild(productsSection);
    }

    async loadBrandProducts(brand) {
      const grid = this.panelContent && this.panelContent.querySelector('[data-manifatture-products]');
      if (!grid) return;
      grid.innerHTML = `<p class="manifatture-panel__products-status">${t('productsLoading')}</p>`;

      // Ogni apertura di scheda annulla la precedente: aprendo due vendor in
      // rapida successione, la risposta piu' lenta poteva arrivare per ultima
      // e riempire il pannello con i prodotti del vendor sbagliato.
      const requestId = (this.productsRequestId || 0) + 1;
      this.productsRequestId = requestId;

      try {
        const markup = await fetchVendorProductsMarkup(brand.shopify_vendor);
        if (this.productsRequestId !== requestId) return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        // La Section Rendering API restituisce la sezione dentro il suo
        // wrapper #shopify-section-<id>. Se manca, la richiesta non ha
        // reso la sezione ma la pagina di ricerca INTERA (e' quello che
        // succede se il parametro section_id viene ignorato, p.es. dopo
        // aver rinominato il file della sezione): ripiegare sul body
        // significherebbe infilare header, footer e risultati di ricerca
        // dentro il pannello. Meglio trattarlo come un errore.
        const section = parsed.getElementById(`shopify-section-${MANIFATTURE_PRODUCTS_SECTION}`);
        if (!section) throw new Error('sezione manifatture-products non presente nella risposta');

        grid.innerHTML = '';
        while (section.firstChild) grid.appendChild(section.firstChild);
        activateScripts(grid);
        enhanceComingSoonCards(grid);
        initProductFilters(grid);
      } catch (error) {
        console.error('[manifatture] impossibile caricare i prodotti', error);
        if (this.productsRequestId !== requestId) return;
        grid.innerHTML =
          `<p class="manifatture-panel__products-status">${t('productsError')}</p>`;
      }
    }

    // Tiene il bordo superiore del drawer desktop incollato al bordo
    // inferiore VISIBILE dell'header del tema (vedi --manifatture-panel-top
    // in component-manifatture.css). Si misura la posizione reale
    // dell'header invece di usare la sua altezza nominale
    // (--header-height) perche' con sticky_header_type "on-scroll-up" — il
    // default di Dawn — l'header non e' sempre li': sparisce scrollando in
    // giu' e rientra scrollando in su. Con un offset fisso restava un buco
    // in cima al drawer quando l'header non c'era; lasciandogli invece
    // coprire il drawer diventava illeggibile la testata della scheda,
    // dato che l'header e' opaco. getBoundingClientRect().bottom copre da
    // solo tutti i casi (header in flusso a inizio pagina — announcement
    // bar inclusa —, sticky rivelato, nascosto, e la fascia intermedia in
    // cui scorre via naturalmente), senza doverli distinguere.
    //
    // Il polling e' su requestAnimationFrame e non sull'evento scroll
    // perche' il rientro dell'header e' ANIMATO (transition: top 0.15s in
    // base.css) e prosegue dopo che lo scroll si e' gia' fermato: legato
    // allo scroll, il drawer si fermerebbe a meta' strada. Gira solo
    // mentre il pannello e' aperto, e scrive la custom property solo
    // quando il valore cambia davvero, per non invalidare il layout a ogni
    // frame.
    syncPanelTop() {
      const header = document.querySelector('.section-header');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      if (top !== this.lastPanelTop) {
        this.lastPanelTop = top;
        if (this.panel) this.panel.style.setProperty('--manifatture-panel-top', `${top}px`);
      }
      this.panelTopFrame = requestAnimationFrame(this.syncPanelTop);
    }

    startPanelTopSync() {
      if (this.panelTopFrame) return;
      this.syncPanelTop();
    }

    stopPanelTopSync() {
      if (!this.panelTopFrame) return;
      cancelAnimationFrame(this.panelTopFrame);
      this.panelTopFrame = null;
    }

    showPanel() {
      if (!this.panel) return;
      // La chiusura nasconde il pannello con 250ms di ritardo, il tempo
      // dell'animazione di uscita. Riaprendo dentro quella finestra — cosa
      // ormai a portata di mano, visto che il dot e' un interruttore e due
      // tocchi rapidi chiudono e riaprono — il timer arrivava DOPO e
      // nascondeva la scheda appena riaperta.
      window.clearTimeout(this.closeTimeout);
      this.panel.hidden = false;
      this.panel.setAttribute('aria-hidden', 'false');

      // Si rimisura a ogni apertura, non una volta sola al caricamento: i
      // widget delle app compaiono spesso dopo il primo render (o solo
      // dopo un po' di scroll, come il badge recensioni sul footer).
      const sopra = highestFloatingZIndex(this);
      this.panelZIndex = Math.min(
        Math.max(PANEL_Z_BASE, sopra + PANEL_Z_MARGIN),
        PANEL_Z_MAX
      );
      this.panel.style.zIndex = String(this.panelZIndex);
      // Prima del frame in cui il drawer entra: cosi' parte gia' alla
      // quota giusta invece di scivolare in posizione da un top sbagliato.
      this.startPanelTopSync();
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
      if (this.panelCloseBtn) this.panelCloseBtn.focus();
      if (this.syncCartNotificationElevation) this.syncCartNotificationElevation();
    }

    closePanel() {
      if (!this.panel) return;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      // Un eventuale trascinamento in corso muore qui (il foglio se ne sta
      // andando: non ha piu' senso seguirlo col dito).
      this.sheetDrag = null;
      if (this.panelSheet) this.panelSheet.classList.remove('is-dragging');
      this.openBrandId = null;
      this.openVendor = null;
      this.syncUrl(null);
      this.closeTimeout = window.setTimeout(() => {
        this.panel.hidden = true;
        // La misura del foglio si azzera solo a uscita finita: farlo subito
        // significherebbe vederlo cambiare altezza mentre scivola via.
        this.clearSheetDragHeight();
        this.setSheetExpanded(false);
        if (this.syncCartNotificationElevation) this.syncCartNotificationElevation();
      }, 250);
      this.stopPanelTopSync();
    }
  }

  if (!customElements.get('manifatture-section')) {
    customElements.define('manifatture-section', ManifattureSection);
  }
})();